import { z } from 'zod';
import { BillingCycleSchema, SkuSchema } from './schemas';
import { ALL_PRICING_SOURCES } from './enums';
import {
  AsaasWalletIdSchema,
  CommissionWindowSchema,
  COMMISSION_WINDOW_MONTHS_DEFAULT,
  SplitTransferModeSchema,
} from './salesSplit';
import { SettlementSplitStatusSchema } from './paymentSettlement';
import { CrmAsaasSubaccountSchema } from './asaasSubaccount';
import { TenantRegionSchema } from './tenantRegion';

// ============================================================================
// Contrato compartilhado do CRM (crm.etiquetachef.com) — single source of truth
// entre apps/crm (web) e apps/crm-functions. App novo e independente do
// EtiquetaChef: todas as coleções Firestore usam prefixo `crm_`; nomes de Cloud
// Functions usam camelCase `crmXxx` (Gen2/Cloud Run rejeita underscore). Ver
// issue épica #109.
//
// Branded types para IDs (padrão CLAUDE.md — sem custo em runtime).
// ============================================================================

// E-mail do primeiro ADMIN do CRM — usado pelo bootstrap one-time
// (crmBootstrapAdmin): sem cadastro público e com crm_users vazio, ninguém
// entraria. Este e-mail (o super-admin do EtiquetaChef) se auto-provisiona
// como ADMIN ATIVO ao logar. Cliente e servidor compartilham a constante.
export const CRM_BOOTSTRAP_ADMIN_EMAIL = 'admin@etiquetachef.com';

type Brand<K, T> = K & { readonly __brand: T };

export type CrmUserId = Brand<string, 'CrmUserId'>;
export const CrmUserId = (s: string): CrmUserId => s as CrmUserId;
export type CrmBoardId = Brand<string, 'CrmBoardId'>;
export const CrmBoardId = (s: string): CrmBoardId => s as CrmBoardId;
export type CrmColumnId = Brand<string, 'CrmColumnId'>;
export const CrmColumnId = (s: string): CrmColumnId => s as CrmColumnId;
export type CrmLeadId = Brand<string, 'CrmLeadId'>;
export const CrmLeadId = (s: string): CrmLeadId => s as CrmLeadId;
export type CrmInviteId = Brand<string, 'CrmInviteId'>;
export const CrmInviteId = (s: string): CrmInviteId => s as CrmInviteId;

// ---------------------------------------------------------------------------
// Enums / unions discriminadas
// ---------------------------------------------------------------------------

// #761 — o papel mora em `crmRole.ts` (ver o cabeçalho de lá: ciclo com `salesSplit.ts`).
import { CrmRoleSchema, CrmRole } from './crmRole';
import { CrmPartnerStatusSchema } from './crmPartner';

export { CrmRoleSchema, CrmRole };

/** Papel do usuário DENTRO de um quadro. */
export const CrmBoardRoleSchema = z.enum(['MANAGER', 'SELLER']);
export type CrmBoardRole = z.infer<typeof CrmBoardRoleSchema>;

/**
 * Papel no quadro DERIVADO do papel global (issue #173, B2). Não é escolha de
 * quem adiciona: gerente administra o funil; todo o resto — inclusive ADMIN e
 * AUDITOR — entra como vendedor. A UI (`MembersPage`), o aceite de convite
 * (`crmAcceptInvite`) e o `crmAddBoardMember` usam ESTA função; antes eram três
 * cópias da mesma regra, e o callable ainda aceitava o papel cru do input —
 * um MANAGER do quadro podia promover terceiro a MANAGER dali.
 */
// PARTNER entra como SELLER no quadro pessoal dele (OWN_ONLY); o MANAGER do
// quadro é o gerente interno do parceiro (owner, 2026-09-25).
export function crmBoardRoleForGlobalRole(globalRole: CrmRole): CrmBoardRole {
  return globalRole === 'MANAGER' ? 'MANAGER' : 'SELLER';
}

/**
 * Estado do colaborador no CRM.
 *
 * `REMOVED` é o soft-delete de `crmRemoveCollaborator`: a pessoa saiu da
 * empresa, some da lista `/admin/collaborators` (salvo o toggle "Mostrar
 * removidos"), perde o acesso e sai de todos os quadros — mas o doc
 * `crm_users/{uid}` e a conta do Firebase Auth CONTINUAM existindo, porque
 * `crm_sales`/`crm_commissions` apontam para ele e
 * dinheiro apurado não se reescreve. Só `crmRestoreCollaborator` sai daqui, e
 * volta para `DISABLED` (nunca direto para `ACTIVE`).
 *
 * Adicionar valor a este enum é aditivo — todo consumidor compara com
 * `'ACTIVE'`, então `REMOVED` já cai no lado negado sem nenhuma migração.
 */
export const CrmUserStatusSchema = z.enum(['ACTIVE', 'INVITED', 'DISABLED', 'REMOVED']);
export type CrmUserStatus = z.infer<typeof CrmUserStatusSchema>;

export const CrmInviteStatusSchema = z.enum(['PENDING', 'ACCEPTED', 'EXPIRED', 'CANCELLED']);
export type CrmInviteStatus = z.infer<typeof CrmInviteStatusSchema>;

export const CrmBoardStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
export type CrmBoardStatus = z.infer<typeof CrmBoardStatusSchema>;

export const CrmBoardVisibilitySchema = z.enum(['PRIVATE', 'SHARED']);
export type CrmBoardVisibility = z.infer<typeof CrmBoardVisibilitySchema>;

export const CrmSellerLeadVisibilitySchema = z.enum(['OWN_ONLY', 'BOARD_SHARED']);
export type CrmSellerLeadVisibility = z.infer<typeof CrmSellerLeadVisibilitySchema>;

/**
 * Normalização do documento fiscal para uso como IDENTIDADE (issue #202):
 * remove tudo que não é alfanumérico e sobe a caixa. É a MESMA normalização do
 * `completeOnboarding` (app principal) e do `normalizeTaxId` do
 * `crm-functions` — mora aqui porque agora os DOIS lados precisam dela: o
 * cliente monta a chave da reserva no `writeBatch` de criação de lead, e o
 * servidor a monta na transaction da importação. Duas cópias divergindo fariam
 * a unicidade valer só para um dos caminhos.
 */
export function crmNormalizeTaxId(taxId: string): string {
  return taxId.replace(/[^A-Z0-9]/gi, '').toUpperCase();
}

/**
 * Chave da reserva `crm_lead_taxids/{key}` — "1 CNPJ = no máximo 1 lead em
 * negociação" (ordem do owner): `${PAÍS}-${documento normalizado}`. Até a #1005
 * (2026-10-19) era também o id do tenant; hoje o tenant nasce `{CC}-{autoId}`,
 * um CNPJ pode ter vários tenants, e esta chave é SÓ da reserva (que guarda o
 * tenant provisionado em `provisionedTenantId`) e do marcador de cliente
 * `crm_client_taxids/{key}` — a reserva é solta quando o lead vira cliente,
 * para a 2ª venda do mesmo CNPJ (#1005, 2026-10-19 17:26 +0200).
 *
 * Devolve `null` quando não há país OU documento: lead de prospecção pode nascer
 * sem CNPJ (é campo opcional), e nesse caso não há o que reservar — a unicidade
 * só existe para quem tem documento.
 */
export function crmLeadTaxKey(
  countryCode: string | null | undefined,
  taxId: string | null | undefined,
): string | null {
  const country = (countryCode ?? '').trim().toUpperCase();
  const normalized = crmNormalizeTaxId(taxId ?? '');
  if (country === '' || normalized === '') return null;
  return `${country}-${normalized}`;
}

/**
 * `OWN_ONLY` é fronteira de confidencialidade, não filtro de tela (decisão do
 * owner, issue #173/A4): num quadro OWN_ONLY o vendedor não enxerga — nem age
 * — nos leads dos colegas, em NENHUM caminho.
 *
 * Antes a mesma pergunta tinha três respostas diferentes: o callable de mutação
 * respeitava o campo, o de leitura filtrava sempre (ignorando o campo) e o
 * Kanban (leitura direta, gateada só por membership) mostrava tudo — o vendedor
 * via nome, telefone, e-mail, taxId e endereço dos leads do colega numa tela e
 * não via os mesmos leads na outra. Esta função é a resposta única: as
 * `firestore.rules` a espelham (fronteira de verdade — cliente não é fonte da
 * verdade), o Kanban a usa para FILTRAR a query (sem o filtro, a rule nega a
 * query inteira) e o servidor a usa no guard de mutação.
 *
 * ADMIN e AUDITOR (read-only global) enxergam tudo; o MANAGER do quadro também.
 */
export function crmSellerSeesOwnLeadsOnly(args: {
  /** Papel global no CRM. */
  role: CrmRole;
  /** Papel no quadro; `null` quando não é membro. */
  roleInBoard: CrmBoardRole | null;
  /** `crm_boards/{id}.sellerLeadVisibility` (default do schema: OWN_ONLY). */
  sellerLeadVisibility: CrmSellerLeadVisibility;
}): boolean {
  if (args.role === 'ADMIN' || args.role === 'AUDITOR') return false;
  return args.roleInBoard === 'SELLER' && args.sellerLeadVisibility === 'OWN_ONLY';
}

/**
 * Lê `sellerLeadVisibility` do doc do quadro tolerando legado/lixo: o default do
 * schema é OWN_ONLY, e o fallback tem que ser o lado FECHADO — um valor
 * inesperado nunca pode abrir o funil do colega.
 *
 * Mudou de `crm-functions/lib/permissions.ts` para cá na F1 da #679: o codebase
 * principal passou a autorizar o colaborador que gera a venda
 * (`requireCrmCollaborator`), e a regra do OWN_ONLY não pode ter duas cópias.
 */
export function crmSellerLeadVisibilityOf(data: unknown): CrmSellerLeadVisibility {
  const raw = (data as { sellerLeadVisibility?: unknown } | undefined)?.sellerLeadVisibility;
  return raw === 'BOARD_SHARED' ? 'BOARD_SHARED' : 'OWN_ONLY';
}

export const CrmColumnTypeSchema = z.enum(['OPEN', 'WON', 'LOST', 'CUSTOM']);
export type CrmColumnType = z.infer<typeof CrmColumnTypeSchema>;

/**
 * Etapas canônicas do funil "Vendas" (§4), todas OPEN. Ganho/perdido não são
 * colunas — viram status via ações. Fonte única para o seed de board novo e
 * para a migração dos boards existentes (crmMigrateBoardColumns).
 */
export const CRM_DEFAULT_SALES_STAGES = [
  'Novo lead',
  'Primeiro contato',
  'Qualificado',
  'Dor identificada',
  'Demonstração agendada',
  'Demonstração realizada',
  'Proposta enviada',
  'Follow-up',
  'Negociação',
  'Fechamento',
] as const;

/**
 * Nomes canônicos de colunas usados como gatilho semântico (notificações,
 * `stageReachedAt`, datas da barra de funil). Como o usuário pode renomear
 * colunas, o match é heurístico por nome (case-insensitive) — dívida
 * registrada: alternativa robusta seria um `stageKind` semântico na coluna.
 * Fonte única para servidor (crm-functions) e cliente (apps/crm).
 */
export const CRM_DEMO_SCHEDULED_STAGE = 'Demonstração agendada';
export const CRM_PROPOSAL_SENT_STAGE = 'Proposta enviada';
export const CRM_DEMO_DONE_STAGE = 'Demonstração realizada';
export const CRM_NEGOTIATION_STAGE = 'Negociação';

/**
 * Janela de "lead fresco" (issue #202): um lead cujo `updatedAt` está dentro
 * destes dias pertence a quem está trabalhando nele — outro vendedor que tente
 * cadastrar o MESMO documento fiscal é bloqueado, com o nome do dono e a idade
 * do lead. Passada a janela (e abaixo de "Demonstração agendada"), o lead vira
 * reivindicável. Fonte única: o servidor DECIDE com esta constante; o cliente
 * só a usa para interpolar o texto — nunca para liberar/bloquear.
 */
export const CRM_LEAD_CLAIM_FRESH_DAYS = 30;

/** Compara o nome de uma coluna com um estágio canônico (case-insensitive). */
export function crmStageMatches(name: string | null | undefined, stage: string): boolean {
  return (name ?? '').trim().toLowerCase() === stage.toLowerCase();
}

/** Marco de estágio (`stageReachedAt`) que um NOME de coluna representa (ou null). */
export function crmStageMarkOf(
  columnName: string | null | undefined,
): 'proposal' | 'demoDone' | 'negotiation' | null {
  if (crmStageMatches(columnName, CRM_PROPOSAL_SENT_STAGE)) return 'proposal';
  if (crmStageMatches(columnName, CRM_DEMO_DONE_STAGE)) return 'demoDone';
  if (crmStageMatches(columnName, CRM_NEGOTIATION_STAGE)) return 'negotiation';
  return null;
}

/**
 * Nome do quadro pessoal criado automaticamente quando um usuário nasce no CRM
 * (aceite de convite / bootstrap do primeiro ADMIN). O nome é gravado no doc do
 * board pelo servidor no idioma do dono — não é string de UI (não passa por
 * i18next). Fonte única para todos os caminhos de nascimento.
 */
export function crmPersonalBoardName(language: CrmLanguage, ownerName: string): string {
  const name = ownerName.trim() || (language === 'pt' ? 'vendas' : 'sales');
  return language === 'pt' ? `Funil de ${name}` : `${name}'s pipeline`;
}

/**
 * Nota que o servidor grava em cada lead herdado na remoção de um colaborador.
 * Mesma natureza de `crmPersonalBoardName`: texto de dado, no idioma do quadro
 * que recebe o lead, não string de UI.
 */
export function crmCollaboratorTransferNote(language: CrmLanguage, removedName: string): string {
  const name = removedName.trim() || (language === 'pt' ? 'removido' : 'removed');
  return language === 'pt'
    ? `Lead movido do colaborador ${name}, removido do CRM.`
    : `Lead moved from collaborator ${name}, who was removed from the CRM.`;
}

export const CrmLeadTemperatureSchema = z.enum(['COLD', 'WARM', 'HOT']);
export type CrmLeadTemperature = z.infer<typeof CrmLeadTemperatureSchema>;

export const CrmLeadStatusSchema = z.enum(['OPEN', 'WON', 'LOST', 'ARCHIVED']);
export type CrmLeadStatus = z.infer<typeof CrmLeadStatusSchema>;
/** Constantes do status do lead (#768, auditoria 2026-09-28): compare por elas, nunca pelo texto. */
export const CrmLeadStatus = CrmLeadStatusSchema.enum;

export const CrmLanguageSchema = z.enum(['pt', 'en']);
export type CrmLanguage = z.infer<typeof CrmLanguageSchema>;

/** País do usuário: ISO 3166-1 alpha-2, em maiúsculas. */
export const CrmCountryCodeSchema = z.string().regex(/^[A-Z]{2}$/);

/**
 * Estado civil do usuário. Enum fechado (não texto livre) — o rótulo humano,
 * localizado no perfil, deriva do código.
 */
export const CrmMaritalStatusSchema = z.enum([
  'SINGLE',
  'MARRIED',
  'DIVORCED',
  'WIDOWED',
  'STABLE_UNION',
]);
export type CrmMaritalStatus = z.infer<typeof CrmMaritalStatusSchema>;

/** Tipo de material de treinamento. */
export const CrmTrainingTypeSchema = z.enum(['PDF', 'IMAGE', 'LINK', 'MARKDOWN', 'AUDIO', 'VIDEO', 'OTHER']);
export type CrmTrainingType = z.infer<typeof CrmTrainingTypeSchema>;

/**
 * Alcance do arquivo. `ALL` = todo usuário CRM ativo (subido por ADMIN);
 * `TEAM` = só o time do MANAGER que subiu, ou seja, os membros dos quadros
 * que ele gerencia. O servidor deriva do papel de quem sobe — não é escolha
 * do cliente. Material antigo sem o campo é lido como `ALL`.
 */
export const CrmTrainingVisibilitySchema = z.enum(['ALL', 'TEAM']);
export type CrmTrainingVisibility = z.infer<typeof CrmTrainingVisibilitySchema>;

/** `crm_training_materials/{id}` — arquivo de apoio ("Arquivos" na UI). */
export const CrmTrainingMaterialSchema = z.object({
  title: z.string(),
  type: CrmTrainingTypeSchema,
  description: z.string().nullable().optional(),
  url: z.string().nullable().optional(), // link externo (type LINK)
  storagePath: z.string().nullable().optional(), // arquivo no Storage
  visibility: CrmTrainingVisibilitySchema.default('ALL'),
  // Quadros do MANAGER no momento do upload (base para recalcular a audiência
  // quando a membership muda). Vazio quando visibility = ALL.
  boardIds: z.array(z.string()).nullable().optional(),
  // Uids que enxergam um material TEAM (membros ativos de `boardIds`). É o que
  // as rules checam — mantido pelo trigger crmOnBoardMemberWritten.
  audienceUids: z.array(z.string()).nullable().optional(),
  createdBy: z.string().nullable().optional(),
  // Nome de quem subiu, desnormalizado: as rules só deixam o próprio usuário
  // (e o ADMIN) ler `crm_users`, então o cliente não conseguiria resolvê-lo.
  createdByName: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmTrainingMaterial = z.infer<typeof CrmTrainingMaterialSchema>;

// NOTA: o CRM NÃO registra histórico de movimentação (issue #109 / regra do
// owner). O card guarda só o estado atual — não existe subcoleção de
// atividades/timeline. Enum de atividade removido de propósito; não readicionar.

/**
 * Plano de interesse / plano vendido. `QUARTERLY` e `SEMIANNUAL` (#679 D13/D14)
 * existem só na venda assistida — a assinatura trimestral ou semestral que o
 * colaborador negocia na Contratação.
 */
export const CrmPlanInterestSchema = z.enum(['MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL']);
export type CrmPlanInterest = z.infer<typeof CrmPlanInterestSchema>;

/**
 * #679 D14 — teto de parcelas do ciclo VENDIDO, gravado em
 * `CrmSale.installmentsCap` no registro da venda.
 *
 * Desde a #780 (owner, 2026-09-29 12:48 +0200) o teto NÃO limita mais nada: o
 * limite é o prazo de cada beneficiário (`commissionWindowMonths` do cadastro),
 * e toda cobrança dentro dele comissiona. A função fica só para preencher o
 * campo do schema da venda, que as vendas antigas também trazem.
 * `switch` exaustivo: um plano novo reprova o typecheck aqui.
 */
export function crmInstallmentsCapFor(plan: CrmPlanInterest): 1 | 4 | 12 {
  switch (plan) {
    case 'MONTHLY':
      return 12;
    case 'QUARTERLY':
      return 4;
    case 'SEMIANNUAL':
      return 1;
    case 'ANNUAL':
      return 1;
  }
}

// `CRM_COMMISSION_WINDOW_MONTHS` (12 fixos) e `crmIsOneShotCycle` saíram na
// #780 (2026-09-29 12:48 +0200): o prazo é do cadastro de cada beneficiário
// (`COMMISSION_WINDOW_MONTHS_DEFAULT` quando ausente), e semestral/anual
// comissionam por cobrança dentro do prazo, como os demais ciclos.

/** Meses cobertos por uma cobrança do ciclo. */
export function crmCycleMonths(plan: CrmPlanInterest): 1 | 3 | 6 | 12 {
  switch (plan) {
    case 'MONTHLY':
      return 1;
    case 'QUARTERLY':
      return 3;
    case 'SEMIANNUAL':
      return 6;
    case 'ANNUAL':
      return 12;
  }
}

/**
 * O plano vendido a partir do ciclo da assinatura (`billing_cycle` do app
 * principal) — o que o auto-WIN do pagamento (F2) grava no lead.
 */
export function crmPlanFromBillingCycle(
  cycle: 'monthly' | 'quarterly' | 'semiannual' | 'yearly',
): CrmPlanInterest {
  switch (cycle) {
    case 'monthly':
      return 'MONTHLY';
    case 'quarterly':
      return 'QUARTERLY';
    case 'semiannual':
      return 'SEMIANNUAL';
    case 'yearly':
      return 'ANNUAL';
  }
}

/** Motivo estruturado de perda (ação "Perdido"). */
export const CrmLostReasonSchema = z.enum([
  'TOO_EXPENSIVE', // Achou caro
  'USES_COMPETITOR', // Já usa concorrente
  'NO_BUDGET', // Sem verba agora
  'NO_RESPONSE', // Não respondeu
  'NO_INTEREST', // Não tem interesse
  'NOT_A_FIT', // Não é perfil
  'NEEDS_APPROVAL', // Precisa falar com sócio/dono
  'CHOSE_OTHER_SYSTEM', // Fechou com outro sistema
  'DIDNT_SEE_VALUE', // Não entendeu valor
  'HARDWARE_ISSUE', // Problema com impressora/equipamento
  'OTHER', // Outro motivo
]);
export type CrmLostReason = z.infer<typeof CrmLostReasonSchema>;

/** Motivo de "Não qualificado". */
export const CrmNotQualifiedReasonSchema = z.enum([
  'NOT_FOOD_BUSINESS', // Não é negócio de alimentação
  'NO_PERISHABLES', // Não trabalha com perecíveis
  'TOO_SMALL', // Muito pequeno no momento
  'UNSTRUCTURED_OPS', // Sem operação estruturada
  'WRONG_LEAD', // Lead errado
  'REGION_NOT_SERVED', // Região não atendida
  'OTHER', // Outro motivo
]);
export type CrmNotQualifiedReason = z.infer<typeof CrmNotQualifiedReasonSchema>;

/** Canal de contato (ação "Sem resposta"). */
export const CrmContactChannelSchema = z.enum([
  'WHATSAPP',
  'CALL',
  'EMAIL',
  'INSTAGRAM',
  'REFERRAL',
  'VISIT',
]);
export type CrmContactChannel = z.infer<typeof CrmContactChannelSchema>;

/** Tipo de ação (ação "Reagendar"). */
export const CrmActionTypeSchema = z.enum([
  'CALL',
  'WHATSAPP',
  'MEETING',
  'DEMO',
  'VISIT',
  'FOLLOW_UP',
]);
export type CrmActionType = z.infer<typeof CrmActionTypeSchema>;

/** Status de onboarding do cliente ganho (separado do status comercial). */
export const CrmOnboardingStatusSchema = z.enum([
  'AWAITING_PAYMENT', // Aguardando pagamento
  'AWAITING_SETUP', // Aguardando configuração
  'IN_PROGRESS', // Em implantação
  'PENDING_CLIENT', // Pendente do cliente
  'PENDING_SUPPORT', // Pendente de suporte
  'TRAINING_SCHEDULED', // Treinamento agendado
  'IN_VALIDATION', // Em validação
  'DONE', // Implementação concluída
]);
export type CrmOnboardingStatus = z.infer<typeof CrmOnboardingStatusSchema>;

/**
 * Itens do checklist de onboarding (Client Success), em ordem de exibição.
 * Chaves estáveis — o `onboardingChecklist` do lead é um mapa `{ [item]: millis }`
 * (marcado = data em que foi marcado; desmarcado = sem chave). Todos os itens são
 * obrigatórios: o status DONE só é permitido quando TODOS estão marcados (gate §9).
 *
 * DEFAULT/SEED/FALLBACK: o template configurável vive em
 * `crm_appConfig/onboardingChecklist` (`CrmOnboardingTemplateSchema`). Estas
 * constantes semeiam o doc no primeiro save do admin e são o fallback quando o
 * doc não existe/é inválido — cliente e servidor caem aqui (zero downtime).
 */
export const CRM_ONBOARDING_ITEMS = [
  'paymentConfirmed',
  'companyDataConfirmed',
  'taxIdConfirmed',
  'primaryContactConfirmed',
  'contactChannelsConfirmed',
  'establishmentConfigured',
  'localeConfigured',
  'logoAdded',
  'productsRegistered',
  'validityRulesRegistered',
  'labelTemplateConfigured',
  'printerConfigured',
  'printTestDone',
  'firstLabelPrinted',
  'trainedCreateLabel',
  'trainedQrScan',
  'trainedPinLookup',
  'trainedActiveLabels',
  'trainedHistory',
  'expiryAlertsExplained',
  'teamTrained',
  'usingInProduction',
] as const;
export type CrmOnboardingItem = (typeof CRM_ONBOARDING_ITEMS)[number];

// --- Itens verificados no tenant (crmSyncTenantDeployment) ------------------

/**
 * Quantos produtos ATIVOS o tenant precisa ter para `productsRegistered`, e
 * quantos deles precisam ter ao menos uma regra de validade para
 * `validityRulesRegistered`. Limites de negócio ("implantação de verdade"),
 * fixos no código por decisão do owner — o template do checklist configura
 * QUAIS itens existem, não o rigor de cada um.
 */
export const CRM_DEPLOYMENT_MIN_PRODUCTS = 5;
export const CRM_DEPLOYMENT_MIN_PRODUCTS_WITH_RULE = 5;

/**
 * Itens do checklist derivados do estado REAL do tenant no app principal,
 * carimbados por `crmSyncTenantDeployment` (botão "Sincronizar Aplicativo").
 * Como o `paymentConfirmed`, NÃO são marcáveis à mão: a fonte da verdade é o
 * app, e `crmUpdateOnboarding` rejeita divergência com `AUTO_ITEM_MANUAL`.
 *
 * Os demais itens seguem manuais por não terem sinal no tenant (treinamentos,
 * confirmações de contato feitas por telefone, etc.).
 */
export const CRM_AUTO_ONBOARDING_ITEMS = [
  // Único carimbado por outro callable (`crmVerifyPayment`); o sync não o toca.
  'paymentConfirmed',
  'companyDataConfirmed',
  'primaryContactConfirmed',
  'establishmentConfigured',
  'logoAdded',
  'productsRegistered',
  'validityRulesRegistered',
  'labelTemplateConfigured',
  'printerConfigured',
  'printTestDone',
  'firstLabelPrinted',
  'usingInProduction',
] as const;
export type CrmAutoOnboardingItem = (typeof CRM_AUTO_ONBOARDING_ITEMS)[number];

const CRM_AUTO_ONBOARDING_ITEM_SET: ReadonlySet<string> = new Set(CRM_AUTO_ONBOARDING_ITEMS);

/** True se o item é verificado no tenant (read-only na UI, guard no servidor). */
export function crmIsAutoOnboardingItem(key: string): boolean {
  return CRM_AUTO_ONBOARDING_ITEM_SET.has(key);
}

/**
 * Foto do tenant no último "Sincronizar Aplicativo" — alimenta os KPIs da aba
 * de onboarding sem re-chamar o callable a cada abertura do lead. É um
 * SNAPSHOT (não tempo real): a UI mostra `syncedAt` junto dos números.
 *
 * Campo do lead gravado só pelo servidor (as rules negam pelo cliente). Fica
 * aqui, e não junto do `crmSyncTenantDeployment` lá embaixo, porque o
 * `CrmLeadSchema` o referencia.
 */
export const CrmTenantSnapshotSchema = z.object({
  syncedAt: z.number().int().nonnegative(),
  /** Etiquetas vigentes = status DRAFT ou PRINTED (nem histórico, nem DELETED). */
  activeLabels: z.number().int().nonnegative(),
  /** Impressões acumuladas — soma de `daily_ops.totals.printed`. */
  printed: z.number().int().nonnegative(),
  /** Produtos ativos (arquivados não contam). */
  products: z.number().int().nonnegative(),
  /** Responsáveis ativos. */
  responsibles: z.number().int().nonnegative(),
  printers: z.number().int().nonnegative(),
  /** Membros OWNER + MANAGER (quem acessa o app web). */
  users: z.number().int().nonnegative(),
  /** Membros STAFF ("Colaboradores" na terminologia do app). */
  staff: z.number().int().nonnegative(),
});
export type CrmTenantSnapshot = z.infer<typeof CrmTenantSnapshotSchema>;

/**
 * Valor de uma célula do checklist: `number` (millis da marcação), `true` (legado,
 * marcado sem data conhecida) ou ausente (desmarcado).
 */
export type CrmChecklistValue = number | boolean;

/** True se o item está marcado (número > 0, ou `true` legado). */
export function crmChecklistIsChecked(value: CrmChecklistValue | undefined | null): boolean {
  return value === true || (typeof value === 'number' && value > 0);
}

/** Data (millis) em que o item foi marcado; `null` para `true` legado ou desmarcado. */
export function crmChecklistDate(value: CrmChecklistValue | undefined | null): number | null {
  return typeof value === 'number' && value > 0 ? value : null;
}

/**
 * Itens do checklist ainda não marcados, dentre `keys` (para o gate §9 e a UI).
 * `keys` vem do template configurável (`crmTemplateKeys`); o default é o fallback
 * hardcoded quando não há template publicado.
 */
export function crmMissingChecklistItems(
  checklist: Readonly<Record<string, CrmChecklistValue>> | null | undefined,
  keys: readonly string[] = CRM_ONBOARDING_ITEMS,
): string[] {
  const cl = checklist ?? {};
  return keys.filter((item) => !crmChecklistIsChecked(cl[item]));
}

/** True quando TODOS os itens de `keys` estão marcados (permite o status DONE). */
export function crmChecklistComplete(
  checklist: Readonly<Record<string, CrmChecklistValue>> | null | undefined,
  keys: readonly string[] = CRM_ONBOARDING_ITEMS,
): boolean {
  return crmMissingChecklistItems(checklist, keys).length === 0;
}

// ---------------------------------------------------------------------------
// Template configurável do checklist de onboarding (`crm_appConfig/onboardingChecklist`)
// ---------------------------------------------------------------------------

/** Doc ID do template dentro de `crm_appConfig`. */
export const CRM_ONBOARDING_TEMPLATE_DOC_ID = 'onboardingChecklist';

/**
 * Item do template do checklist. A `key` casa com as chaves do mapa
 * `onboardingChecklist` do lead (os itens legados viram seed com as MESMAS
 * strings — migração zero). Rótulos vivem no doc: `labelPt` obrigatório,
 * `labelEn` opcional (fallback pt). Todos os itens são obrigatórios (sem conceito
 * de "essencial"); admin adiciona/remove/reordena livremente.
 */
export const CrmOnboardingTemplateItemSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
  labelPt: z.string().trim().min(1).max(160),
  labelEn: z.string().trim().min(1).max(160).nullable().optional(),
  // Mantido por tolerância de leitura de docs legados; a UI não expõe toggle
  // (remoção é hard delete). Itens inativos saem do gate/progresso.
  active: z.boolean().default(true),
});
export type CrmOnboardingTemplateItem = z.infer<typeof CrmOnboardingTemplateItemSchema>;

/** `crm_appConfig/onboardingChecklist` — leitura tolerante (safeParse + fallback). */
export const CrmOnboardingTemplateSchema = z.object({
  items: z.array(CrmOnboardingTemplateItemSchema).min(1).max(100),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
});
export type CrmOnboardingTemplate = z.infer<typeof CrmOnboardingTemplateSchema>;

/**
 * Item avulso do checklist de um lead específico (§8): rótulo livre (não i18n),
 * chave própria `extra_*`. Nunca entra no gate §9. O estado (data/desmarcado)
 * vive no mesmo mapa `onboardingChecklist` do lead.
 */
export const CrmOnboardingExtraItemSchema = z.object({
  key: z.string().regex(/^extra_[a-z0-9]{8,24}$/),
  label: z.string().trim().min(1).max(160),
});
export type CrmOnboardingExtraItem = z.infer<typeof CrmOnboardingExtraItemSchema>;

/** Chaves dos itens ATIVOS do template (ordem do doc); null → default hardcoded. */
export function crmTemplateKeys(
  template: CrmOnboardingTemplate | null | undefined,
): string[] {
  if (!template) return [...CRM_ONBOARDING_ITEMS];
  return template.items.filter((i) => i.active).map((i) => i.key);
}

// ---------------------------------------------------------------------------
// Playbooks comerciais (`crm_playbooks/{slug}`) — base de conhecimento da IA
// ---------------------------------------------------------------------------

/**
 * Os 10 playbooks comerciais. O `slug` é o ID do doc em `crm_playbooks` e a
 * chave estável em todo o código (import, seleção pela IA, export). A ORDEM
 * (01..10) é o número do arquivo Word de origem; o admin sobe o `.docx` e o
 * conteúdo (seções) passa a viver no Firestore.
 */
export const CRM_PLAYBOOK_SLUGS = [
  'sdr',
  'demonstracao',
  'closer',
  'objecoes',
  'follow-up',
  'client-success',
  'manual-produto',
  'manual-crm',
  'academia-vendedor',
  'manual-gestor',
] as const;
export const CrmPlaybookSlugSchema = z.enum(CRM_PLAYBOOK_SLUGS);
export type CrmPlaybookSlug = z.infer<typeof CrmPlaybookSlugSchema>;

/** Metadados default de um playbook (ordem 01..10 + mapeamento inicial). */
export type CrmPlaybookDefault = {
  order: number;
  titlePt: string;
  titleEn: string;
  /** Papéis-alvo do conteúdo (referência da UI; não gate de leitura). */
  targetRoles: CrmRole[];
  /** Fases do funil que este playbook cobre (nomes de CRM_DEFAULT_SALES_STAGES).
   *  `[]` = transversal (entra como referência em qualquer fase). */
  stageNames: string[];
  /** Aplica-se a leads GANHOS (Client Success / onboarding). */
  appliesToWon: boolean;
};

/**
 * Catálogo fixo dos 10 playbooks: ordem, título default e o mapeamento
 * fase→papel inicial. É a fonte para semear o slot vazio na tela admin (antes
 * do upload do `.docx`) e o fallback do mapeamento usado pela IA quando o admin
 * ainda não personalizou. Os nomes de fase casam com CRM_DEFAULT_SALES_STAGES.
 */
export const CRM_PLAYBOOK_DEFAULTS: Record<CrmPlaybookSlug, CrmPlaybookDefault> = {
  sdr: {
    order: 1,
    titlePt: 'Playbook SDR',
    titleEn: 'SDR Playbook',
    targetRoles: ['SELLER', 'MANAGER'],
    stageNames: ['Novo lead', 'Primeiro contato', 'Qualificado'],
    appliesToWon: false,
  },
  demonstracao: {
    order: 2,
    titlePt: 'Playbook de Demonstração',
    titleEn: 'Demo Playbook',
    targetRoles: ['SELLER', 'MANAGER'],
    stageNames: ['Dor identificada', 'Demonstração agendada', 'Demonstração realizada'],
    appliesToWon: false,
  },
  closer: {
    order: 3,
    titlePt: 'Playbook Closer',
    titleEn: 'Closer Playbook',
    targetRoles: ['SELLER', 'MANAGER'],
    stageNames: ['Proposta enviada', 'Negociação', 'Fechamento'],
    appliesToWon: false,
  },
  objecoes: {
    order: 4,
    titlePt: 'Playbook de Objeções',
    titleEn: 'Objections Playbook',
    targetRoles: ['SELLER', 'MANAGER'],
    stageNames: [], // transversal — objeções aparecem em qualquer fase
    appliesToWon: false,
  },
  'follow-up': {
    order: 5,
    titlePt: 'Playbook Follow-up',
    titleEn: 'Follow-up Playbook',
    targetRoles: ['SELLER', 'MANAGER'],
    stageNames: ['Follow-up'],
    appliesToWon: false,
  },
  'client-success': {
    order: 6,
    titlePt: 'Playbook Client Success',
    titleEn: 'Client Success Playbook',
    targetRoles: ['SELLER', 'MANAGER'],
    stageNames: [],
    appliesToWon: true, // pós-venda: leads GANHOS em implantação
  },
  'manual-produto': {
    order: 7,
    titlePt: 'Manual do Produto',
    titleEn: 'Product Manual',
    targetRoles: ['SELLER', 'MANAGER', 'ADMIN'],
    stageNames: [], // referência de produto — transversal
    appliesToWon: false,
  },
  'manual-crm': {
    order: 8,
    titlePt: 'Manual do CRM Comercial',
    titleEn: 'Sales CRM Manual',
    targetRoles: ['SELLER', 'MANAGER', 'ADMIN'],
    stageNames: [],
    appliesToWon: false,
  },
  'academia-vendedor': {
    order: 9,
    titlePt: 'Academia do Vendedor',
    titleEn: 'Seller Academy',
    targetRoles: ['SELLER', 'MANAGER'],
    stageNames: [],
    appliesToWon: false,
  },
  'manual-gestor': {
    order: 10,
    titlePt: 'Manual do Gestor Comercial',
    titleEn: 'Sales Manager Manual',
    targetRoles: ['MANAGER', 'ADMIN'],
    stageNames: [],
    appliesToWon: false,
  },
};

/**
 * Uma seção do playbook: um heading (h1..h4) + o corpo em markdown até o próximo
 * heading. O import (`crmImportPlaybookDocx`) quebra o `.docx` convertido em
 * markdown por headings; o export remonta o `.docx` a partir daqui.
 */
export const CrmPlaybookSectionSchema = z.object({
  heading: z.string().trim().min(1).max(300),
  level: z.number().int().min(1).max(4),
  contentMarkdown: z.string().max(20000),
});
export type CrmPlaybookSection = z.infer<typeof CrmPlaybookSectionSchema>;

/**
 * `crm_playbooks/{slug}` — leitura tolerante (safeParse). Um doc por playbook
 * (10 no total). `sections` é o conteúdo importado do Word; `stageNames`/
 * `targetRoles`/`appliesToWon`/`active` são o mapeamento que a IA usa para
 * escolher o playbook certo por fase — preservados entre reimportações.
 */
export const CrmPlaybookSchema = z.object({
  slug: CrmPlaybookSlugSchema,
  title: z.string().trim().min(1).max(200),
  order: z.number().int().min(1).max(99),
  targetRoles: z.array(CrmRoleSchema).min(1),
  stageNames: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  appliesToWon: z.boolean().default(false),
  sections: z.array(CrmPlaybookSectionSchema).max(300).default([]),
  version: z.number().int().min(1).default(1),
  sourceFileName: z.string().trim().max(300).nullable().optional(),
  active: z.boolean().default(true),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
});
export type CrmPlaybook = z.infer<typeof CrmPlaybookSchema>;

/**
 * Manda o `.docx` já no Storage para importação. O `storagePath` é revalidado no
 * servidor (prefixo `crm_playbooks_imports/`). `dryRun` devolve o preview das
 * seções SEM gravar; o admin confirma e reenvia com `dryRun:false`.
 */
export const CrmImportPlaybookInputSchema = z.object({
  slug: CrmPlaybookSlugSchema,
  storagePath: z.string().trim().min(1).max(500),
  dryRun: z.boolean().default(false),
});
export type CrmImportPlaybookInput = z.infer<typeof CrmImportPlaybookInputSchema>;

/** Gera o `.docx` do playbook a partir do Firestore (download pelo admin). */
export const CrmExportPlaybookInputSchema = z.object({
  slug: CrmPlaybookSlugSchema,
});
export type CrmExportPlaybookInput = z.infer<typeof CrmExportPlaybookInputSchema>;

/** Atualiza só o mapeamento (fase/papel/ativo), sem tocar no conteúdo. */
export const CrmUpdatePlaybookMetaInputSchema = z.object({
  slug: CrmPlaybookSlugSchema,
  stageNames: z.array(z.string().trim().min(1).max(80)).max(20),
  targetRoles: z.array(CrmRoleSchema).min(1),
  active: z.boolean(),
});
export type CrmUpdatePlaybookMetaInput = z.infer<typeof CrmUpdatePlaybookMetaInputSchema>;

/**
 * Resultado do import: sempre devolve o preview (título/seções/avisos). Com
 * `dryRun:true`, `saved=false` e `version=null` (nada gravado); com
 * `dryRun:false`, `saved=true` e `version` da versão persistida.
 */
export const CrmPlaybookImportResultSchema = z.object({
  title: z.string(),
  sections: z.array(CrmPlaybookSectionSchema),
  warnings: z.array(z.string()),
  totalChars: z.number().int().nonnegative(),
  saved: z.boolean(),
  version: z.number().int().nullable(),
});
export type CrmPlaybookImportResult = z.infer<typeof CrmPlaybookImportResultSchema>;

// ---------------------------------------------------------------------------
// Assistente de IA no card do lead (`crmLeadAdvice`)
// ---------------------------------------------------------------------------

/** Pede o conselho da IA para um lead. `refresh` força regenerar (ignora cache). */
export const CrmLeadAdviceInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  refresh: z.boolean().default(false),
});
export type CrmLeadAdviceInput = z.infer<typeof CrmLeadAdviceInputSchema>;

/**
 * Sentinelas textuais que um LLM (ou colagem humana) usa para dizer "sem valor"
 * num campo de texto livre: o valor único é um marcador de ausência, não conteúdo
 * real. Tratamos qualquer um deles como vazio nas bordas (sugestão, extração,
 * upsert e leitura). Sem isso, a string `"null"` devolvida pelo Gemini (o
 * `responseSchema` declara `script` como `type: "string"`) era persistida e
 * exibida literalmente no editor.
 */
const TEXT_SENTINELS = new Set([
  '',
  'null',
  'none',
  'nil',
  'n/a',
  'na',
  'undefined',
  '-',
  '–',
  '—',
  'nenhum',
  'nenhuma',
  'não se aplica',
]);

/** `true` se o valor, sozinho, é um marcador de ausência (ver `TEXT_SENTINELS`). */
export function isTextSentinel(v: string): boolean {
  return TEXT_SENTINELS.has(v.trim().toLowerCase());
}

/** Normaliza sentinela textual → `null` (usado nos transforms de `script`). */
const sentinelToNull = (v: string | null | undefined): string | null =>
  v != null && isTextSentinel(v) ? null : (v ?? null);

/** Uma objeção provável + a resposta sugerida (do playbook de objeções). */
export const CrmLeadObjectionSchema = z.object({
  objecao: z.string().trim().max(400),
  resposta: z.string().trim().max(800),
});
export type CrmLeadObjection = z.infer<typeof CrmLeadObjectionSchema>;

/**
 * Conselho estruturado que o modelo devolve (responseSchema Gemini + Zod na
 * borda), no idioma do usuário. Campos curtos e acionáveis.
 */
export const CrmLeadAdviceSchema = z.object({
  resumo: z.string().trim().max(1000),
  proximosPassos: z.array(z.string().trim().min(1).max(400)).max(5),
  // Sentinela ("null"/"n/a") vira '' — o campo é required, então vazio é o
  // marcador de ausência; a UI esconde a seção quando vazio.
  scriptSugerido: z.string().trim().max(2000).transform((v) => (isTextSentinel(v) ? '' : v)),
  objecoesProvaveis: z.array(CrmLeadObjectionSchema).max(4),
  riscos: z.array(z.string().trim().min(1).max(400)).max(3),
});
export type CrmLeadAdvice = z.infer<typeof CrmLeadAdviceSchema>;

/**
 * Retorno de `crmLeadAdvice`. `signature` identifica a versão do conselho (usada
 * pelo feedback 👍/👎). `playbooksUsed` são os slugs que entraram no contexto.
 */
export const CrmLeadAdviceResultSchema = z.object({
  advice: CrmLeadAdviceSchema,
  playbooksUsed: z.array(z.string()),
  /** IDs dos blocos de conhecimento usados no contexto (#153). Aditivo: leituras
   *  antigas sem o campo caem no default vazio. */
  blocksUsed: z.array(z.string()).default([]),
  stageName: z.string().nullable(),
  cached: z.boolean(),
  signature: z.string(),
});
export type CrmLeadAdviceResult = z.infer<typeof CrmLeadAdviceResultSchema>;

// ---------------------------------------------------------------------------
// Rascunho de mensagem por fase (WhatsApp/e-mail) — extra do assistente
// ---------------------------------------------------------------------------

export const CrmMessageChannelSchema = z.enum(['WHATSAPP', 'EMAIL']);
export type CrmMessageChannel = z.infer<typeof CrmMessageChannelSchema>;

/** Pede à IA um rascunho de mensagem para o lead, no canal escolhido. */
export const CrmDraftMessageInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  channel: CrmMessageChannelSchema,
});
export type CrmDraftMessageInput = z.infer<typeof CrmDraftMessageInputSchema>;

/** Rascunho gerado. `subject` só faz sentido no e-mail (null no WhatsApp). */
export const CrmDraftMessageResultSchema = z.object({
  channel: CrmMessageChannelSchema,
  subject: z.string().nullable(),
  message: z.string(),
});
export type CrmDraftMessageResult = z.infer<typeof CrmDraftMessageResultSchema>;

// ---------------------------------------------------------------------------
// Insights de IA por card (crmLeadInsight) — multi-modo, alimentado por blocos
// ---------------------------------------------------------------------------

/**
 * Modo do insight. Cada um puxa categorias de blocos diferentes e um prompt
 * próprio: sugestão ao mover card (#144), qualificação/risco, roteiros de
 * ligação/demonstração, checklist da etapa, follow-up, responder objeção e
 * coaching das anotações.
 */
export const CrmLeadInsightKindSchema = z.enum([
  'STAGE_CHANGE',
  'QUALIFICATION_RISK',
  'CALL_SCRIPT',
  'DEMO_SCRIPT',
  'STAGE_CHECKLIST',
  'FOLLOW_UP',
  'OBJECTION_HELP',
  'NOTES_COACHING',
]);
export type CrmLeadInsightKind = z.infer<typeof CrmLeadInsightKindSchema>;

/** Pede um insight para um lead. `objectionText` só em OBJECTION_HELP. */
export const CrmLeadInsightInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  kind: CrmLeadInsightKindSchema,
  objectionText: z.string().trim().max(600).nullable().optional(),
  refresh: z.boolean().default(false),
});
export type CrmLeadInsightInput = z.infer<typeof CrmLeadInsightInputSchema>;

/** Nível qualitativo (risco/qualificação). Null nos modos que não pontuam. */
export const CrmInsightLevelSchema = z.enum(['BAIXO', 'MEDIO', 'ALTO']);
export type CrmInsightLevel = z.infer<typeof CrmInsightLevelSchema>;

/**
 * Saída única e flexível do insight (responseSchema Gemini + Zod). `itens` são
 * passos/checklist/fatores/perguntas; `script` é roteiro/mensagem quando cabe;
 * `nivel` é risco/qualificação (null nos demais modos).
 */
export const CrmLeadInsightSchema = z.object({
  titulo: z.string().trim().max(200),
  resumo: z.string().trim().max(1000),
  // Tolerante a excesso: o modelo às vezes devolve mais itens que o teto; em vez
  // de rejeitar a resposta inteira, corta os primeiros 16 (o resto é ruído).
  itens: z
    .array(
      z.object({
        texto: z.string().trim().min(1).max(500),
        detalhe: z.string().trim().max(800).nullable().optional().transform(sentinelToNull),
      }),
    )
    .catch([])
    .transform((arr) => arr.slice(0, 16))
    .default([]),
  script: z.string().trim().max(3000).nullable().catch(null).default(null).transform(sentinelToNull),
  // Tolerante: o modelo pode devolver caixa/sinônimo fora do enum. Normaliza para
  // maiúsculas e, se ainda não casar, vira null — nunca quebra a resposta.
  nivel: z
    .preprocess(
      (v) => (typeof v === "string" && v.trim() !== "" ? v.trim().toUpperCase() : null),
      CrmInsightLevelSchema.nullable().catch(null),
    )
    .default(null),
});
export type CrmLeadInsight = z.infer<typeof CrmLeadInsightSchema>;

/** Retorno de `crmLeadInsight`. `blocksUsed` são os IDs de blocos no contexto. */
export const CrmLeadInsightResultSchema = z.object({
  kind: CrmLeadInsightKindSchema,
  insight: CrmLeadInsightSchema,
  blocksUsed: z.array(z.string()).default([]),
  stageName: z.string().nullable(),
  cached: z.boolean(),
  signature: z.string(),
});
export type CrmLeadInsightResult = z.infer<typeof CrmLeadInsightResultSchema>;

// ---------------------------------------------------------------------------
// Ações sugeridas pela IA a partir das notas (crmLeadSuggestActions)
// ---------------------------------------------------------------------------

/**
 * Tipo de ação que a IA extrai das notas do lead. Diferente dos insights (texto
 * para ler), cada ação é APLICÁVEL: a UI liga cada tipo ao executor que o
 * usuário já usa à mão (crmMoveLead, addLeadTask, crmUpsertAppointment,
 * updateLead, crmDraftLeadMessage), sempre com confirmação antes de gravar.
 */
export const CrmSuggestedActionTypeSchema = z.enum([
  'MOVE_COLUMN',
  'CREATE_TASK',
  'CREATE_APPOINTMENT',
  'QUALIFY',
  'DRAFT_MESSAGE',
]);
export type CrmSuggestedActionType = z.infer<typeof CrmSuggestedActionTypeSchema>;

/** `YYYY-MM-DD` (dia civil, sem fuso — resolvido no fuso do quadro). */
export const CRM_ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** `HH:mm` em 24h. */
export const CRM_HH_MM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
/** Teto de ações por análise — acima disso a lista vira ruído na aba. */
export const CRM_MAX_SUGGESTED_ACTIONS = 6;

/**
 * Uma ação sugerida, já RESOLVIDA pelo servidor (union estrita: cada tipo só
 * carrega os campos que usa). O formato cru que o modelo devolve é flat e
 * tolerante — vive no backend (`lib/suggestActions.ts`), como o responseSchema
 * do Gemini; aqui fica só o contrato client↔server.
 */
export const CrmSuggestedActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('MOVE_COLUMN'),
    justification: z.string().trim().max(400),
    toColumnId: z.string().min(1),
    /** Nome da coluna denormalizado — a UI exibe sem re-resolver o id. */
    toColumnName: z.string().min(1),
  }),
  z.object({
    type: z.literal('CREATE_TASK'),
    justification: z.string().trim().max(400),
    // Teto de 200 espelha CrmTaskSchema.title.
    title: z.string().trim().min(1).max(200),
    dueDate: z.string().regex(CRM_ISO_DATE_RE).nullable(),
  }),
  z.object({
    type: z.literal('CREATE_APPOINTMENT'),
    justification: z.string().trim().max(400),
    date: z.string().regex(CRM_ISO_DATE_RE),
    time: z.string().regex(CRM_HH_MM_RE),
    /** Epoch resolvido no fuso do quadro — só para exibir "quando" formatado.
     *  O epoch GRAVADO nasce do modal (o usuário pode editar antes de salvar). */
    startAt: z.number().int(),
    // Teto de 300 espelha CrmAppointmentSchema.description.
    description: z.string().trim().max(300).nullable(),
  }),
  z.object({
    type: z.literal('QUALIFY'),
    justification: z.string().trim().max(400),
  }),
  z.object({
    type: z.literal('DRAFT_MESSAGE'),
    justification: z.string().trim().max(400),
    channel: CrmMessageChannelSchema,
  }),
]);
export type CrmSuggestedAction = z.infer<typeof CrmSuggestedActionSchema>;

/** Pede as ações sugeridas de um lead. `refresh` força regenerar (ignora cache). */
export const CrmLeadSuggestActionsInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  refresh: z.boolean().default(false),
});
export type CrmLeadSuggestActionsInput = z.infer<typeof CrmLeadSuggestActionsInputSchema>;

/** Retorno de `crmLeadSuggestActions`. Lista vazia = nada acionável nas notas. */
export const CrmLeadSuggestActionsResultSchema = z.object({
  actions: z.array(CrmSuggestedActionSchema).max(CRM_MAX_SUGGESTED_ACTIONS),
  stageName: z.string().nullable(),
  cached: z.boolean(),
  signature: z.string(),
});
export type CrmLeadSuggestActionsResult = z.infer<typeof CrmLeadSuggestActionsResultSchema>;

// ---------------------------------------------------------------------------
// Feedback do conselho + autotreinamento curado (Fase C)
// ---------------------------------------------------------------------------

/** Doc de aprendizados auto-gerados (curados pelo admin) em `crm_appConfig`. */
export const CRM_AI_LEARNINGS_DOC_ID = 'aiLearnings';

export const CrmAdviceRatingSchema = z.enum(['UP', 'DOWN']);
export type CrmAdviceRating = z.infer<typeof CrmAdviceRatingSchema>;

/**
 * Feedback 👍/👎 de um conselho da IA (`crm_ai_feedback`). `signature` liga o
 * feedback à versão exata do conselho mostrado. `comment` opcional (só faz
 * sentido no 👎, mas é aceito nos dois).
 */
export const CrmAdviceFeedbackInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  signature: z.string().min(1).max(4000),
  rating: CrmAdviceRatingSchema,
  comment: z.string().trim().max(1000).nullable().optional(),
});
export type CrmAdviceFeedbackInput = z.infer<typeof CrmAdviceFeedbackInputSchema>;

export const CrmAiLearningStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export type CrmAiLearningStatus = z.infer<typeof CrmAiLearningStatusSchema>;

/**
 * Um aprendizado auto-gerado (leads ganhos/perdidos + feedback 👎), CURADO pelo
 * admin. Só `APPROVED` entra no prompt do conselho. `stageName` vazio = geral
 * (vale em qualquer fase). Nada gerado pela IA vira orientação sem aprovação
 * humana — mitiga prompt drift e vazamento de dado sensível.
 */
export const CrmAiLearningSchema = z.object({
  id: z.string().min(1),
  stageName: z.string().trim().max(80).default(''),
  text: z.string().trim().min(1).max(600),
  evidence: z.string().trim().max(600).nullable().optional(),
  status: CrmAiLearningStatusSchema.default('PENDING'),
  createdAt: z.number().int().nonnegative(),
  reviewedAt: z.number().int().nullable().optional(),
  reviewedBy: z.string().nullable().optional(),
});
export type CrmAiLearning = z.infer<typeof CrmAiLearningSchema>;

/** `crm_appConfig/aiLearnings` — leitura tolerante (safeParse + fallback vazio). */
export const CrmAiLearningsSchema = z.object({
  items: z.array(CrmAiLearningSchema).max(300).default([]),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmAiLearnings = z.infer<typeof CrmAiLearningsSchema>;

/** Gera aprendizados a partir dos últimos N dias de leads fechados + feedback. */
export const CrmGenerateAiLearningsInputSchema = z.object({
  days: z.number().int().min(7).max(365).default(90),
});
export type CrmGenerateAiLearningsInput = z.infer<typeof CrmGenerateAiLearningsInputSchema>;

/** Curadoria de um aprendizado (aprovar/rejeitar/apagar), com edição opcional. */
export const CrmReviewAiLearningInputSchema = z.object({
  id: z.string().min(1),
  action: z.enum(['APPROVE', 'REJECT', 'DELETE']),
  text: z.string().trim().min(1).max(600).nullable().optional(),
  stageName: z.string().trim().max(80).nullable().optional(),
});
export type CrmReviewAiLearningInput = z.infer<typeof CrmReviewAiLearningInputSchema>;

// ---------------------------------------------------------------------------
// Base de conhecimento em blocos + busca semântica (#153)
// ---------------------------------------------------------------------------

/**
 * Categoria de um bloco de conhecimento. Enum fechado (não texto livre): a IA
 * seleciona blocos por categoria conforme o recurso (ex.: `OBJECTION_HELP` puxa
 * OBJECTION; roteiro puxa CALL_SCRIPT/DEMO_SCRIPT). O rótulo humano é localizado.
 */
export const CRM_KNOWLEDGE_CATEGORIES = [
  'OBJECTION', // objeção + resposta
  'SCRIPT', // fala/mensagem pronta para uma situação
  'FOLLOW_UP', // cadência/abordagem de follow-up
  'CALL_SCRIPT', // roteiro de ligação
  'DEMO_SCRIPT', // roteiro de demonstração
  'QUALIFICATION', // critério/pergunta de qualificação
  'CHECKLIST', // item de checklist por etapa
  'PRODUCT_FACT', // fato/argumento de produto
  'CLOSING', // negociação/fechamento
  'GENERAL', // transversal, sem categoria específica
] as const;
export const CrmKnowledgeCategorySchema = z.enum(CRM_KNOWLEDGE_CATEGORIES);
export type CrmKnowledgeCategory = z.infer<typeof CrmKnowledgeCategorySchema>;

/**
 * Sugestões para o campo `personas` do bloco (dropdown na tela admin). NÃO é
 * enum fechado: o admin pode digitar valores custom. Quem é o interlocutor.
 */
export const CRM_KNOWLEDGE_PERSONA_SUGGESTIONS = [
  'Dono / Proprietário',
  'Sócio',
  'Gerente operacional',
  'Chef',
  'Nutricionista / Resp. técnico',
  'Comprador',
  'Franqueado',
] as const;

/**
 * Sugestões para o campo `segments` do bloco (dropdown na tela admin). NÃO é
 * enum fechado: o admin pode digitar valores custom. Tipo de estabelecimento —
 * casa com o `businessType`/`segment` do lead (match case-insensitive).
 */
export const CRM_KNOWLEDGE_SEGMENT_SUGGESTIONS = [
  'Restaurante',
  'Bar',
  'Lanchonete',
  'Padaria',
  'Confeitaria',
  'Pizzaria',
  'Hamburgueria',
  'Cafeteria',
  'Hotel',
  'Buffet / Catering',
  'Dark kitchen',
  'Food truck',
  'Açougue',
  'Mercado / Hortifruti',
] as const;

/**
 * Ciclo de vida de um bloco. Blocos extraídos por IA nascem `PENDING_REVIEW`
 * (gate humano obrigatório); só `ACTIVE` entra no retrieval do assistente.
 * `ARCHIVED` some da IA sem apagar o histórico.
 */
export const CrmKnowledgeBlockStatusSchema = z.enum(['PENDING_REVIEW', 'ACTIVE', 'ARCHIVED']);
export type CrmKnowledgeBlockStatus = z.infer<typeof CrmKnowledgeBlockStatusSchema>;

/** Proveniência de um bloco: cadastrado à mão ou extraído de um playbook. */
export const CrmKnowledgeSourceSchema = z.object({
  type: z.enum(['MANUAL', 'PLAYBOOK_EXTRACTION']),
  playbookSlug: CrmPlaybookSlugSchema.nullable().optional(),
  playbookVersion: z.number().int().nullable().optional(),
  sectionHeading: z.string().trim().max(300).nullable().optional(),
});
export type CrmKnowledgeSource = z.infer<typeof CrmKnowledgeSourceSchema>;

/**
 * `crm_knowledge_blocks/{blockId}` (ID aleatório) — um bloco granular da base de
 * conhecimento comercial. O campo `embedding` (VectorValue de 768 dims) NÃO está
 * neste schema de propósito: só o Admin SDK o escreve/lê e o parse tolerante do
 * cliente descarta chaves desconhecidas. `stageNames` usa os nomes de
 * `CRM_DEFAULT_SALES_STAGES` (`[]` = transversal), espelhando os playbooks.
 */
export const CrmKnowledgeBlockSchema = z.object({
  title: z.string().trim().min(1).max(200),
  category: CrmKnowledgeCategorySchema,
  stageNames: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  personas: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
  segments: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
  situation: z.string().trim().max(400).default(''),
  guidance: z.string().trim().min(1).max(2000),
  script: z.string().trim().max(2000).nullable().optional().transform(sentinelToNull),
  examples: z.array(z.string().trim().min(1).max(1000)).max(5).default([]),
  tags: z.array(z.string().trim().min(1).max(40)).max(15).default([]),
  priority: z.number().int().min(0).max(100).default(50),
  language: CrmLanguageSchema.default('pt'),
  status: CrmKnowledgeBlockStatusSchema.default('PENDING_REVIEW'),
  source: CrmKnowledgeSourceSchema,
  // Metadados do embedding (o vetor em si vive fora do Zod). `embeddingTextHash`
  // é o sha256 do texto embedado — reindexa só quando muda.
  embeddingModel: z.string().nullable().optional(),
  embeddingUpdatedAt: z.number().int().nullable().optional(),
  embeddingTextHash: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
});
export type CrmKnowledgeBlock = z.infer<typeof CrmKnowledgeBlockSchema>;

/** Bloco + id do doc (o cliente lê a coleção direto; o id vem do snapshot). */
export const CrmKnowledgeBlockEntrySchema = CrmKnowledgeBlockSchema.extend({
  id: z.string().min(1),
});
export type CrmKnowledgeBlockEntry = z.infer<typeof CrmKnowledgeBlockEntrySchema>;

/**
 * Cria (id null) ou edita (id preenchido) um bloco. `status` aqui permite
 * aprovar (PENDING_REVIEW → ACTIVE) na mesma chamada. Só ADMIN.
 */
export const CrmUpsertKnowledgeBlockInputSchema = z.object({
  id: z.string().min(1).nullable(),
  title: z.string().trim().min(1).max(200),
  category: CrmKnowledgeCategorySchema,
  stageNames: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  personas: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
  segments: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
  situation: z.string().trim().max(400).default(''),
  guidance: z.string().trim().min(1).max(2000),
  script: z.string().trim().max(2000).nullable().optional().transform(sentinelToNull),
  examples: z.array(z.string().trim().min(1).max(1000)).max(5).default([]),
  tags: z.array(z.string().trim().min(1).max(40)).max(15).default([]),
  priority: z.number().int().min(0).max(100).default(50),
  language: CrmLanguageSchema.default('pt'),
  status: CrmKnowledgeBlockStatusSchema.default('ACTIVE'),
});
export type CrmUpsertKnowledgeBlockInput = z.infer<typeof CrmUpsertKnowledgeBlockInputSchema>;

/** Apaga um bloco (hard delete só de PENDING_REVIEW; senão arquivar). */
export const CrmDeleteKnowledgeBlockInputSchema = z.object({
  id: z.string().min(1),
});
export type CrmDeleteKnowledgeBlockInput = z.infer<typeof CrmDeleteKnowledgeBlockInputSchema>;

/** Extrai blocos de um playbook já importado (nascem PENDING_REVIEW). */
export const CrmExtractKnowledgeBlocksInputSchema = z.object({
  slug: CrmPlaybookSlugSchema,
  maxBlocks: z.number().int().min(1).max(60).default(40),
});
export type CrmExtractKnowledgeBlocksInput = z.infer<typeof CrmExtractKnowledgeBlocksInputSchema>;

/** Resultado da extração: quantos criados/pulados + avisos do modelo. */
export const CrmExtractKnowledgeBlocksResultSchema = z.object({
  created: z.number().int().nonnegative(),
  skippedDuplicates: z.number().int().nonnegative(),
  warnings: z.array(z.string()),
});
export type CrmExtractKnowledgeBlocksResult = z.infer<typeof CrmExtractKnowledgeBlocksResultSchema>;

/** Reindexa embeddings em massa (`onlyMissing` pula quem já tem vetor válido). */
export const CrmReindexKnowledgeInputSchema = z.object({
  onlyMissing: z.boolean().default(true),
});
export type CrmReindexKnowledgeInput = z.infer<typeof CrmReindexKnowledgeInputSchema>;

/** Resultado do reindex: quantos re-embedados e quantos falharam. */
export const CrmReindexKnowledgeResultSchema = z.object({
  reindexed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type CrmReindexKnowledgeResult = z.infer<typeof CrmReindexKnowledgeResultSchema>;

/**
 * Limpa blocos cujo `script` guarda uma sentinela textual ("null", "n/a"…)
 * gravada por versões antigas dos normalizadores. `dryRun` só conta os alvos.
 */
export const CrmCleanupKnowledgeInputSchema = z.object({
  dryRun: z.boolean().default(false),
});
export type CrmCleanupKnowledgeInput = z.infer<typeof CrmCleanupKnowledgeInputSchema>;

/** Resultado da limpeza: varridos, corrigidos e quantos falharam. */
export const CrmCleanupKnowledgeResultSchema = z.object({
  scanned: z.number().int().nonnegative(),
  cleaned: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type CrmCleanupKnowledgeResult = z.infer<typeof CrmCleanupKnowledgeResultSchema>;

/**
 * "Preencher com IA" no editor de bloco: o admin fornece o esqueleto (título,
 * categoria, fases, situação) e a IA sugere o restante. `guidance` aqui é a
 * orientação JÁ digitada (contexto para o modelo — o cliente não a sobrescreve);
 * vazia = a IA também gera a orientação. Nada é persistido — só sugere.
 */
export const CrmSuggestKnowledgeBlockInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  category: CrmKnowledgeCategorySchema,
  stageNames: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  situation: z.string().trim().max(400).default(''),
  guidance: z.string().trim().max(2000).default(''),
  language: CrmLanguageSchema.default('pt'),
});
export type CrmSuggestKnowledgeBlockInput = z.infer<typeof CrmSuggestKnowledgeBlockInputSchema>;

/**
 * Sugestão devolvida ao editor. Limites idênticos aos do `CrmKnowledgeBlockSchema`
 * — o que a IA devolve sempre cabe no upsert sem truncar de novo no cliente.
 */
export const CrmSuggestKnowledgeBlockResultSchema = z.object({
  guidance: z.string().trim().min(1).max(2000),
  script: z.string().trim().max(2000).nullable(),
  personas: z.array(z.string().trim().min(1).max(80)).max(10),
  segments: z.array(z.string().trim().min(1).max(80)).max(10),
  tags: z.array(z.string().trim().min(1).max(40)).max(15),
  examples: z.array(z.string().trim().min(1).max(1000)).max(5),
});
export type CrmSuggestKnowledgeBlockResult = z.infer<typeof CrmSuggestKnowledgeBlockResultSchema>;

/**
 * `crm_appConfig/knowledgeMeta` — carimbo global da base de conhecimento. É o
 * que entra na assinatura de cache do assistente (1 read barato em vez de varrer
 * todos os blocos): quando `updatedAt` muda, os conselhos regeneram.
 */
export const CRM_KNOWLEDGE_META_DOC_ID = 'knowledgeMeta';
export const CrmKnowledgeMetaSchema = z.object({
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  activeCount: z.number().int().nonnegative().nullable().optional(),
  embeddingModel: z.string().nullable().optional(),
});
export type CrmKnowledgeMeta = z.infer<typeof CrmKnowledgeMetaSchema>;

// ---------------------------------------------------------------------------
// Documentos (leitura tolerante do Firestore — validar na borda)
// ---------------------------------------------------------------------------

/**
 * Canais de entrega de uma notificação do CRM (§18). Cada tipo tem dois canais
 * independentes: push (FCM/web push) e e-mail (SendGrid). Espelha o padrão do
 * app admin (`adminNotifications.ts`).
 */
export const CRM_NOTIFICATION_CHANNELS = ['push', 'email'] as const;
export type CrmNotificationChannel = (typeof CRM_NOTIFICATION_CHANNELS)[number];

/** Estado por-canal de um tipo: cada canal ligado/desligado. */
export const CrmNotificationChannelPrefSchema = z.object({
  push: z.boolean(),
  email: z.boolean(),
});
export type CrmNotificationChannelPref = z.infer<typeof CrmNotificationChannelPrefSchema>;

/** Default quando o usuário nunca configurou: tudo ligado (opt-out explícito). */
export const CRM_NOTIFICATION_CHANNEL_DEFAULTS: CrmNotificationChannelPref = {
  push: true,
  email: true,
};

// --- Kanban: ordenação de coluna e filtros por usuário (ícones no header) ---

/**
 * Critérios de ordenação de uma coluna do Kanban. A ação REESCREVE o campo
 * `order` dos leads da coluna (server-side, afeta todos os usuários) — não é
 * um modo de visualização; o drag manual continua funcionando depois.
 */
export const CRM_KANBAN_SORT_CRITERIA = [
  'estimatedValue',
  'temperature',
  'name',
  'columnEnteredAt',
  'createdAt',
  'nextFollowUpAt',
] as const;
export const CrmKanbanSortCriterionSchema = z.enum(CRM_KANBAN_SORT_CRITERIA);
export type CrmKanbanSortCriterion = z.infer<typeof CrmKanbanSortCriterionSchema>;

export const CrmSortDirectionSchema = z.enum(['asc', 'desc']);
export type CrmSortDirection = z.infer<typeof CrmSortDirectionSchema>;

/**
 * Filtros de UMA coluna do Kanban (visuais, por usuário — ao contrário do
 * sort, não tocam nos leads). Campo ausente/vazio = filtro inativo.
 * `lastSort` só lembra a última ordenação aplicada (destaque no menu).
 * Todos os campos são `nullish`: o serializador do SDK web de Functions
 * converte `undefined` → `null` no wire, então um schema só-`optional`
 * rejeitaria no servidor um payload que o cliente validou.
 */
export const CrmKanbanColumnFiltersSchema = z.object({
  temperatures: z.array(CrmLeadTemperatureSchema).max(3).nullish(),
  overdueOnly: z.boolean().nullish(),
  ownerUserIds: z.array(z.string().min(1)).max(50).nullish(),
  minValueCents: z.number().int().nonnegative().nullable().optional(),
  maxValueCents: z.number().int().nonnegative().nullable().optional(),
  lastSort: z
    .object({
      criterion: CrmKanbanSortCriterionSchema,
      direction: CrmSortDirectionSchema,
    })
    .nullable()
    .optional(),
});
export type CrmKanbanColumnFilters = z.infer<typeof CrmKanbanColumnFiltersSchema>;

/**
 * Quantos filtros de coluna estão ativos (badge do ícone de filtro). `lastSort`
 * NÃO conta — é memória de ordenação, não filtro.
 */
export function crmKanbanActiveFilterCount(filters: CrmKanbanColumnFilters): number {
  let count = 0;
  if (filters.temperatures != null && filters.temperatures.length > 0) count += 1;
  if (filters.overdueOnly === true) count += 1;
  if (filters.ownerUserIds != null && filters.ownerUserIds.length > 0) count += 1;
  if (filters.minValueCents != null || filters.maxValueCents != null) count += 1;
  return count;
}

/** Entrada de coluna vale a pena persistir? (algum filtro ativo OU lastSort) */
export function crmKanbanColumnEntryActive(filters: CrmKanbanColumnFilters): boolean {
  return crmKanbanActiveFilterCount(filters) > 0 || filters.lastSort != null;
}

/** Preferências de Kanban de um quadro: mapa colunaId → filtros. */
export const CrmKanbanBoardPrefsSchema = z.object({
  columns: z
    .record(z.string(), CrmKanbanColumnFiltersSchema)
    .refine((m) => Object.keys(m).length <= 30, 'máximo de 30 colunas'),
});
export type CrmKanbanBoardPrefs = z.infer<typeof CrmKanbanBoardPrefsSchema>;

/** `crm_users/{uid}` */
export const CrmUserSchema = z.object({
  firebaseUid: z.string().min(1),
  name: z.string(),
  email: z.email(),
  phone: z.string().nullable().optional(),
  role: CrmRoleSchema,
  status: CrmUserStatusSchema,
  /** #743 — só em `role: PARTNER`: o parceiro (`crm_partners/{id}`) deste usuário. */
  partnerId: z.string().min(1).nullable().optional(),
  /**
   * #1078 (F2 da #960, 2026-10-29 09:31 +0100) — região em que o colaborador trabalha. Ausente =
   * `southamerica-east1` (todos hoje). A trava por região (MANAGER/SELLER/PARTNER só na própria;
   * ADMIN/AUDITOR em todas; demos para todos) chega na F6 (#1084); na F2 é só o campo. Sem
   * `.catch()`: valor desconhecido reprova o doc e o colaborador fica sem acesso — melhor que
   * vê-lo como SA e abrir dado de outra região (decisão do owner de 2026-10-28, item 3).
   */
  region: TenantRegionSchema.optional(),
  // Fallback de idioma é SEMPRE inglês (owner, 2026-07-14): doc sem o campo lê
  // como 'en'; pt só quando foi escolhido explicitamente (convite ou perfil).
  preferredLanguage: CrmLanguageSchema.default('en'),
  timezone: z.string().default('America/Sao_Paulo'),
  createdBy: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  lastLoginAt: z.number().int().nonnegative().nullable().optional(),
  // Preferências de notificação por tipo (§18) — ausente = tudo ligado. Cada
  // valor é `{push,email}` (parcial); o boolean legado é tolerado na leitura
  // (`b` = ambos os canais em `b`). Resolvido por `resolveCrmNotificationPref`.
  notificationPrefs: z
    .record(
      z.string(),
      z.union([z.boolean(), CrmNotificationChannelPrefSchema.partial()]),
    )
    .nullable()
    .optional(),
  // Tokens FCM dos dispositivos do usuário (web push, §18).
  fcmTokens: z.array(z.string()).nullable().optional(),
  // Preferências de Kanban por quadro (filtros por coluna + última ordenação),
  // gravadas só via `crmUpdateKanbanPrefs`. O `.catch(null)` é OBRIGATÓRIO:
  // `evaluateCrmAccess`/`getCrmUser` fazem safeParse do doc inteiro — sem ele,
  // um nó malformado derrubaria o login do usuário (NO_ACCESS).
  kanbanPrefs: z
    .record(z.string(), CrmKanbanBoardPrefsSchema)
    .nullable()
    .optional()
    .catch(null),
  // Perfil pessoal (dados sensíveis). Pelas rules leem o próprio, o ADMIN e o
  // AUDITOR; o MANAGER não (#173, M1).
  //
  // Os documentos do perfil (identidade, comprovantes, currículo, adicionais) e o
  // contrato de trabalho saíram em 2026-09-30 (#760): existiam para preencher o
  // contrato de representação, que não existe mais. Os campos
  // `*DocPath`/`otherDocs`/`contract*` foram apagados de produção em
  // 2026-09-30 17:05 +0200 (#760); se algum reaparecer (restore de backup), o `z.object` descarta
  // a chave no parse. Não re-declarar: sem leitor, só voltariam a ser escritos.
  jobTitle: z.string().nullable().optional(),
  // Foto de perfil no Storage (`crm_users/{uid}/photo/{arquivo}`).
  photoPath: z.string().nullable().optional(),
  linkedinUrl: z.string().nullable().optional(),
  // País do usuário em ISO 3166-1 alpha-2. Qualquer país do mundo (o time pode
  // morar fora dos mercados do produto); os cinco de CRM_DOCUMENT_SPECS ganham
  // rótulos próprios (RG/CPF…), o resto cai em rótulos genéricos.
  country: CrmCountryCodeSchema.nullable().optional(),
  /** Data de nascimento em ISO `YYYY-MM-DD` — sem hora, sem fuso. */
  birthDate: z.string().nullable().optional(),
  // Qualificação civil digitada pelo usuário no perfil.
  profession: z.string().nullable().optional(),
  maritalStatus: CrmMaritalStatusSchema.nullable().optional(),
  nationality: z.string().nullable().optional(),
  // Número do documento de identidade e cadastro fiscal (RG/CPF no BR…). Até a
  // #760 vinham da extração por IA da foto do documento; hoje são digitados.
  identityNumber: z.string().nullable().optional(),
  taxId: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  // Cidade e UF/estado — estruturados, separados do `address` (linha completa).
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  // Pessoa jurídica do vendedor (PJ/MEI), digitada à mão. O rótulo do cadastro
  // segue o país (CNPJ no BR, P.IVA na IT…) via TaxIdValidator.specFor — são os
  // specs de EMPRESA, ao contrário de taxId.
  companyName: z.string().nullable().optional(),
  companyTaxId: z.string().nullable().optional(),
  /** IBAN da conta de recebimento — gravado sempre normalizado (sem espaços, maiúsculas). */
  iban: z.string().nullable().optional(),
  /** Nome do beneficiário da conta. */
  ibanHolderName: z.string().nullable().optional(),
  // Configs que o ADMIN atribui ao vendedor na tela /admin/sellers (#115).
  /**
   * Comissão de quem VENDE (0..100, até 2 casas). Todo colaborador tem a sua,
   * independente do papel: um MANAGER que fecha a venda ganha ESTE percentual, não
   * o de gerente. Dinheiro derivado dele é cents.
   *
   * O default 10 é também a migração: o doc legado tem `commissionPercent` (campo
   * único, hoje morto) e não tem este — o parse entrega 10 para todos, que é a
   * decisão do owner ("todos passam a 10/5"). Sem escrita manual no Firestore.
   */
  sellerCommissionPercent: z.number().min(0).max(100).default(10),
  /**
   * Comissão de quem GERENCIA o vendedor (0..100), sobre a MESMA base do pagamento —
   * parcela própria e independente, não uma fatia da comissão do vendedor. Só é lida
   * quando o colaborador é o `managerUserId` do lead vendido. Default 5: ver acima.
   */
  managerCommissionPercent: z.number().min(0).max(100).default(5),
  /**
   * #748 — por quantos meses, a partir do 1º pagamento, este colaborador recebe
   * comissão de uma venda; `null` = tempo indeterminado (#780). Configurado por
   * usuário (owner, 2026-09-27). Desde a #780 (2026-09-29) é lido a CADA
   * pagamento — mudar aqui vale também para as vendas já fechadas; o snapshot
   * `/salesSplits` guarda só o fallback. Default 12 = a regra da #740 para
   * todos os docs existentes.
   */
  // Origem: #750 — 2026-09-27 12:00 +0200 (A1). Usado por: `saleSplitInternals`
  // via `readCollaborator` (Functions, `saleSplitSnapshot.ts`) e a tela do
  // colaborador no CRM (`SellerConfigPage.tsx`).
  // Leitura TOLERANTE (`.catch`), escrita estrita (`CrmUpdateUserConfigInputSchema`):
  // `getCrmUser` faz safeParse e devolve `null` — um valor fora da faixa gravado
  // à mão trancaria o colaborador fora do CRM inteiro.
  // Auditoria 2026-09-27 12:34 +0200 (#748, A1/A4, MÉDIO): era `.default`
  // estrito, e um valor inválido trancava o colaborador. Não voltar a estrito.
  // Ausente (doc anterior à #748) → 12 pelo `.default`; `null` → indeterminado
  // (#780, 2026-09-29 12:48 +0200); qualquer outra coisa → 12 pelo `.catch`.
  // Antes da #780 era só `.catch`, e `null` também virava 12 — a mesma regra
  // mora em `collaboratorCommissionWindowOf` (`salesSplit.ts`).
  commissionWindowMonths: CommissionWindowSchema.default(COMMISSION_WINDOW_MONTHS_DEFAULT).catch(
    COMMISSION_WINDOW_MONTHS_DEFAULT,
  ),
  /**
   * #748 — carteira Asaas que recebe a comissão por split. Ausente = a
   * comissão dele vai para o extrato a pagar (a venda nunca é bloqueada).
   * Leitura tolerante pelo mesmo motivo acima: o formato é conferido NO USO
   * (`asaasDestinationOrNull`), e carteira inválida vira extrato a pagar.
   * Por isso aqui é `string` com teto, e não o `AsaasWalletIdSchema` da escrita.
   *
   * Origem: #750 — 2026-09-27 12:00 +0200 (A1); tolerante desde a auditoria
   * de 2026-09-27 12:34 +0200 (#748, A1/A4, MÉDIO) — era o `AsaasWalletIdSchema`
   * estrito, e uma carteira malformada trancava o colaborador fora do CRM.
   * Usado por: `readCollaborator` (`saleSplitSnapshot.ts`) → split do Asaas.
   */
  asaasWalletId: z.string().max(128).nullable().optional().catch(null),
  /**
   * #845 — subconta Asaas (BaaS) criada pelo ADMIN para este colaborador, com
   * os dados do titular. Escrita só por `crmCreateAsaasSubaccount` (Functions).
   * Quando ACTIVE, a `wallet_id` dela é gravada também em `asaasWalletId`.
   * Leitura tolerante (`.catch(null)`) pelo mesmo motivo do `asaasWalletId`:
   * um espelho malformado não pode trancar o colaborador fora do CRM.
   *
   * Origem: #845 — 2026-10-07 18:23 +0200. Usado por: `readCollaborator`
   * (`saleSplitSnapshot.ts`, teto e modo da carteira) e a tela "Conta Asaas"
   * do CRM via `crmGetAsaasSubaccount`. Schema: `asaasSubaccount.ts`.
   */
  asaasSubaccount: CrmAsaasSubaccountSchema.nullable().optional().catch(null),
  // `participationCommissionPercent` (comissão de participação, 2026-08-27)
  // saiu em 2026-09-28 (#758), e produção foi limpa por
  // `scripts/cleanup-crm-participation.ts` no mesmo dia. Não reintroduzir.
  /**
   * Gerente responsável por este colaborador (uid de outro `crm_users` com papel
   * MANAGER). Null/ausente = sem gerente. Atribuído pelo ADMIN em /admin/sellers;
   * é hierarquia direta, distinta do `managerUserId` de quadro/lead. Tolerante a
   * docs legados sem o campo (safeParse do getCrmUser não pode derrubá-los).
   */
  managerUserId: z.string().nullable().optional(),
  /**
   * Carimbo do soft-delete (`crmRemoveCollaborator`). Ausente/null = nunca
   * removido. `.optional()` sem default de propósito: `getCrmUser` e
   * `evaluateCrmAccess` fazem safeParse do doc INTEIRO, e exigir o campo
   * trancaria todos os docs legados fora do CRM.
   */
  removedAt: z.number().int().nonnegative().nullable().optional(),
  /** Uid do ADMIN que removeu. Ausente/null = nunca removido. */
  removedBy: z.string().nullable().optional(),
});
export type CrmUser = z.infer<typeof CrmUserSchema>;

/** Tipos de notificação do CRM (§18). Todos ligados por padrão. */
export const CRM_NOTIFICATION_TYPES = [
  'newLeadAssigned',
  'nextActionDue',
  'followUpOverdue',
  'demoScheduled',
  'proposalNoResponse',
  'dealWon',
  'dealLost',
  'clientEnteredOnboarding',
  'onboardingStalled',
  'onboardingChecklistIncomplete',
  'implementationComplete',
  'commentMentioned',
  'fileAttached',
  // Um lead PARADO da sua carteira foi reivindicado por outro vendedor que
  // tentou cadastrar o mesmo documento fiscal (issue #202). Perder um lead em
  // silêncio é pior que perdê-lo: o dono anterior precisa saber para contestar.
  'leadClaimed',
  // Vínculo automático pagamento→lead por CNPJ (issue #278): um pagamento
  // Stripe chegou para um tenant cujo documento fiscal casa com um lead
  // elegível, e o CRM verificou/registrou a venda sozinho — o dono e o
  // gerente precisam saber que a venda nasceu sem clique.
  'paymentAutoVerified',
  // Pagamento chegou para um lead que EXISTE mas não está em condições de
  // verificação automática (não é WON, dados incompletos, conflito) — alguém
  // precisa agir, senão a venda/comissão não nasce (issue #278).
  'paymentActionNeeded',
  // Pagamento chegou para um tenant SEM lead com aquele documento fiscal no
  // CRM (venda direta sem CRM ou CNPJ divergente) — só o ADMIN decide o que
  // fazer (issue #278).
  'paymentWithoutLead',
  // Um pagamento que já gerou parcela PAGA foi estornado na Stripe — o ADMIN
  // precisa decidir o clawback manualmente; o dinheiro nunca é mexido
  // sozinho (issue #278).
  'commissionRefundAlert',
  // O lead abriu o checkout e NAO pagou — sessao expirada ou QR do Pix vencido
  // (#289). Chega enquanto ainda da para ligar: e a diferenca entre recuperar
  // a venda hoje e descobrir o abandono na virada do mes.
  'checkoutAbandoned',
  // O link de RECUPERAÇÃO de uma venda da Contratação venceu sem pagamento
  // (#679 F2, 9.5): a regeneração é manual — o cliente pode ter mudado de
  // ideia —, então o vendedor precisa saber que é a vez dele.
  'checkoutRecoveryExpired',
  // Monitor de padrão de uso (#886): o cliente vendido saiu da própria rotina
  // de impressão. Vendedor e gerente são quem liga — queda é risco de churn,
  // excesso pode ser oportunidade (ou reimpressão em loop), e o dia habitual
  // sem impressão costuma ser impressora parada.
  'usageWeeklyDrop',
  'usageWeeklySurge',
  'usageHabitualDayMissed',
] as const;
export type CrmNotificationType = (typeof CRM_NOTIFICATION_TYPES)[number];

/**
 * Valor armazenado de um tipo: o novo `{push,email}` (parcial) OU o boolean
 * legado (docs antigos, antes da separação por canal). Lido com tolerância.
 */
export type CrmStoredNotificationPref =
  | boolean
  | { push?: boolean | undefined; email?: boolean | undefined };
/** Mapa tipo → preferência armazenada (tipo ausente = default no read). */
export type CrmNotificationPrefs = Record<string, CrmStoredNotificationPref>;

/**
 * Resolve a preferência efetiva de um tipo em `{push,email}`, aplicando os
 * defaults (tudo ligado) a canais/tipos ausentes. Tolera o boolean legado:
 * `b` → `{push:b, email:b}`. Server e cliente usam no read (sem backfill).
 */
export function resolveCrmNotificationPref(
  type: CrmNotificationType,
  prefs: CrmNotificationPrefs | null | undefined,
): CrmNotificationChannelPref {
  const entry = prefs?.[type];
  if (typeof entry === 'boolean') return { push: entry, email: entry };
  return {
    push: entry?.push ?? CRM_NOTIFICATION_CHANNEL_DEFAULTS.push,
    email: entry?.email ?? CRM_NOTIFICATION_CHANNEL_DEFAULTS.email,
  };
}

/** `true` se o usuário deve receber o tipo naquele canal (default = ligado). */
export function crmNotificationChannelEnabled(
  prefs: CrmNotificationPrefs | null | undefined,
  type: CrmNotificationType,
  channel: CrmNotificationChannel,
): boolean {
  return resolveCrmNotificationPref(type, prefs)[channel];
}

/** Preferências default: todos os tipos com ambos os canais LIGADOS (§18). */
export function crmDefaultNotificationPrefs(): Record<CrmNotificationType, CrmNotificationChannelPref> {
  return Object.fromEntries(
    CRM_NOTIFICATION_TYPES.map((t) => [t, { ...CRM_NOTIFICATION_CHANNEL_DEFAULTS }]),
  ) as Record<CrmNotificationType, CrmNotificationChannelPref>;
}

/**
 * Audiência de cada notificação por papel — quem pode recebê-la/configurá-la.
 * AUDITOR é só-leitura (não opera leads) → não recebe alertas operacionais. É
 * gate de defesa em profundidade no fan-out e define as linhas visíveis na UI
 * de preferências por papel. Espelha `AUDIENCE_BY_KIND` do `notificationKinds`.
 */
export const CRM_NOTIFICATION_AUDIENCE: Record<CrmNotificationType, readonly CrmRole[]> = {
  newLeadAssigned: ['ADMIN', 'MANAGER', 'SELLER'],
  nextActionDue: ['ADMIN', 'MANAGER', 'SELLER'],
  followUpOverdue: ['ADMIN', 'MANAGER', 'SELLER'],
  demoScheduled: ['ADMIN', 'MANAGER', 'SELLER'],
  proposalNoResponse: ['ADMIN', 'MANAGER', 'SELLER'],
  dealWon: ['ADMIN', 'MANAGER', 'SELLER'],
  dealLost: ['ADMIN', 'MANAGER', 'SELLER'],
  clientEnteredOnboarding: ['ADMIN', 'MANAGER', 'SELLER'],
  onboardingStalled: ['ADMIN', 'MANAGER', 'SELLER'],
  onboardingChecklistIncomplete: ['ADMIN', 'MANAGER', 'SELLER'],
  implementationComplete: ['ADMIN', 'MANAGER', 'SELLER'],
  commentMentioned: ['ADMIN', 'MANAGER', 'SELLER'],
  fileAttached: ['ADMIN', 'MANAGER', 'SELLER'],
  // Vai para o dono ANTERIOR do lead — qualquer papel operacional pode sê-lo.
  leadClaimed: ['ADMIN', 'MANAGER', 'SELLER'],
  // Vai para o dono/gerente do lead vinculado automaticamente (issue #278).
  paymentAutoVerified: ['ADMIN', 'MANAGER', 'SELLER'],
  // Dono/gerente do lead precisam destravar; admins acompanham (issue #278).
  paymentActionNeeded: ['ADMIN', 'MANAGER', 'SELLER'],
  // Sem lead não há dono — decisão é administrativa (issue #278).
  paymentWithoutLead: ['ADMIN'],
  // Clawback é decisão de dinheiro do ADMIN (issue #278).
  commissionRefundAlert: ['ADMIN'],
  // Dono/gerente do lead são quem liga para recuperar a venda (#289).
  checkoutAbandoned: ['ADMIN', 'MANAGER', 'SELLER'],
  // Dono/gerente do lead geram o link novo (#679 F2).
  checkoutRecoveryExpired: ['ADMIN', 'MANAGER', 'SELLER'],
  // Dono/gerente da venda ligam para o cliente; o gerente interno de um
  // parceiro também (o usuário PARCEIRO fica fora da audiência, como em todos
  // os tipos) (#886).
  usageWeeklyDrop: ['ADMIN', 'MANAGER', 'SELLER'],
  usageWeeklySurge: ['ADMIN', 'MANAGER', 'SELLER'],
  usageHabitualDayMissed: ['ADMIN', 'MANAGER', 'SELLER'],
};

/** `true` se o papel está na audiência do tipo. */
export function crmRoleInNotificationAudience(
  role: CrmRole | null | undefined,
  type: CrmNotificationType,
): boolean {
  if (!role) return false;
  return CRM_NOTIFICATION_AUDIENCE[type].includes(role);
}

/**
 * Tipos que um papel pode configurar/receber, na ordem do catálogo. Usado pela
 * aba de notificações do perfil para listar só as linhas relevantes ao papel
 * (AUDITOR → lista vazia).
 */
export function crmNotificationsForRole(role: CrmRole | null | undefined): CrmNotificationType[] {
  return CRM_NOTIFICATION_TYPES.filter((type) => crmRoleInNotificationAudience(role, type));
}

/**
 * Métricas denormalizadas de um quadro, gravadas em `crm_boards/{id}.stats` pelo
 * job horário `crmAggregateBoardStats`. Todos os campos são tolerantes a docs
 * legados (quadro sem `stats` até o 1º run). Contagens por status do lead
 * (`CrmLeadStatusSchema`): `openCount` é o pipeline ativo; ganhos/perdidos/
 * arquivados em contadores próprios. `newLeadsToday`/`newLeadsWeek` contam por
 * `createdAt` no fuso do quadro (dia corrente / semana atual seg–dom).
 */
export const CrmBoardStatsSchema = z.object({
  openCount: z.number().int().nonnegative().default(0),
  wonCount: z.number().int().nonnegative().default(0),
  lostCount: z.number().int().nonnegative().default(0),
  archivedCount: z.number().int().nonnegative().default(0),
  newLeadsToday: z.number().int().nonnegative().default(0),
  newLeadsWeek: z.number().int().nonnegative().default(0),
  // Atividade mais recente entre os leads (max de updatedAt/createdAt/
  // columnEnteredAt); fallback para `board.updatedAt` quando o quadro não tem
  // leads. Null quando não há nenhuma referência de tempo.
  lastActivityAt: z.number().int().nonnegative().nullable().optional(),
  // Quando o job rodou (millis do servidor).
  computedAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmBoardStats = z.infer<typeof CrmBoardStatsSchema>;

/** `crm_boards/{boardId}` */
export const CrmBoardSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  createdBy: z.string(),
  managerUserId: z.string(),
  status: CrmBoardStatusSchema.default('ACTIVE'),
  visibility: CrmBoardVisibilitySchema.default('PRIVATE'),
  sellerLeadVisibility: CrmSellerLeadVisibilitySchema.default('OWN_ONLY'),
  timezone: z.string().default('America/Sao_Paulo'),
  language: CrmLanguageSchema.default('pt'),
  order: z.number().int().default(0),
  // Uid do dono quando o quadro nasceu automaticamente com o usuário (aceite de
  // convite / bootstrap). Usado para idempotência (não duplicar o quadro pessoal)
  // e para identificar o funil próprio. Boards antigos e os criados à mão não têm
  // o campo — por isso opcional.
  personalOwnerUserId: z.string().nullable().optional(),
  /**
   * #743 — quadro pessoal de um usuário PARCEIRO. É o que o perímetro exige
   * (o parceiro só age no quadro marcado com o `partnerId` dele) e o que tira
   * o quadro das métricas internas (metas, cidades, relatórios de vendas).
   */
  partnerId: z.string().min(1).nullable().optional(),
  /**
   * LEGADO, em remoção. Marcava o quadro SEMEADO pelo callable
   * `crmSeedDemoBoard` (ex.: `demo-vendas-v3`) — 120 leads fabricados para
   * demonstrar dashboards. O callable foi APAGADO: nada escreve este campo
   * hoje, e nenhum quadro novo nasce com ele.
   *
   * Sobrevive só enquanto o quadro semeado de produção ainda tiver os leads
   * inventados dentro, porque é por esta marca que as agregações os excluem
   * (o demo respondia sozinho por 82 dos 84 leads contados). Assim que
   * `scripts/cleanup-crm-seed-leads.ts` purgar e apagar a marca do doc, este
   * campo sai do schema junto com o filtro do `cityGeo`.
   *
   * NÃO volte a gravá-lo: um quadro que ganhe `seedTag` some das estatísticas
   * de cidade em silêncio.
   */
  seedTag: z.string().nullable().optional(),
  // Métricas denormalizadas do quadro, recomputadas de hora em hora pelo job
  // `crmAggregateBoardStats` (varre `crm_leads`). Só o servidor grava; o cliente
  // lê para as colunas da lista `/boards`. Ausente até o 1º run — a UI trata com
  // fallback. Ver `apps/crm-functions/src/lib/boardStats.ts`.
  stats: CrmBoardStatsSchema.nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmBoard = z.infer<typeof CrmBoardSchema>;

/** `crm_boards/{boardId}/crm_members/{uid}` */
export const CrmBoardMemberSchema = z.object({
  userId: z.string(),
  roleInBoard: CrmBoardRoleSchema,
  addedBy: z.string().nullable().optional(),
  addedAt: z.number().int().nonnegative().nullable().optional(),
  status: z.enum(['ACTIVE', 'REMOVED']).default('ACTIVE'),
});
export type CrmBoardMember = z.infer<typeof CrmBoardMemberSchema>;

/** `crm_boards/{boardId}/crm_columns/{columnId}` */
export const CrmColumnSchema = z.object({
  name: z.string().min(1),
  order: z.number().int(),
  type: CrmColumnTypeSchema.default('CUSTOM'),
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmColumn = z.infer<typeof CrmColumnSchema>;

// --- Dinheiro do lead (issue #173, M12) --------------------------------------
// Canônico: CENTS int + moeda gravada no lead. Moedas fechadas em 3 (decisão
// do owner): BR→BRL, zona do euro→EUR, resto→USD; país desconhecido → null
// (a venda resolve por `payment.currency ?? lead.currency ?? 'BRL'`).

export const CrmCurrencySchema = z.enum(['BRL', 'EUR', 'USD']);
export type CrmCurrency = z.infer<typeof CrmCurrencySchema>;

const CRM_EURO_COUNTRIES = new Set([
  'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT',
  'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
]);

/** Moeda derivada do país do lead (regra das 3 moedas). */
export function crmCurrencyForCountry(
  countryCode: string | null | undefined,
): CrmCurrency | null {
  const cc = (countryCode ?? '').trim().toUpperCase();
  if (!cc) return null;
  if (cc === 'BR') return 'BRL';
  return CRM_EURO_COUNTRIES.has(cc) ? 'EUR' : 'USD';
}

/**
 * Parse de valor digitado (unidades, vírgula OU ponto decimal) para cents int.
 * `''` → null (campo vazio); formato inválido — inclusive 3+ casas decimais
 * ("499,999", o erro de vírgula do M12) e separador de milhar ("1.234,56") —
 * → NaN (teste com `Number.isNaN` e bloqueie o envio).
 */
export function crmParseMoneyToCents(input: string): number | null {
  const v = input.trim();
  if (v === '') return null;
  const match = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(v);
  if (!match) return Number.NaN;
  const units = Number(match[1]);
  const fraction = match[2] ?? '';
  const fractionCents = fraction === '' ? 0 : Number(fraction.padEnd(2, '0'));
  return units * 100 + fractionCents;
}

type CrmLeadMoneyData = {
  estimatedValue?: number | null | undefined;
  estimatedValueCents?: number | null | undefined;
  soldValue?: number | null | undefined;
  soldValueCents?: number | null | undefined;
};

/** Valor estimado em cents; docs legados (float em unidades) convertem na leitura. */
export function crmLeadEstimatedCents(lead: CrmLeadMoneyData | null | undefined): number | null {
  if (!lead) return null;
  if (lead.estimatedValueCents != null) return lead.estimatedValueCents;
  return lead.estimatedValue != null ? Math.round(lead.estimatedValue * 100) : null;
}

/** Valor vendido em cents; docs legados (float em unidades) convertem na leitura. */
export function crmLeadSoldCents(lead: CrmLeadMoneyData | null | undefined): number | null {
  if (!lead) return null;
  if (lead.soldValueCents != null) return lead.soldValueCents;
  return lead.soldValue != null ? Math.round(lead.soldValue * 100) : null;
}

/** Cents → unidades, para display (`formatCurrency`) e agregados legados em unidades. */
export function crmCentsToUnits(cents: number | null | undefined): number | null {
  return cents == null ? null : cents / 100;
}

/**
 * Estado do link de venda no espelho do lead (#679 F2). Os quatro primeiros
 * são os do `checkoutAttempt` (`CheckoutAttemptStatus`); os dois últimos só
 * existem aqui, gravados pela varredura diária `crmScanCheckoutRecovery`:
 * `RECOVERY_EXPIRED` — o link de recuperação (30 d) venceu e o vendedor
 * precisa gerar outro; `ABANDONED` — a venda está há mais de 5 dias sem
 * pagamento (9.7), mesma régua do passe 6 do `dailyBillingTick`.
 */
export const CrmLeadCheckoutStatusSchema = z.enum([
  'OPEN',
  'EXPIRED',
  'FAILED',
  'COMPLETED',
  'RECOVERY_EXPIRED',
  'ABANDONED',
]);
export type CrmLeadCheckoutStatus = z.infer<typeof CrmLeadCheckoutStatusSchema>;

/** Um item da composição vendida, em cents inteiros. */
export const CrmSaleItemSchema = z.object({
  sku: SkuSchema,
  amountCents: z.number().int().nonnegative(),
});
export type CrmSaleItem = z.infer<typeof CrmSaleItemSchema>;

/**
 * Espelho SERVER-ONLY do link de venda (`checkoutAttempts/{sessionId}`), no
 * lead (#679 §4.2, F2). Existe porque o CRM não lê `checkoutAttempts` (rules:
 * só super-admin) e a tela Contratação precisa do status ao vivo. Escrito por
 * `crmOnCheckoutAttemptWritten` (cada transição) e por `crmScanCheckoutRecovery`
 * (lembrete, recuperação vencida, abandono); fora da allowlist das rules.
 */
export const CrmLeadCheckoutSchema = z.object({
  sessionId: z.string(),
  tenantId: z.string(),
  status: CrmLeadCheckoutStatusSchema,
  /** Link vivo da sessão; `null` depois que ela expira. */
  url: z.string().nullable().default(null),
  /** Link de recuperação da Stripe (vale 30 d a partir da expiração). */
  recoveryUrl: z.string().nullable().default(null),
  recoveryExpiresAt: z.number().int().nullable().default(null),
  expiresAt: z.number().int().nullable().default(null),
  items: z.array(CrmSaleItemSchema).default([]),
  billingCycle: BillingCycleSchema,
  totalCents: z.number().int().nonnegative(),
  currency: z.string(),
  /** Quem gerou o link (uid do colaborador — `sales_origin.created_by_uid`). */
  generatedBy: z.string().nullable().default(null),
  /** Criação da sessão (`checkoutAttempt.created_at`) — a ordem do espelho. */
  generatedAt: z.number().int(),
  updatedAt: z.number().int(),
  /** Lembrete do dia 20 da recuperação já enviado ao cliente (9.5). */
  reminderSentAt: z.number().int().nullable().default(null),
});
export type CrmLeadCheckout = z.infer<typeof CrmLeadCheckoutSchema>;

/**
 * Como o lead virou WON: clique do colaborador, pagamento do link (F2) ou
 * ativação pelo parceiro, sem pagamento a nós (#743).
 */
export const CrmLeadWonViaSchema = z.enum(['MANUAL', 'CHECKOUT', 'PARTNER_ACTIVATION']);
export type CrmLeadWonVia = z.infer<typeof CrmLeadWonViaSchema>;
/** Constantes de `wonVia` (#768, auditoria 2026-09-28): compare por elas, nunca pelo texto. */
export const CrmLeadWonVia = CrmLeadWonViaSchema.enum;

/** `crm_boards/{boardId}/crm_leads/{leadId}` */
export const CrmLeadSchema = z.object({
  boardId: z.string(),
  columnId: z.string(),
  // Quando o lead entrou na coluna atual (millis), carimbado em `crmMoveLead`.
  // O DOC não guarda histórico de movimentação (issue #109) — a trilha vive em
  // `crm_audit_logs` (aba Auditoria, #277); este campo é o proxy de "tempo em
  // estágio", usado pelo scan `proposalNoResponse`. Tolerante a docs legados
  // sem o campo.
  columnEnteredAt: z.number().int().nullable().optional(),
  ownerUserId: z.string(),
  managerUserId: z.string().nullable().optional(),
  /**
   * Quem fez a ÚLTIMA transferência dirigida deste lead (`crmTransferLead`), e
   * quando. Registro de quem entregou o lead — nada o usa em cálculo. Foi a
   * base da comissão de participação (2026-08-27), removida na #758; o campo
   * ficou como rastro da transferência (owner, 2026-09-28).
   *
   * Sempre a última: em `A → G → B`, quem transferiu é `G` — o lead passou
   * adiante e A saiu da jogada. Transferir para si mesmo, reivindicar lead frio
   * (`crmClaimColdLead`) e herdar carteira de colaborador removido gravam
   * `null`: nenhum dos três é ENTREGA de lead.
   *
   * SERVER-ONLY (fora da allowlist das rules e do `CrmUpdateLeadPatchSchema`) —
   * o cliente não forja o rastro. Tolerante a docs legados sem o campo.
   */
  transferredByUserId: z.string().nullable().optional(),
  transferredAt: z.number().int().nullable().optional(),
  // Nome do RESPONSÁVEL (pessoa) — a empresa é `companyName`. Schema de LEITURA
  // tolerante: lead importado da Base CNPJ nasce sem responsável quando a
  // Receita não traz sócio (7% da base), e `min(1)` faria o doc falhar no parse
  // e sumir do funil. A escrita continua exigindo preenchido
  // (`CrmUpdateLeadPatchSchema`, `CrmCreateLeadInputSchema`).
  name: z.string(),
  companyName: z.string().nullable().optional(),
  // Razão social (#998, 2026-10-18): `companyName` é o nome FANTASIA (título do
  // card, `cnpjLeadDraft`). A venda RESELLER no BR exige este campo
  // (`crmLeadLegalNameMissing`) e o congela em `tenants/{id}.legalName`, que vai
  // no corpo da NFS-e do parceiro. Pré-preenchido pela base CNPJ e pelo dossiê.
  legalName: z.string().nullable().optional(),
  // Documento fiscal — campo genérico; o label muda por país na UI (crmTaxLabel).
  // Sem validação rígida no schema (campo livre por decisão do owner).
  taxId: z.string().nullable().optional(),
  taxIdType: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  whatsapp: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  // Endereço ESTRUTURADO (#771, 2026-09-28 17:59 +0200). O Asaas exige CEP, rua, número e
  // bairro no cliente (a NFS-e de boleto/Pix era recusada com "Endereço do
  // cliente incompleto; CEP inválido"), e `address` é linha livre. No Brasil o
  // editor monta `address` a partir destas partes; a base de CNPJ já as traz
  // separadas (`cnpjLeadDraft`). `postalCode` só dígitos (8 no BR). Fora do BR
  // ficam vazias e nada muda. Quem lê: `crmLeadBrAddressMissing` (Contratação,
  // cliente e servidor) e `readTenantFacts` (Functions, cliente do Asaas).
  postalCode: z.string().nullable().optional(),
  street: z.string().nullable().optional(),
  streetNumber: z.string().nullable().optional(),
  complement: z.string().nullable().optional(),
  district: z.string().nullable().optional(),
  segment: z.string().nullable().optional(),
  businessType: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  // Dinheiro do lead (issue #173, M12): canônico em CENTS int (regra do
  // projeto) + moeda gravada. `estimatedValue`/`soldValue` (float em UNIDADES)
  // são legado de LEITURA — docs antigos os carregam até a migração
  // (`crmMigrateLeadMoney`); nada novo os escreve. Leia sempre pelos helpers
  // `crmLeadEstimatedCents`/`crmLeadSoldCents` (fazem o fallback).
  estimatedValue: z.number().nonnegative().nullable().optional(),
  estimatedValueCents: z.number().int().nonnegative().nullable().optional(),
  currency: CrmCurrencySchema.nullable().optional(),
  planInterest: CrmPlanInterestSchema.nullable().optional(),
  temperature: CrmLeadTemperatureSchema.default('COLD'),
  status: CrmLeadStatusSchema.default('OPEN'),
  // Próxima ação — texto livre; a DATA reusa nextFollowUpAt (já indexado).
  nextAction: z.string().nullable().optional(),
  // Perda estruturada (§10): código + observação. `lostReason` legado mantido
  // para leitura tolerante de docs antigos.
  lostReason: z.string().nullable().optional(),
  lostReasonCode: CrmLostReasonSchema.nullable().optional(),
  lostReasonNote: z.string().nullable().optional(),
  // Não qualificado (§13) / duplicado (§14).
  notQualifiedReason: CrmNotQualifiedReasonSchema.nullable().optional(),
  duplicateOfLeadId: z.string().nullable().optional(),
  nextFollowUpAt: z.number().int().nullable().optional(),
  /**
   * Dedupe das notificações de follow-up (issue #173, B9) — `${nextFollowUpAt}:${tipo}`
   * do último aviso enviado pelo `crmScanFollowUps`. Escrito SÓ pelo job (Admin
   * SDK); fora da allowlist de escrita direta do cliente nas rules. Reagendar o
   * follow-up muda a chave e volta a avisar, que é o comportamento desejado.
   */
  followUpNotifiedKey: z.string().nullable().optional(),
  lastContactAt: z.number().int().nullable().optional(),
  firstContactAt: z.number().int().nullable().optional(),
  wonAt: z.number().int().nullable().optional(),
  lostAt: z.number().int().nullable().optional(),
  closedAt: z.number().int().nullable().optional(),
  // Venda ganha (§6): plano e valor efetivamente vendidos. `soldValueCents` é
  // o canônico; `soldValue` (unidades) é legado de leitura (M12, ver acima).
  soldPlan: CrmPlanInterestSchema.nullable().optional(),
  soldValue: z.number().nonnegative().nullable().optional(),
  soldValueCents: z.number().int().nonnegative().nullable().optional(),
  // Como a venda foi ganha (#679 F2): `CHECKOUT` = o pagamento do link da
  // Contratação ganhou o lead sozinho. Server-only. Ausente em lead legado.
  wonVia: CrmLeadWonViaSchema.nullable().optional().catch(undefined),
  // Espelho do link de venda (#679 F2) — ver `CrmLeadCheckoutSchema`. O
  // `.catch(undefined)` impede que um espelho malformado derrube o parse do
  // lead inteiro (o card sumiria do funil).
  checkout: CrmLeadCheckoutSchema.nullable().optional().catch(undefined),
  // Vínculo com o tenant PAGO do app principal (issue #168), gravado pelo
  // vínculo (`linkLeadToTenant`). É o docId de `tenants/`: `{CC}-{autoId}`
  // desde a #1005, `${countryCode}-${taxIdNormalizado}` nos tenants antigos —
  // nunca derive do documento (`resolveLeadTenantId`).
  tenantId: z.string().nullable().optional(),
  // Quando o lock de edição ativou (primeiro pagamento Stripe vinculado).
  // Com o lock, campos já preenchidos ficam imutáveis: as rules negam a
  // escrita direta e `crmUpdateLead` (único caminho) trava campo a campo.
  billingLockedAt: z.number().int().nullable().optional(),
  // = leadId quando a venda foi registrada em `crm_sales` (atalho p/ UI).
  saleId: z.string().nullable().optional(),
  // Foto do tenant no último "Sincronizar Aplicativo" (KPIs da aba onboarding).
  // Server-only, como `tenantId` — as rules negam a escrita pelo cliente.
  tenantSnapshot: CrmTenantSnapshotSchema.nullable().optional(),
  // Client Success / onboarding (§7/§8): status separado do comercial + checklist.
  onboardingStatus: CrmOnboardingStatusSchema.nullable().optional(),
  // Mapa item→data de marcação (millis). Marcado = valor number; `true` legado é
  // tolerado (marcado sem data conhecida); desmarcado = sem chave.
  onboardingChecklist: z.record(z.string(), z.union([z.number().int().nonnegative(), z.boolean()])).default({}),
  // Quando o onboarding foi concluído (status DONE). Congela a contagem de dias.
  onboardingDoneAt: z.number().int().nullable().optional(),
  // Marcos de PRIMEIRA entrada em estágios-chave do funil (millis), carimbados em
  // `crmMoveLead`. O CRM não guarda histórico de movimentação (issue #109); estes
  // marcos são o que permite contar "propostas/demos/negociações no período" para
  // as METAS (crm_goals). Só de agora em diante — leads que passaram por esses
  // estágios antes da feature ficam sem o marco (sem contagem retroativa).
  stageReachedAt: z
    .object({
      proposal: z.number().int().nullable().optional(),
      demoDone: z.number().int().nullable().optional(),
      negotiation: z.number().int().nullable().optional(),
    })
    .nullable()
    .optional(),
  // PRIMEIRA entrada em CADA coluna do quadro (columnId → millis): a coluna
  // inicial nasce carimbada no `createLead` (rules restringem a chave ao
  // `columnId` do create) e as demais no `crmMoveLead`. Não é histórico (issue
  // #109) — só o marco de 1ª entrada, sem retroativo para leads antigos —
  // alimenta as datas da barra de funil do detalhe do lead. Ao mover de quadro
  // (`crmMoveLeadToBoard`) o mapa RESETA: columnIds do quadro de origem não
  // significam nada no destino.
  columnReachedAt: z.record(z.string(), z.number().int()).nullable().optional(),
  // Última mudança do onboarding (status/checklist), carimbada em
  // `crmUpdateOnboarding`. Distinto do `updatedAt` genérico do lead — usado pelo
  // scan `onboardingStalled` para "parado há N dias". Tolerante a docs legados
  // sem o campo (ficam sem alerta de travado até a próxima edição).
  onboardingUpdatedAt: z.number().int().nullable().optional(),
  // Itens avulsos só deste lead (§8) — rótulo livre. O estado (data/desmarcado)
  // fica no mapa `onboardingChecklist` acima, pela `key` `extra_*`.
  onboardingExtraItems: z.array(CrmOnboardingExtraItemSchema).default([]),
  notes: z.string().nullable().optional(),
  // Metadados de apresentação do cabeçalho (fora do lock de billing — não
  // entram em CRM_BILLING_LOCKED_LEAD_FIELDS nem na allowlist das rules;
  // caminho único de escrita é o `crmUpdateLead`).
  // Favorito é DO LEAD (compartilhado no quadro), não por usuário.
  favorite: z.boolean().default(false),
  // Logo da empresa no Storage: `crm_boards/{boardId}/lead_logos/{leadId}/...`.
  logoPath: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  hasFiles: z.boolean().default(false),
  filesCount: z.number().int().nonnegative().default(0),
  // Chave de ordenação do card na coluna. Number (não int) para permitir
  // índice fracionário no reorder — inserir entre dois cards = média.
  order: z.number().default(0),
  createdBy: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  // Quem fez a última edição DIRETA de cadastro (uid) — gravado pelo cliente
  // junto com `updatedAt`; as rules exigem que seja o próprio `auth.uid`
  // (anti-spoof). O trigger de auditoria (#277) o usa como ator do
  // `LEAD_UPDATED`. Tolerante a docs legados/bundle antigo (fallback:
  // `ownerUserId`).
  updatedBy: z.string().nullable().optional(),
  // Marcador SERVER-ONLY (#277): callables que editam cadastro e já gravam a
  // própria entrada de audit (`crmUpdateLead`, `crmSetLeadTaxId`) o renovam no
  // patch; o trigger `crmOnLeadAuditUpdated` pula a escrita quando ele mudou,
  // evitando entrada duplicada. Fora da allowlist das rules — cliente não forja.
  serverEditedAt: z.number().int().nullable().optional(),
  archivedAt: z.number().int().nullable().optional(),
});
export type CrmLead = z.infer<typeof CrmLeadSchema>;

/**
 * Identidade do lead em texto puro (corpo de notificação, e-mail, log): a
 * EMPRESA, que é o que o vendedor reconhece; sem ela, o responsável. Espelho
 * servidor do `leadDisplayName` do `apps/crm` — os dois têm que dizer a mesma
 * coisa, senão o push mostra um nome e o card mostra outro. Nunca devolve
 * string vazia por escolha: `name` pode ser `""` em lead importado sem sócio.
 */
export function crmLeadLabel(lead: Pick<CrmLead, 'name' | 'companyName'>): string {
  const company = lead.companyName?.trim() ?? '';
  return company !== '' ? company : lead.name.trim();
}

/**
 * Rótulo do lead com o responsável quando ele existe e não é o próprio título
 * ("EMPRESA — Fulano"). Usado nos corpos de notificação.
 */
export function crmLeadLabelWithContact(lead: Pick<CrmLead, 'name' | 'companyName'>): string {
  const label = crmLeadLabel(lead);
  const person = lead.name.trim();
  return person === '' || person === label ? label : `${label} — ${person}`;
}

/**
 * Identidade do lead para prompt de IA: `"Fulano (EMPRESA)"`. Cai para só um
 * dos dois quando o outro falta — sem isso, lead importado sem sócio geraria
 * `"Lead:  (EMPRESA)"`, e o prompt manda "use o nome do lead" (o modelo
 * inventaria um). Recebe os campos soltos porque quem monta o prompt já os
 * carrega separados no contexto.
 */
export function crmLeadIdentityLine(name: string, companyName: string | null): string {
  const person = name.trim();
  const company = companyName?.trim() ?? '';
  if (person === '') return company;
  return company === '' ? person : `${person} (${company})`;
}

/** `crm_boards/{boardId}/crm_leads/{leadId}/crm_files/{fileId}` (§2 anexos). */
export const CrmFileSchema = z.object({
  name: z.string(),
  path: z.string(),
  size: z.number().int().nonnegative(),
  contentType: z.string().nullable().optional(),
  uploadedBy: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmFile = z.infer<typeof CrmFileSchema>;

/**
 * `crm_boards/{boardId}/crm_leads/{leadId}/crm_appointments/{id}` — compromisso
 * do lead. Um lead tem N compromissos.
 *
 * `lead.nextFollowUpAt` / `lead.nextAction` são o **espelho** do próximo
 * compromisso pendente (menor `startAt` com `done === false`), mantido para o
 * Kanban, a Agenda semanal e o job `crmScanFollowUps` continuarem lendo um
 * campo plano no lead. A subcoleção é a fonte; o espelho é derivado.
 */
export const CrmAppointmentSchema = z.object({
  startAt: z.number().int().nonnegative(),
  description: z.string().trim().max(300).nullable().optional(),
  done: z.boolean().default(false),
  doneAt: z.number().int().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmAppointment = z.infer<typeof CrmAppointmentSchema>;

/** `crm_boards/{boardId}/crm_tasks/{taskId}` — tarefa de um lead do quadro. */
export const CrmTaskSchema = z.object({
  leadId: z.string(),
  title: z.string().min(1).max(200),
  dueAt: z.number().int().nullable().optional(),
  assigneeUid: z.string().nullable().optional(),
  done: z.boolean().default(false),
  doneAt: z.number().int().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmTask = z.infer<typeof CrmTaskSchema>;

// ---------------------------------------------------------------------------
// Inputs das Cloud Functions (callables) — Fase 1
// ---------------------------------------------------------------------------

const emailField = z.string().trim().max(200).pipe(z.email());
const nameField = z.string().trim().min(1).max(120);

/**
 * E.164 (`+5541999990000`): DDI + número, 8–15 dígitos após o `+`. O Twilio
 * recusa qualquer outro formato (erro 21211) — telefone fora disso não recebe
 * o OTP de assinatura (issue #157).
 */
export const CRM_PHONE_E164_REGEX = /^\+[1-9]\d{7,14}$/;

/** Tira espaços e pontuação de digitação: `+55 (41) 99999-0000` → `+5541999990000`. */
export function normalizeCrmPhone(raw: string): string {
  return raw.replace(/[\s().-]/g, '');
}

// Só nos INPUTS (convite, perfil): normaliza e exige E.164. Os schemas de
// LEITURA continuam tolerantes — docs legados têm telefone fora do formato.
const crmPhoneField = z
  .string()
  .trim()
  .max(40)
  .transform(normalizeCrmPhone)
  .pipe(z.string().regex(CRM_PHONE_E164_REGEX, 'Telefone deve ser E.164 (ex.: +5541999990000).'));
const tzField = z.string().trim().min(1).max(64);

/**
 * Percentual de comissão: inteiro de 1 a 20 (passos de 1%). É percentual, não
 * dinheiro — qualquer valor monetário derivado dele é inteiro em cents. Este é o
 * boundary de ESCRITA dos DOIS percentuais (o admin define via slider em
 * /admin/collaborators, na config do colaborador e no convite). A leitura dos
 * docs gravados (`CrmUserSchema.{seller,manager}CommissionPercent`) permanece
 * tolerante a valores legados (0..100, decimais) de propósito: apertá-la
 * trancaria quem já tem comissão fora da faixa fora do CRM (getCrmUser faz
 * safeParse e retorna null).
 *
 * Declarado antes dos schemas de convite que o referenciam — const em
 * import-time, mover para depois quebra (TDZ).
 */
export const CrmCommissionPercentSchema = z.number().int().min(1).max(20);

export const CrmCreateInviteInputSchema = z.object({
  email: emailField,
  name: nameField,
  phone: crmPhoneField.nullable().optional(),
  role: CrmRoleSchema,
  /** #743 — obrigatório em `role: PARTNER` (validado no servidor): o parceiro do convidado. */
  partnerId: z.string().min(1).max(128).nullable().optional(),
  boardIds: z.array(z.string().min(1)).max(50).default([]),
  // Gerente direto do convidado — só se aplica a SELLER (ignorado nos demais
  // papéis). Vira `crm_users.managerUserId` no aceite. Validado no servidor
  // (tem que ser MANAGER ativo).
  managerUserId: z.string().min(1).nullable().optional(),
  // Comissões do convidado, definidas já no convite e copiadas ao crm_users no
  // aceite. Só ADMIN pode enviá-las (o servidor rejeita de MANAGER); ausentes,
  // o aceite não grava nada e valem os defaults do CrmUserSchema (10/5). A de
  // gerente só se aplica a role !== 'SELLER' (enviada para SELLER, o servidor
  // grava null — mesmo tratamento do managerUserId).
  sellerCommissionPercent: CrmCommissionPercentSchema.optional(),
  managerCommissionPercent: CrmCommissionPercentSchema.optional(),
  // Idioma escolhido para o convite — o e-mail sai neste idioma (o navegador do
  // convidado é desconhecido no momento do envio). Default = idioma da UI de
  // quem convida.
  language: CrmLanguageSchema.default('pt'),
  // Origin do cliente (`window.location.origin`) para montar o link de aceite.
  // O servidor só o aceita se estiver na allowlist; senão usa a base de produção.
  origin: z.string().trim().max(200).optional(),
});
export type CrmCreateInviteInput = z.infer<typeof CrmCreateInviteInputSchema>;

export const CrmAcceptInviteInputSchema = z.object({
  token: z.string().trim().min(10).max(200),
  // Idioma do navegador do convidado — vira `preferredLanguage` do crm_users.
  language: CrmLanguageSchema.optional(),
});
export type CrmAcceptInviteInput = z.infer<typeof CrmAcceptInviteInputSchema>;

// Consulta pública (o token é a credencial) que a tela de aceite faz ao abrir:
// devolve o e-mail atrelado ao convite (read-only na UI) e em que estado está a
// conta Auth daquele e-mail.
export const CrmGetInviteInfoInputSchema = z.object({
  token: z.string().trim().min(10).max(200),
});
export type CrmGetInviteInfoInput = z.infer<typeof CrmGetInviteInfoInputSchema>;

export const CrmInviteInfoStatusSchema = z.enum(['OK', 'EXPIRED', 'NOT_PENDING', 'NOT_FOUND']);
export type CrmInviteInfoStatus = z.infer<typeof CrmInviteInfoStatusSchema>;

/**
 * Em que estado está a conta Firebase Auth do e-mail convidado — decide o que a
 * tela de aceite oferece. Um booleano `accountExists` não bastava: conta
 * Google-only existe mas não entra com senha, e o cliente NÃO consegue
 * descobrir isso sozinho (o projeto usa email enumeration protection, então
 * `signInWithEmailAndPassword` devolve `auth/invalid-credential` tanto para
 * senha errada quanto para conta inexistente). Ver issue #288.
 *
 * - `NEW`         — não existe conta: criar senha (senha + confirmação).
 * - `PASSWORD`    — existe com provedor `password`: entrar (+ esqueci a senha).
 * - `NO_PASSWORD` — existe sem senha (Google-only, telefone): Google ou pedir
 *                   link para criar senha.
 * - `UNKNOWN`     — o lookup falhou: não adivinhar, a UI oferece os dois
 *                   caminhos em vez de fingir uma resposta.
 */
export const CrmInviteAccountModeSchema = z.enum([
  'NEW',
  'PASSWORD',
  'NO_PASSWORD',
  'UNKNOWN',
]);
export type CrmInviteAccountMode = z.infer<typeof CrmInviteAccountModeSchema>;

/** Resultado de `crmGetInviteInfo`. `email`/`name`/`accountMode` só vêm quando `status === 'OK'`. */
export type CrmInviteInfoResult = {
  status: CrmInviteInfoStatus;
  email?: string;
  name?: string;
  // Legado de antes do `accountMode` (#288). Só continua no contrato porque o
  // cliente novo pode falar com a Function antiga durante o skew de deploy —
  // ali ele cai neste booleano. Não usar em código novo; some quando não
  // houver mais versão antiga no ar.
  accountExists?: boolean;
  accountMode?: CrmInviteAccountMode;
  /** Conta existente tem provedor `google.com` (vale a pena oferecer o botão). */
  hasGoogle?: boolean;
};

export const CrmRevokeInviteInputSchema = z.object({
  inviteId: z.string().min(1),
});
export type CrmRevokeInviteInput = z.infer<typeof CrmRevokeInviteInputSchema>;

export const CrmResendInviteInputSchema = z.object({
  inviteId: z.string().min(1),
  // Mesma allowlist do create; usada para montar o novo link de aceite.
  origin: z.string().trim().max(200).optional(),
});
export type CrmResendInviteInput = z.infer<typeof CrmResendInviteInputSchema>;

export const CrmCreateBoardInputSchema = z.object({
  name: z.string().trim().min(2).max(80),
  // `nullable` além de `optional`: o SDK web serializa `undefined` como `null`
  // no payload do callable — sem isso, criar quadro sem descrição é rejeitado.
  description: z.string().trim().max(500).nullable().optional(),
  timezone: tzField.default('America/Sao_Paulo'),
  language: CrmLanguageSchema.default('pt'),
});
export type CrmCreateBoardInput = z.infer<typeof CrmCreateBoardInputSchema>;

// Edição inline do cabeçalho do quadro (nome/descrição) na própria tela do
// funil. Restrito a ADMIN global / MANAGER do quadro (revalidado no servidor).
// `description` aceita null/'' para limpar.
export const CrmUpdateBoardInputSchema = z.object({
  boardId: z.string().min(1),
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(500).nullable().optional(),
});
export type CrmUpdateBoardInput = z.infer<typeof CrmUpdateBoardInputSchema>;

export const CrmAddBoardMemberInputSchema = z.object({
  boardId: z.string().min(1),
  userId: z.string().min(1),
  /**
   * IGNORADO pelo servidor desde #173 B2 — o papel no quadro é derivado do papel
   * global do alvo (`crmBoardRoleForGlobalRole`). Continua no schema só para o
   * bundle antigo não quebrar na janela de deploy; some numa limpeza futura.
   */
  roleInBoard: CrmBoardRoleSchema,
});
export type CrmAddBoardMemberInput = z.infer<typeof CrmAddBoardMemberInputSchema>;

export const CrmRemoveBoardMemberInputSchema = z.object({
  boardId: z.string().min(1),
  userId: z.string().min(1),
  // Destino dos leads do removido (exclusivos entre si):
  // - transferToUserId: migra para outro membro;
  // - archiveLeads: "remover tudo" via SOFT DELETE (status=ARCHIVED, recuperável);
  // - nenhum: mantém com o antigo dono (que perde acesso ao quadro).
  transferToUserId: z.string().min(1).nullable().optional(),
  archiveLeads: z.boolean().optional(),
});
export type CrmRemoveBoardMemberInput = z.infer<typeof CrmRemoveBoardMemberInputSchema>;

export const CrmListBoardMembersInputSchema = z.object({
  boardId: z.string().min(1),
});
export type CrmListBoardMembersInput = z.infer<typeof CrmListBoardMembersInputSchema>;

/**
 * Lista convites PENDING. Com `boardId` → convites daquele quadro (ADMIN ou
 * MANAGER do quadro). Sem `boardId` → TODOS os convites PENDING (ADMIN apenas),
 * usado pela gestão global de colaboradores (/admin/collaborators).
 */
export const CrmListInvitesInputSchema = z.object({
  boardId: z.string().min(1).optional(),
});
export type CrmListInvitesInput = z.infer<typeof CrmListInvitesInputSchema>;

export const CrmInviteEntrySchema = z.object({
  inviteId: z.string(),
  email: z.string(),
  name: z.string(),
  role: CrmRoleSchema,
  createdAt: z.number().nullable(),
  // Quadros a que o convite dá acesso (vazio = convite global, sem quadro).
  boardIds: z.array(z.string()).optional(),
  // #787: parceiro do convite PARTNER (`null` nos demais). Opcional: Functions
  // antigas não o mandam.
  partnerId: z.string().nullable().optional(),
  // Comissões definidas no convite; null = convite legado sem os campos (o
  // aceite cai nos defaults 10/5 do CrmUserSchema). Leitura tolerante.
  sellerCommissionPercent: z.number().nullable().optional(),
  managerCommissionPercent: z.number().nullable().optional(),
});
export type CrmInviteEntry = z.infer<typeof CrmInviteEntrySchema>;

/**
 * Contato do dono dos quadros da lista (`/boards`). Vai por callable porque o
 * cliente não lê `crm_users` de terceiros (issue #173, M1): o doc carrega PII e
 * comissão, e a leitura direta que alimentava estas células anulava a ocultação
 * de comissão alheia na tela de Vendas. O servidor devolve só o contato
 * profissional, e só dos quadros que o chamador já enxerga.
 */
export const CrmListBoardOwnersInputSchema = z.object({
  // Teto alinhado ao `in` do Firestore em lotes de 30 — a lista de quadros de
  // um usuário fica muito abaixo disso.
  boardIds: z.array(z.string().min(1)).min(1).max(120),
});
export type CrmListBoardOwnersInput = z.infer<typeof CrmListBoardOwnersInputSchema>;

/** Dono de um quadro + contato profissional. Nunca PII nem comissão. */
export const CrmBoardOwnerEntrySchema = z.object({
  boardId: z.string(),
  ownerUserId: z.string(),
  name: z.string().nullable(),
  /** Foto de perfil (Storage) — avatar do dono na lista de quadros. Não-PII
   * (storage rules liberam a leitura a qualquer usuário CRM ativo). */
  photoPath: z.string().nullable().optional(),
  phone: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
});
export type CrmBoardOwnerEntry = z.infer<typeof CrmBoardOwnerEntrySchema>;

/** Membro do quadro com nome resolvido (server lê crm_users). */
export const CrmBoardMemberEntrySchema = z.object({
  userId: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  roleInBoard: CrmBoardRoleSchema,
  addedAt: z.number().nullable(),
  lastLoginAt: z.number().nullable(),
  // Foto de perfil (path no Storage `crm_users/{uid}/photo/**`) para exibir o
  // avatar do autor no feed de notas do lead. `null` = usa iniciais.
  photoPath: z.string().nullable().optional(),
});
export type CrmBoardMemberEntry = z.infer<typeof CrmBoardMemberEntrySchema>;

export const CrmMoveLeadInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  toColumnId: z.string().min(1),
  order: z.number().default(0),
});
export type CrmMoveLeadInput = z.infer<typeof CrmMoveLeadInputSchema>;

/**
 * Aba Auditoria do lead (#277): lista os eventos de `crm_audit_logs` daquele
 * lead via callable `crmListLeadAudit`. Paginação por cursor (`beforeCreatedAt`
 * = `createdAt` do último evento da página anterior).
 */
export const CrmListLeadAuditInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  limit: z.number().int().min(1).max(100).default(50),
  beforeCreatedAt: z.number().int().positive().optional(),
});
export type CrmListLeadAuditInput = z.infer<typeof CrmListLeadAuditInputSchema>;

/**
 * Evento de auditoria SANITIZADO para a aba do lead: nunca carrega os
 * `before`/`after` crus do log — só `changedFields` (nomes de campos, sem
 * valores) e um `summary` com chaves permitidas por action. `action` fica
 * string aberta de propósito: o cliente exibe o código cru quando não conhece
 * o label (ações futuras não quebram a aba).
 */
export const CrmLeadAuditEventSchema = z.object({
  id: z.string(),
  action: z.string(),
  actorUserId: z.string(),
  actorRole: CrmRoleSchema.nullable(),
  createdAt: z.number().int(),
  changedFields: z.array(z.string()).optional(),
  summary: z.record(z.string(), z.union([z.string(), z.number(), z.null()])).optional(),
});
export type CrmLeadAuditEvent = z.infer<typeof CrmLeadAuditEventSchema>;

export const CrmListLeadAuditResultSchema = z.object({
  events: z.array(CrmLeadAuditEventSchema),
  hasMore: z.boolean(),
});
export type CrmListLeadAuditResult = z.infer<typeof CrmListLeadAuditResultSchema>;

/**
 * Ordena uma coluna do Kanban reescrevendo o `order` de todos os leads OPEN
 * dela (efeito global — ver CRM_KANBAN_SORT_CRITERIA). Negado a papéis que só
 * enxergam os próprios leads no quadro (OWN_ONLY): reordenariam leads que nem
 * podem ler.
 */
export const CrmSortColumnInputSchema = z.object({
  boardId: z.string().min(1),
  columnId: z.string().min(1),
  criterion: CrmKanbanSortCriterionSchema,
  direction: CrmSortDirectionSchema,
});
export type CrmSortColumnInput = z.infer<typeof CrmSortColumnInputSchema>;

export const CrmTransferLeadInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  toUserId: z.string().min(1),
});
export type CrmTransferLeadInput = z.infer<typeof CrmTransferLeadInputSchema>;

// --- Ações de card (zonas de soltura do Kanban, §5–§14) ---

/** Ganho (§6): carimba venda, plano/valor vendidos e inicia o onboarding. */
export const CrmWinLeadInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  soldPlan: CrmPlanInterestSchema,
  // `soldValueCents` é o canônico (M12); `soldValue` (unidades) só existe para
  // o bundle antigo na janela de deploy — o servidor prefere cents.
  soldValue: z.number().nonnegative().nullable().optional(),
  soldValueCents: z.number().int().nonnegative().nullable().optional(),
  managerUserId: z.string().min(1).nullable().optional(),
});
export type CrmWinLeadInput = z.infer<typeof CrmWinLeadInputSchema>;

/** Perdido (§10): motivo obrigatório + observação opcional. */
export const CrmLoseLeadInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  lostReasonCode: CrmLostReasonSchema,
  lostReasonNote: z.string().trim().max(500).nullable().optional(),
});
export type CrmLoseLeadInput = z.infer<typeof CrmLoseLeadInputSchema>;

/** Não qualificado (§13): motivo obrigatório → arquiva. */
export const CrmDisqualifyLeadInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  notQualifiedReason: CrmNotQualifiedReasonSchema,
  note: z.string().trim().max(500).nullable().optional(),
});
export type CrmDisqualifyLeadInput = z.infer<typeof CrmDisqualifyLeadInputSchema>;

/** Duplicado (§14): vincula ao original e arquiva o duplicado. */
export const CrmMarkDuplicateInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  duplicateOfLeadId: z.string().min(1),
});
export type CrmMarkDuplicateInput = z.infer<typeof CrmMarkDuplicateInputSchema>;

/**
 * Sem resposta (§11) / Reagendar (§12): define a próxima data e a próxima ação
 * (texto composto no cliente a partir de canal/tipo). Mantém o lead OPEN.
 */
export const CrmRescheduleLeadInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  nextFollowUpAt: z.number().int().nonnegative(),
  nextAction: z.string().trim().max(300).nullable().optional(),
});
export type CrmRescheduleLeadInput = z.infer<typeof CrmRescheduleLeadInputSchema>;

/** Cria (`appointmentId: null`) ou reagenda um compromisso do lead. */
export const CrmUpsertAppointmentInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  appointmentId: z.string().min(1).nullable(),
  startAt: z.number().int().nonnegative(),
  description: z.string().trim().max(300).nullable().optional(),
  done: z.boolean().optional(),
  /**
   * `false` = NÃO materializar o follow-up legado do lead nesta escrita.
   *
   * O follow-up antigo (`nextFollowUpAt`/`nextAction` no lead) aparece na lista
   * como uma linha derivada, sem documento próprio. Ele ganha documento na
   * primeira escrita de compromisso — exceto quando é ele MESMO que está sendo
   * reagendado: aí o documento novo já carrega os valores novos, e materializar
   * o antigo em paralelo criaria dois compromissos onde o usuário pediu um.
   *
   * O cliente manda `appointmentId: null` nos dois casos (o legado não tem id
   * real), então só este campo distingue "criar novo" de "reagendar o legado".
   */
  materializeLegacy: z.boolean().optional(),
});
export type CrmUpsertAppointmentInput = z.infer<typeof CrmUpsertAppointmentInputSchema>;

export const CrmDeleteAppointmentInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  appointmentId: z.string().min(1),
});
export type CrmDeleteAppointmentInput = z.infer<typeof CrmDeleteAppointmentInputSchema>;

/** `includeDone` desligado = só compromissos pendentes (o que a agenda mostra). */
export const CrmListAppointmentsInputSchema = z.object({
  includeDone: z.boolean().default(false),
});
export type CrmListAppointmentsInput = z.infer<typeof CrmListAppointmentsInputSchema>;

/** Linha da agenda semanal: um compromisso, já com os dados do lead resolvidos. */
export const CrmAppointmentRowSchema = z.object({
  boardId: z.string(),
  leadId: z.string(),
  appointmentId: z.string(),
  startAt: z.number().int(),
  description: z.string().nullable(),
  done: z.boolean(),
  name: z.string(),
  companyName: z.string().nullable(),
  /** Dono do lead — habilita a tabela por vendedor e o drill /schedule/:userId. */
  ownerUserId: z.string(),
  ownerName: z.string().nullable(),
});
export type CrmAppointmentRow = z.infer<typeof CrmAppointmentRowSchema>;

/** Mover para outro quadro: leads são subcoleção — copia+deleta entre boards. */
export const CrmMoveLeadToBoardInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  toBoardId: z.string().min(1),
  toColumnId: z.string().min(1),
});
export type CrmMoveLeadToBoardInput = z.infer<typeof CrmMoveLeadToBoardInputSchema>;

/**
 * Path canônico de um anexo no Storage: `crm_boards/{boardId}/attachments/{fileName}`.
 * Espelha o `match` + o `fileName.matches` de `firebase/storage.rules` — se
 * divergirem, o upload passa e o metadado aponta para objeto ilegível.
 */
export function crmAttachmentPathValid(boardId: string, path: string): boolean {
  const prefix = `crm_boards/${boardId}/attachments/`;
  if (!path.startsWith(prefix)) return false;
  return /^[A-Za-z0-9_.-]{1,160}$/.test(path.slice(prefix.length));
}

/** Registra metadados de um anexo já enviado ao Storage (§2). */
export const CrmAttachFileInputSchema = z
  .object({
    boardId: z.string().min(1),
    leadId: z.string().min(1),
    name: z.string().trim().min(1).max(200),
    path: z.string().min(1).max(500),
    size: z.number().int().nonnegative().max(20 * 1024 * 1024),
    contentType: z.string().max(200).nullable().optional(),
  })
  // O `path` tem que ficar DENTRO do próprio quadro (issue #173, B10). Sem
  // isto, o metadado em `crm_files` podia apontar para o anexo de outro quadro:
  // a leitura seguia negada pelas storage rules, mas a lista mentia. Cabe no
  // Zod porque `boardId` e `path` estão no MESMO objeto — diferente do país do
  // telefone, que precisa do `tenant.countryCode` de fora.
  .refine((v) => crmAttachmentPathValid(v.boardId, v.path), {
    path: ['path'],
    message: 'Caminho do anexo fora do quadro.',
  });
export type CrmAttachFileInput = z.infer<typeof CrmAttachFileInputSchema>;

/**
 * Anexa VÁRIOS arquivos ao lead numa chamada só (`crmAttachFiles`).
 *
 * Origem: #777 — 2026-09-29 09:14 +0200 (F5 do épico #772).
 *
 * Por quê: o owner pediu e-mail a cada arquivo inserido no lead, e decidiu
 * (2026-09-29) um e-mail POR LOTE — quem arrasta 10 arquivos recebe 1 aviso,
 * não 10. A aba Arquivos sobe os binários e registra o lote de uma vez.
 *
 * Contrato: 1 a 20 arquivos (o seletor da aba não passa disso na prática);
 * cada `path` dentro do próprio quadro, mesma regra de `CrmAttachFileInputSchema`
 * (#173, B10).
 *
 * Usado por: callable `crmAttachFiles` (`apps/crm-functions/src/crmFiles.ts`)
 * ← `uploadLeadFiles` (`apps/crm/src/lib/firestoreLeads.ts`, aba Arquivos).
 */
export const CrmAttachFilesInputSchema = z
  .object({
    boardId: z.string().min(1),
    leadId: z.string().min(1),
    files: z
      .array(
        z.object({
          name: z.string().trim().min(1).max(200),
          path: z.string().min(1).max(500),
          size: z.number().int().nonnegative().max(20 * 1024 * 1024),
          contentType: z.string().max(200).nullable().optional(),
        }),
      )
      .min(1)
      .max(20),
  })
  .refine((v) => v.files.every((f) => crmAttachmentPathValid(v.boardId, f.path)), {
    path: ['files'],
    message: 'Caminho do anexo fora do quadro.',
  });
export type CrmAttachFilesInput = z.infer<typeof CrmAttachFilesInputSchema>;

/** Remove o registro de um anexo (o objeto no Storage é apagado no cliente). */
export const CrmDeleteFileInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  fileId: z.string().min(1),
});
export type CrmDeleteFileInput = z.infer<typeof CrmDeleteFileInputSchema>;

/**
 * Atualiza as preferências de notificação do usuário logado (§18). Cada tipo
 * carrega os dois canais (`{push,email}`); o mapa é parcial (só os tipos que a
 * UI mostra ao papel são enviados). Leitura tolera boolean legado, mas a
 * escrita é sempre no formato novo.
 */
export const CrmUpdateNotificationPrefsInputSchema = z.object({
  prefs: z.record(z.string(), CrmNotificationChannelPrefSchema),
});
export type CrmUpdateNotificationPrefsInput = z.infer<typeof CrmUpdateNotificationPrefsInputSchema>;

/**
 * Substitui as preferências de Kanban do usuário logado para UM quadro
 * (`crm_users/{uid}.kanbanPrefs[boardId]` — padrão notificationPrefs). Nó sem
 * nenhum filtro/lastSort ativo é deletado no servidor em vez de gravado.
 */
export const CrmUpdateKanbanPrefsInputSchema = z.object({
  boardId: z.string().min(1),
  prefs: CrmKanbanBoardPrefsSchema,
});
export type CrmUpdateKanbanPrefsInput = z.infer<typeof CrmUpdateKanbanPrefsInputSchema>;

/** Registra o token FCM do dispositivo atual para web push (§18). */
export const CrmRegisterPushTokenInputSchema = z.object({
  token: z.string().trim().min(20).max(4096),
});
export type CrmRegisterPushTokenInput = z.infer<typeof CrmRegisterPushTokenInputSchema>;

/**
 * Autor de uma nota: `USER` (escrita por uma pessoa) ou `AI` (gerada pelo
 * servidor — hoje a nota automática de mudança de fase, via Gemini). Ausência do
 * campo = nota antiga/manual = `USER`. Usado só para exibir o selo "IA" na UI.
 */
export const CrmNoteAuthorTypeSchema = z.enum(['USER', 'AI']);
export type CrmNoteAuthorType = z.infer<typeof CrmNoteAuthorTypeSchema>;

/**
 * Nota interna de um lead (`crm_boards/{b}/crm_leads/{l}/crm_notes/{id}`).
 * `mentions` são uids de membros do quadro citados via `@` no texto — o
 * servidor os notifica (`commentMentioned`). `serverCreatedAt` é Firestore
 * `Timestamp` (tipado `unknown`; o cliente ordena pelo `createdAt` em millis).
 * `authorType` distingue nota de pessoa (`USER`) da nota automática de IA (`AI`);
 * opcional para compatibilidade com notas já existentes (ausência = `USER`).
 */
export const CrmNoteSchema = z.object({
  text: z.string().min(1),
  createdBy: z.string(),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  mentions: z.array(z.string()).nullable().optional(),
  authorType: CrmNoteAuthorTypeSchema.nullable().optional(),
  serverCreatedAt: z.unknown().optional(),
});
export type CrmNote = z.infer<typeof CrmNoteSchema>;

/**
 * Cria uma nota de lead via callable (§18): o servidor grava a nota e notifica
 * os membros mencionados. `mentions` são uids resolvidos no cliente a partir do
 * typeahead de `@`; o servidor revalida que cada um é membro do quadro.
 */
export const CrmAddLeadNoteInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  text: z.string().trim().min(1).max(5000),
  mentions: z.array(z.string().min(1)).max(50).default([]),
});
export type CrmAddLeadNoteInput = z.infer<typeof CrmAddLeadNoteInputSchema>;

/**
 * Adiciona um arquivo (ADMIN ou MANAGER). `materialId` é gerado pelo cliente
 * antes do upload — o arquivo vai para `crm_training/{materialId}/{nome}`, o
 * que permite às Storage rules consultarem o doc para autorizar a leitura.
 * `visibility` NÃO entra aqui: o servidor deriva do papel do autor.
 */
export const CrmAddTrainingMaterialInputSchema = z.object({
  materialId: z.string().trim().regex(/^[A-Za-z0-9_-]{1,64}$/),
  title: z.string().trim().min(1).max(160),
  type: CrmTrainingTypeSchema,
  description: z.string().trim().max(500).nullable().optional(),
  url: z.string().trim().max(1000).nullable().optional(),
  storagePath: z.string().trim().max(500).nullable().optional(),
});
export type CrmAddTrainingMaterialInput = z.infer<typeof CrmAddTrainingMaterialInputSchema>;

/** Remove um arquivo. Só quem o subiu — nem o ADMIN remove arquivo alheio. */
export const CrmDeleteTrainingMaterialInputSchema = z.object({
  materialId: z.string().min(1),
});
export type CrmDeleteTrainingMaterialInput = z.infer<typeof CrmDeleteTrainingMaterialInputSchema>;

/** Troca o alcance de um arquivo (só ADMIN). */
export const CrmSetTrainingVisibilityInputSchema = z.object({
  materialId: z.string().min(1),
  visibility: CrmTrainingVisibilitySchema,
});
export type CrmSetTrainingVisibilityInput = z.infer<typeof CrmSetTrainingVisibilityInputSchema>;

// Rótulos por país vivem em crmDocumentSpecs.ts (tabela grande, revisada à parte).
export {
  CRM_DOCUMENT_SPECS,
  crmDocumentSpecFor,
  type CrmDocumentSpec,
} from './crmDocumentSpecs';

/** Data ISO `YYYY-MM-DD`, sem hora nem fuso. */
export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * URL de PERFIL do LinkedIn: exatamente `https://www.linkedin.com/in/<perfil>`.
 * Página de empresa, vaga ou post não serve — o campo é o perfil da pessoa.
 */
export const LINKEDIN_URL_REGEX = /^https:\/\/www\.linkedin\.com\/in\/[^/\s]+\/?$/i;

/**
 * O usuário atualiza o PRÓPRIO perfil (crm_users é server-only nas rules).
 * Todos os campos opcionais — só os presentes são gravados (patch parcial).
 */
export const CrmUpdateProfileInputSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  // Telefone NÃO entra aqui: o usuário não altera o próprio telefone — só o ADMIN
  // muda, via crmUpdateUserConfig (tela /admin/sellers). O telefone nasce no
  // convite (crmCreateInvite, informado por ADMIN/MANAGER) e é copiado no aceite.
  preferredLanguage: CrmLanguageSchema.optional(),
  timezone: z.string().trim().min(1).max(64).optional(),
  jobTitle: z.string().trim().max(120).nullable().optional(),
  photoPath: z.string().trim().max(500).nullable().optional(),
  // Só URL do LinkedIn: campo de perfil profissional, não um link qualquer.
  linkedinUrl: z
    .string()
    .trim()
    .max(300)
    .regex(LINKEDIN_URL_REGEX)
    .nullable()
    .optional(),
  country: CrmCountryCodeSchema.optional(),
  companyName: z.string().trim().max(160).nullable().optional(),
  companyTaxId: z.string().trim().max(40).nullable().optional(),
  // 34 chars é o teto da ISO 13616; 50 acomoda os espaços de agrupamento da
  // digitação. Validação semântica (país/comprimento/mod-97) fica no callable
  // (assertIban), como o companyTaxId. Até a #760 IBAN e beneficiário só se
  // editavam com o print bancário enviado; o print saiu e os dois são digitados.
  iban: z.string().trim().max(50).nullable().optional(),
  ibanHolderName: z.string().trim().max(120).nullable().optional(),
  birthDate: z.string().trim().regex(ISO_DATE_REGEX).nullable().optional(),
  // Qualificação civil digitada pelo usuário (não vem de extração de documento).
  profession: z.string().trim().max(100).nullable().optional(),
  maritalStatus: CrmMaritalStatusSchema.nullable().optional(),
  nationality: z.string().trim().max(100).nullable().optional(),
  // Identidade, cadastro fiscal e endereço: digitados no perfil desde a #760
  // (2026-09-30). Antes só a extração por IA da foto do documento os gravava, e
  // com a saída dos documentos ficariam congelados. Texto livre de propósito: o
  // formato do número varia por país e nenhum fluxo do servidor o consome.
  identityNumber: z.string().trim().max(40).nullable().optional(),
  taxId: z.string().trim().max(40).nullable().optional(),
  address: z.string().trim().max(300).nullable().optional(),
  city: z.string().trim().max(120).nullable().optional(),
  state: z.string().trim().max(120).nullable().optional(),
});
export type CrmUpdateProfileInput = z.infer<typeof CrmUpdateProfileInputSchema>;

/**
 * Atualiza o onboarding do cliente ganho (§7–§9): status + checklist completo.
 * O `checklist` mapeia item→data de marcação (millis; item ausente = desmarcado).
 * O servidor revalida o gate do status DONE (§9) e carimba `onboardingDoneAt`.
 */
export const CrmUpdateOnboardingInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  onboardingStatus: CrmOnboardingStatusSchema,
  checklist: z.record(z.string(), z.number().int().nonnegative()),
  // Itens avulsos do lead (§8). Ausente = não mexe; presente = substitui a lista.
  extraItems: z.array(CrmOnboardingExtraItemSchema).max(30).optional(),
});
export type CrmUpdateOnboardingInput = z.infer<typeof CrmUpdateOnboardingInputSchema>;

/** Substitui o template do checklist de onboarding (callable ADMIN, §7). */
export const CrmUpdateOnboardingTemplateInputSchema = z.object({
  items: z.array(CrmOnboardingTemplateItemSchema).min(1).max(100),
});
export type CrmUpdateOnboardingTemplateInput = z.infer<
  typeof CrmUpdateOnboardingTemplateInputSchema
>;

// --- Relatórios de vendas (§15) — agregados a partir do estado atual ---

export const CrmReportEntrySchema = z.object({
  userId: z.string(),
  name: z.string().nullable().optional(),
  /** Foto de perfil (Storage) — avatar na tabela de desempenho. */
  photoPath: z.string().nullable().optional(),
  won: z.number().int().nonnegative(),
  wonValue: z.number().nonnegative(),
  activeLeads: z.number().int().nonnegative(),
});
export type CrmReportEntry = z.infer<typeof CrmReportEntrySchema>;

/**
 * Filtro opcional do relatório: por equipes de gerentes (hierarquia direta)
 * e/ou por vendedores — MULTI-seleção. Servidor valida o escopo (MANAGER só a
 * própria equipe; SELLER não filtra). Tudo ausente/vazio = relatório geral do
 * papel. Os campos singulares são o formato legado (web anterior ao
 * multiselect) — o servidor normaliza tudo em listas (`normalizeReportFilter`).
 */
export const CrmSalesReportInputSchema = z.object({
  managerUserId: z.string().min(1).optional(),
  sellerUserId: z.string().min(1).optional(),
  managerUserIds: z.array(z.string().min(1)).max(100).optional(),
  sellerUserIds: z.array(z.string().min(1)).max(100).optional(),
});
export type CrmSalesReportInput = z.infer<typeof CrmSalesReportInputSchema>;

export const CrmSalesReportSchema = z.object({
  scope: CrmRoleSchema,
  totals: z.object({
    activeLeads: z.number().int().nonnegative(),
    won: z.number().int().nonnegative(),
    lost: z.number().int().nonnegative(),
  }),
  revenue: z.object({
    monthly: z.number().nonnegative(),
    annual: z.number().nonnegative(),
    ticketAverage: z.number().nonnegative(),
  }),
  bySeller: z.array(CrmReportEntrySchema),
  byManager: z.array(CrmReportEntrySchema),
  lostReasons: z.array(z.object({ code: z.string(), count: z.number().int().nonnegative() })),
  onboarding: z.object({
    inProgress: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
    wonNotImplemented: z.number().int().nonnegative(),
  }),
});
export type CrmSalesReport = z.infer<typeof CrmSalesReportSchema>;

// --- Listagem de leads por status (telas Clientes/Perdidos/Arquivados) ---

/** Uma linha de lead já enriquecida no servidor (nomes, progresso). */
export const CrmLeadRowSchema = z.object({
  boardId: z.string(),
  leadId: z.string(),
  companyName: z.string().nullable(),
  name: z.string(),
  taxId: z.string().nullable(),
  countryCode: z.string().nullable(),
  planInterest: CrmPlanInterestSchema.nullable(),
  soldPlan: CrmPlanInterestSchema.nullable(),
  soldValue: z.number().nullable(),
  /** Dono do lead + gerente DIRETO do dono (`crm_users.managerUserId`, não o
   * gerente de quadro/lead) — habilitam o filtro por vendedor/equipe. */
  ownerUserId: z.string(),
  ownerManagerUserId: z.string().nullable(),
  ownerName: z.string().nullable(),
  /** Foto de perfil do dono (Storage) — avatar da coluna de vendedor. */
  ownerPhotoPath: z.string().nullable().optional(),
  managerName: z.string().nullable(),
  /** Logo da empresa do lead (`lead.logoPath`) — avatar da coluna de lead. */
  logoPath: z.string().nullable().optional(),
  status: CrmLeadStatusSchema,
  onboardingStatus: CrmOnboardingStatusSchema.nullable(),
  onboardingProgress: z.number().int().min(0).max(100),
  onboardingDone: z.boolean(),
  nextAction: z.string().nullable(),
  nextFollowUpAt: z.number().nullable(),
  lostReasonCode: CrmLostReasonSchema.nullable(),
  lostReasonNote: z.string().nullable(),
  notQualifiedReason: CrmNotQualifiedReasonSchema.nullable(),
  duplicateOfLeadId: z.string().nullable(),
  wonAt: z.number().nullable(),
  lostAt: z.number().nullable(),
});
export type CrmLeadRow = z.infer<typeof CrmLeadRowSchema>;

export const CrmListLeadsByStatusInputSchema = z.object({
  statuses: z.array(CrmLeadStatusSchema).min(1).max(4),
});
export type CrmListLeadsByStatusInput = z.infer<typeof CrmListLeadsByStatusInputSchema>;

// --- Equipe (hierarquia direta `crm_users.managerUserId`) ---

/**
 * Membro retornado por `crmListTeam` — versão enxuta do colaborador para
 * montar a tabela de /schedule e os selects de filtro por equipe/vendedor.
 * Sem dados pessoais do perfil (identidade, endereço, IBAN): MANAGER e AUDITOR podem ler.
 */
export const CrmTeamMemberSchema = z.object({
  uid: z.string(),
  name: z.string(),
  /** Foto de perfil (Storage) — avatar nas telas de agenda/equipe. */
  photoPath: z.string().nullable().optional(),
  role: CrmRoleSchema,
  /** Gerente direto (hierarquia), null = sem gerente. */
  managerUserId: z.string().nullable(),
});
export type CrmTeamMember = z.infer<typeof CrmTeamMemberSchema>;

export const CrmListTeamResultSchema = z.object({
  members: z.array(CrmTeamMemberSchema),
});
export type CrmListTeamResult = z.infer<typeof CrmListTeamResultSchema>;

// --- Gestão de vendedores (#115) — tela admin `/admin/sellers` ---
// (CrmCommissionPercentSchema mudou para antes dos schemas de convite, que
// agora também o referenciam.)

/** Uma linha da tabela de vendedores, já enriquecida no servidor. */
export const CrmSellerRowSchema = z.object({
  uid: z.string(),
  name: z.string(),
  /** Foto de perfil (Storage) — avatar na lista de colaboradores. */
  photoPath: z.string().nullable().optional(),
  email: z.email(),
  phone: z.string().nullable(),
  country: z.string().nullable(),
  role: CrmRoleSchema,
  status: CrmUserStatusSchema,
  /**
   * #786: parceiro do usuário (papel PARTNER); `null` no time interno. Usado
   * pelo diálogo de exclusão de parceiro para listar os usuários dele.
   * Opcional porque a web não valida a resposta e um bundle antigo do
   * servidor não o manda.
   */
  partnerId: z.string().nullable().optional(),
  /**
   * #787 — nome, status e país do parceiro do usuário PARTNER, resolvidos no
   * servidor (`buildSellerRows`, `getAll` em `crm_partners`); `null` no time
   * interno ou se o doc do parceiro sumiu/quebrou. Alimentam a coluna e o
   * filtro Parceiro da lista e o modo parceiro do editor (país travado).
   * Opcionais pelo mesmo motivo do `partnerId`: bundle antigo do servidor não
   * os manda.
   */
  partnerName: z.string().nullable().optional(),
  partnerStatus: CrmPartnerStatusSchema.nullable().optional(),
  partnerCountry: z.string().nullable().optional(),
  // Os dois percentuais do colaborador. A tela edita o de vendedor para todos e o
  // de gerente só para quem não é SELLER (um SELLER nunca é gerente de ninguém).
  sellerCommissionPercent: z.number(),
  managerCommissionPercent: z.number(),
  // #748 — opcionais: a web não valida a resposta das callables, e durante a
  // janela de deploy (web nova, functions antigas) os campos chegam ausentes.
  // A tela cai no padrão em vez de travar o Salvar.
  // Origem: #753 — 2026-09-27 12:23 +0200. Auditoria 2026-09-27 12:34 +0200
  // (#748, A1/A4, BAIXO): obrigatórios, travavam o Salvar da tela do
  // colaborador na janela de deploy. Não voltar a obrigatórios.
  /** Prazo da comissão em meses; `null` = indeterminado (#780). Ausente = Functions antigas. */
  commissionWindowMonths: z.number().int().nullable().optional(),
  /** Carteira Asaas que recebe o split; `null`/ausente = extrato a pagar. */
  asaasWalletId: z.string().nullable().optional(),
  // Gerente direto do colaborador. `managerName` é resolvido no servidor (nome do
  // doc do gerente); null se sem gerente ou se o doc do gerente sumiu/quebrou.
  // #787: no usuário PARTNER é o gerente do parceiro, copiado para o
  // `crm_users.managerUserId` dele no aceite e propagado por `crmUpsertPartner`.
  managerUserId: z.string().nullable(),
  managerName: z.string().nullable(),
  lastLoginAt: z.number().nullable(),
  // Quando o colaborador foi removido (soft-delete); null para quem não está
  // REMOVED. Default null porque a resposta do callable não passa por Zod no
  // cliente, e na janela em que a web sobe antes das Functions o campo chega
  // `undefined`.
  removedAt: z.number().nullable().default(null),
  // Leads da carteira (`ownerUserId == uid`) em qualquer quadro, fora os
  // ARCHIVED — coluna "Leads" da lista. `null` = contagem indisponível (a
  // agregação falhou, ou a web subiu antes das Functions e o campo não veio);
  // a tela mostra travessão em vez de um zero que seria mentira.
  leadsCount: z.number().int().nonnegative().nullable().default(null),
});
export type CrmSellerRow = z.infer<typeof CrmSellerRowSchema>;

export const CrmListUsersInputSchema = z.object({
  /** Página seguinte: uid do último item da página anterior. */
  cursor: z.string().min(1).nullable().optional(),
  limit: z.number().int().min(1).max(200).default(100),
  /**
   * Inclui os colaboradores REMOVED na resposta. Default `false`: quem foi
   * removido some da lista. O toggle "Mostrar removidos" da `SellersPage` é o
   * único lugar que manda `true`.
   */
  showRemoved: z.boolean().default(false),
  /**
   * #787 (auditoria 2026-09-30, UI M4): só os usuários deste parceiro. Usado
   * pela aba Usuários do editor de parceiro, que antes paginava o
   * `crm_users` inteiro (com a contagem de carteira de cada um) para mostrar
   * dois ou três usuários. Igualdade + `orderBy(documentId())`: índice
   * single-field automático, sem composto.
   */
  partnerId: z.string().min(1).optional(),
});
export type CrmListUsersInput = z.infer<typeof CrmListUsersInputSchema>;

export const CrmListUsersResultSchema = z.object({
  users: z.array(CrmSellerRowSchema),
  nextCursor: z.string().nullable(),
});
export type CrmListUsersResult = z.infer<typeof CrmListUsersResultSchema>;

/**
 * Patch parcial da config de um vendedor. Só campos presentes são gravados. O
 * servidor rejeita auto-rebaixamento e auto-desativação (evita lockout do CRM).
 */
export const CrmUpdateUserConfigInputSchema = z.object({
  uid: z.string().min(1),
  // Os dois percentuais são independentes: `undefined` = não mexe. A tela só envia
  // o que está visível para o papel — o campo oculto não sobrescreve o gravado.
  sellerCommissionPercent: CrmCommissionPercentSchema.optional(),
  managerCommissionPercent: CrmCommissionPercentSchema.optional(),
  // #748 — `undefined` = não mexe. A carteira é congelada em cada venda; o
  // prazo, desde a #780, vale também para as vendas já fechadas (`null` =
  // tempo indeterminado).
  // Origem: #753 — 2026-09-27 12:23 +0200 (cadastro do colaborador). Usado
  // por: CRM `SellerConfigPage.tsx` (só envia quando muda) e a callable de
  // configuração do usuário (`apps/crm-functions/src/crmSellers.ts`).
  commissionWindowMonths: CommissionWindowSchema.optional(),
  // #748 — carteira Asaas do split. `null` = limpar (a comissão volta para o
  // extrato a pagar). Decide para onde vai dinheiro: só ADMIN, com audit.
  // Aqui só o FORMATO (UUID) é conferido; que a carteira exista no Asaas só
  // uma cobrança-sonda prova (docs/billing/asaas.md, "Carteira do split").
  asaasWalletId: AsaasWalletIdSchema.nullable().optional(),
  role: CrmRoleSchema.optional(),
  status: CrmUserStatusSchema.optional(),
  // Telefone do colaborador: só o ADMIN altera (o usuário não mexe no próprio).
  // `undefined` = não mexe; `null` = limpar; string = definir (E.164 validado).
  phone: crmPhoneField.nullable().optional(),
  // `undefined` = não mexe; `null` = limpar o país (colaborador sem país); string = definir.
  country: CrmCountryCodeSchema.nullable().optional(),
  // Gerente direto do colaborador. `undefined` = não mexe; `null` = limpar
  // gerente; string = atribuir (o servidor valida MANAGER ativo, ≠ o próprio uid).
  managerUserId: z.string().min(1).nullable().optional(),
  /**
   * #788 — destino da EQUIPE quando este patch rebaixa ou desativa um
   * MANAGER: uid de outro MANAGER ativo, ou `null` = "Sem gerente".
   * `undefined` = não informado; com equipe, o servidor recusa com
   * `MANAGER_HAS_TEAM`. Equipe = parceiros, usuários de parceiro, vendedores,
   * quadros e leads que o apontam como gerente.
   */
  teamTransferTo: z.string().min(1).nullable().optional(),
});
export type CrmUpdateUserConfigInput = z.infer<typeof CrmUpdateUserConfigInputSchema>;

/**
 * Remoção de colaborador (soft-delete) — `crmRemoveCollaborator`.
 *
 * O doc `crm_users/{uid}` NUNCA é apagado e a conta do Firebase Auth permanece:
 * o que muda é `status` → `REMOVED`. Não confundir com `crmRemoveBoardMember`,
 * que tira do QUADRO; aqui a pessoa sai do CRM inteiro.
 *
 * Destino da carteira, exclusivos entre si (mesma semântica do
 * `CrmRemoveBoardMemberInputSchema`):
 * - `transferToUserId`: todos os leads dele, em todos os quadros, migram;
 * - `archiveLeads`: "remover tudo" via soft delete (status=ARCHIVED).
 *
 * `confirmEmail` é o e-mail do removido digitado pelo ADMIN. Diferente do
 * `crmRemoveBoardMember` (onde a confirmação é só de UI), aqui ele vai no
 * payload e é REVALIDADO no servidor: é a única ação do CRM que esvazia a
 * carteira de alguém em todos os quadros de uma vez.
 */
export const CrmRemoveCollaboratorInputSchema = z.object({
  uid: z.string().min(1),
  transferToUserId: z.string().min(1).nullable().optional(),
  archiveLeads: z.boolean().default(false),
  confirmEmail: z.email(),
});
export type CrmRemoveCollaboratorInput = z.infer<typeof CrmRemoveCollaboratorInputSchema>;

export const CrmRemoveCollaboratorResultSchema = z.object({
  ok: z.literal(true),
  migratedLeads: z.number().int().nonnegative(),
  archivedLeads: z.number().int().nonnegative(),
  removedMemberships: z.number().int().nonnegative(),
  /** Subordinados que ficaram sem gerente (managerUserId → null). */
  clearedManagerOf: z.number().int().nonnegative(),
  cancelledInvites: z.number().int().nonnegative(),
  archivedBoards: z.number().int().nonnegative(),
  /**
   * Leads do funil PESSOAL do removido que mudaram para o funil pessoal do
   * destino (mesmo ID, mesma fase). Subconjunto de `migratedLeads`.
   */
  movedLeads: z.number().int().nonnegative(),
  /**
   * Leads do funil pessoal que NÃO puderam mudar de quadro (cliente vinculado a
   * pagamento ou venda em andamento): trocaram de dono e ficaram onde estavam.
   */
  keptLinkedLeads: z.number().int().nonnegative(),
  /** Colunas criadas no funil do destino para manter a fase de cada lead. */
  createdColumns: z.number().int().nonnegative(),
  /** Parcelas de comissão PENDING do removido anuladas (`COLLABORATOR_REMOVED`). */
  cancelledCommissions: z.number().int().nonnegative(),
  /** Vendas em que o removido foi revogado (`revokedBeneficiaryUserIds`). */
  revokedSales: z.number().int().nonnegative(),
});
export type CrmRemoveCollaboratorResult = z.infer<typeof CrmRemoveCollaboratorResultSchema>;

/**
 * `crmRestoreCollaborator`: desfaz o status, não a operação. O colaborador
 * volta como `DISABLED` — o ADMIN reativa depois, conscientemente — e NÃO
 * recupera carteira, membership de quadro nem os subordinados.
 */
export const CrmRestoreCollaboratorInputSchema = z.object({
  uid: z.string().min(1),
});
export type CrmRestoreCollaboratorInput = z.infer<typeof CrmRestoreCollaboratorInputSchema>;

// ---------------------------------------------------------------------------
// Metas globais (crm_goals) + Home "Meu dia" (crmHomeDigest)
// ---------------------------------------------------------------------------

/**
 * Métricas de meta. Volume (contagem) exceto SOLD_VALUE (soma, em CENTS). O
 * progresso é medido por um timestamp de marco no lead (ver crmHomeDigest /
 * lib/goals.ts): createdAt (NEW_LEADS), wonAt (WON/SOLD_VALUE), onboardingDoneAt
 * (ONBOARDING_DONE), stageReachedAt.* (PROPOSALS_SENT/DEMOS_DONE/NEGOTIATIONS).
 */
// Ordem = funil de vendas (é a ordem em que a tela /goals renderiza as linhas,
// via CrmGoalMetricSchema.options.map em crmGoals.ts).
export const CrmGoalMetricSchema = z.enum([
  'NEW_LEADS', // Leads novos
  'PROPOSALS_SENT', // Propostas enviadas
  'DEMOS_DONE', // Demonstrações realizadas
  'NEGOTIATIONS', // Negociações
  'WON', // Fechamentos
  'ONBOARDING_DONE', // Onboardings concluídos
  'SOLD_VALUE', // Valor vendido
]);
export type CrmGoalMetric = z.infer<typeof CrmGoalMetricSchema>;

/** Janela da meta (fuso America/Sao_Paulo). BIWEEKLY = dias 1–15 / 16–fim. */
export const CrmGoalPeriodSchema = z.enum(['DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY']);
export type CrmGoalPeriod = z.infer<typeof CrmGoalPeriodSchema>;

/**
 * `crm_goals/{metric}_{period}` — meta GLOBAL da equipe (um único alvo por
 * métrica+período). O modelo VIGENTE é semanal (WEEKLY): o alvo vale
 * INDIVIDUALMENTE para cada colaborador medido (SELLER/MANAGER ativo). Ex.: alvo
 * NEW_LEADS=100/semana → cada colaborador é medido contra 100; a meta da equipe
 * é `100 × nº de colaboradores` e o progresso da equipe é a soma dos membros.
 * O schema segue aceitando os 4 períodos para parsear docs antigos; docs
 * `{metric}_MONTHLY` legados ficam órfãos e são ignorados na leitura.
 */
export const CrmGoalSchema = z.object({
  metric: CrmGoalMetricSchema,
  period: CrmGoalPeriodSchema,
  // Alvo INDIVIDUAL por colaborador na janela. Contagem para as métricas de
  // volume; inteiro em CENTS para SOLD_VALUE (regra dinheiro-em-cents).
  target: z.number().int().nonnegative(),
  active: z.boolean().default(true),
  createdBy: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmGoal = z.infer<typeof CrmGoalSchema>;

export const CrmUpsertGoalInputSchema = z.object({
  metric: CrmGoalMetricSchema,
  period: CrmGoalPeriodSchema,
  // Alvo individual por colaborador na janela (cents para SOLD_VALUE).
  target: z.number().int().nonnegative().max(1_000_000_000),
  active: z.boolean().default(true),
});
export type CrmUpsertGoalInput = z.infer<typeof CrmUpsertGoalInputSchema>;

export const CrmDeleteGoalInputSchema = z.object({ goalId: z.string().min(1) });
export type CrmDeleteGoalInput = z.infer<typeof CrmDeleteGoalInputSchema>;

export const CrmListGoalsInputSchema = z.object({});
export type CrmListGoalsInput = z.infer<typeof CrmListGoalsInputSchema>;

/** Meta global já com o progresso do período atual calculado no servidor. */
export const CrmGoalProgressSchema = z.object({
  goalId: z.string(),
  metric: CrmGoalMetricSchema,
  period: CrmGoalPeriodSchema,
  target: z.number().int().nonnegative(),
  current: z.number().nonnegative(),
  windowStart: z.number().int(),
  windowEnd: z.number().int(),
});
export type CrmGoalProgress = z.infer<typeof CrmGoalProgressSchema>;

export const CrmListGoalsResultSchema = z.object({
  goals: z.array(CrmGoalProgressSchema),
  // Colaboradores medidos (SELLER/MANAGER ativos). A grade calcula a meta da
  // equipe = `target × memberCount` e o progresso da empresa contra ela.
  memberCount: z.number().int().nonnegative(),
});
export type CrmListGoalsResult = z.infer<typeof CrmListGoalsResultSchema>;

// --- Quadro de metas de /performance (crmGoalsBoard) ---------------------
// Setup semanal, alvo individual por colaborador. SELLER vê o próprio progresso;
// MANAGER o agregado da equipe + por membro; ADMIN/AUDITOR por equipes.

/** Progresso numa métrica: `target` é o alvo da linha (individual, ou já
 *  multiplicado por membros num agregado de equipe); `current` o realizado. */
export const CrmGoalMetricProgressSchema = z.object({
  metric: CrmGoalMetricSchema,
  target: z.number().int().nonnegative(),
  current: z.number().nonnegative(),
});
export type CrmGoalMetricProgress = z.infer<typeof CrmGoalMetricProgressSchema>;

/** Progresso de UM colaborador em todas as metas ativas (target = alvo individual). */
export const CrmGoalMemberProgressSchema = z.object({
  uid: z.string(),
  name: z.string(),
  role: CrmRoleSchema,
  metrics: z.array(CrmGoalMetricProgressSchema),
});
export type CrmGoalMemberProgress = z.infer<typeof CrmGoalMemberProgressSchema>;

/**
 * Progresso agregado de uma EQUIPE (gerente + subordinados diretos). `metrics`
 * traz `target = alvo individual × memberCount` e `current = soma dos membros`.
 * `managerUserId = null` é a pseudo-equipe "Sem gerente" (vendedores sem gerente).
 */
export const CrmGoalTeamProgressSchema = z.object({
  managerUserId: z.string().nullable(),
  managerName: z.string().nullable(),
  memberCount: z.number().int().nonnegative(),
  metrics: z.array(CrmGoalMetricProgressSchema),
  members: z.array(CrmGoalMemberProgressSchema),
});
export type CrmGoalTeamProgress = z.infer<typeof CrmGoalTeamProgressSchema>;

/** Filtro do quadro — mesmo contrato do relatório de vendas (multi-seleção). */
export const CrmGoalsBoardInputSchema = z.object({
  managerUserIds: z.array(z.string().min(1)).max(50).optional(),
  sellerUserIds: z.array(z.string().min(1)).max(50).optional(),
});
export type CrmGoalsBoardInput = z.infer<typeof CrmGoalsBoardInputSchema>;

/**
 * Quadro de metas por papel. `goals` lista os alvos individuais ativos (para
 * cabeçalhos). ADMIN/AUDITOR recebem `teams` (todas as equipes); MANAGER recebe
 * `teams` com a sua; SELLER recebe `self` (o próprio) e `teams: []`.
 */
export const CrmGoalsBoardResultSchema = z.object({
  scope: CrmRoleSchema,
  windowStart: z.number().int(),
  windowEnd: z.number().int(),
  goals: z.array(z.object({ metric: CrmGoalMetricSchema, target: z.number().int().nonnegative() })),
  teams: z.array(CrmGoalTeamProgressSchema),
  self: CrmGoalMemberProgressSchema.nullable(),
});
export type CrmGoalsBoardResult = z.infer<typeof CrmGoalsBoardResultSchema>;

// ---------------------------------------------------------------------------
// Atividade diária por vendedor (crm_daily_activity) — 12 indicadores/dia
// ---------------------------------------------------------------------------
// Acompanhamento diário do esforço de cada vendedor. 12 indicadores, divididos
// em dois grupos:
//   • 5 CONTADORES manuais persistidos em `crm_daily_activity/{uid}_{dayKey}`:
//     ligações, WhatsApp, e-mails, respostas e follow-ups. Não deixam rastro
//     natural (os botões de contato do lead são só deep-links tel:/wa.me/mailto:),
//     então são incrementados via `crmLogActivity` — automático no clique +
//     ajuste manual. NÃO é timeline por lead (issue #109): é agregado por
//     vendedor/dia.
//   • 7 DERIVADOS on-demand dos carimbos do lead + appointments (nunca gravados,
//     para não duplicar fonte da verdade): leads novos (createdAt), reuniões
//     agendadas (crm_appointments.createdAt), demos (stageReachedAt.demoDone),
//     propostas (stageReachedAt.proposal), negociações iniciadas
//     (stageReachedAt.negotiation), vendas (wonAt), onboardings (onboardingDoneAt).
// `dayKey` = "YYYY-MM-DD" no fuso America/Sao_Paulo (mesma convenção das metas,
// lib/dailyActivity.ts) — dias comparáveis entre vendedores e consistentes com
// crm_goals. Escrita só via callable (rules em default-deny).

/** Contador manual de atividade (canal de contato / follow-up). */
export const CrmDailyCounterSchema = z.enum(['CALLS', 'WHATSAPP', 'EMAILS', 'REPLIES', 'FOLLOW_UPS']);
export type CrmDailyCounter = z.infer<typeof CrmDailyCounterSchema>;

/** Chave de dia local (São Paulo), formato "YYYY-MM-DD". */
export const CrmDayKeySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Os 5 contadores manuais de um dia (default 0 — doc pode não existir ainda). */
export const CrmDailyActivityCountersSchema = z.object({
  calls: z.number().int().nonnegative().default(0),
  whatsapp: z.number().int().nonnegative().default(0),
  emails: z.number().int().nonnegative().default(0),
  replies: z.number().int().nonnegative().default(0),
  followUps: z.number().int().nonnegative().default(0),
});
export type CrmDailyActivityCounters = z.infer<typeof CrmDailyActivityCountersSchema>;

// --- Presença medida (heartbeat) ---------------------------------------------
// O MESMO doc guarda, além dos contadores de esforço, os SLOTS de 5 min em que o
// colaborador esteve ativo no CRM (`activeSlots`). 288 slots cobrem o dia local
// SP; o servidor calcula o índice (o cliente nunca escolhe quando esteve ativo) e
// grava com `FieldValue.arrayUnion` — idempotente, sem transação e sem conflito
// entre abas. `firstSeenAt`/`lastSeenAt` NÃO são campos: derivam de min/max dos
// slots. Gravá-los exigiria semântica "min-only"/"max-only" que o Firestore não
// tem — com `set(merge)` last-write-wins e várias abas, `lastSeenAt` andaria para
// trás. Derivar é monotônico por construção.

/** Slot de 5 min do dia local SP: 288 por dia (0 = 00:00–00:05, 287 = 23:55–24:00). */
export const CrmActivitySlotSchema = z.number().int().min(0).max(287);

/** `crm_daily_activity/{uid}_{dayKey}` — contadores manuais + presença de um vendedor/dia. */
export const CrmDailyActivityDocSchema = CrmDailyActivityCountersSchema.extend({
  uid: z.string().min(1),
  dayKey: CrmDayKeySchema,
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  activeSlots: z.array(CrmActivitySlotSchema).default([]),
});
export type CrmDailyActivityDoc = z.infer<typeof CrmDailyActivityDocSchema>;

/** Parse TOLERANTE só da presença, para ler docs legados (sem o campo) ou com
 *  lixo sem derrubar o parse dos contadores. A faixa 0..287 é reimposta na
 *  derivação (`normalizeSlots`), então aqui basta garantir "array de números". */
export const CrmDailyActivityPresenceSchema = z.object({
  activeSlots: z.array(z.number()).catch([]).default([]),
});

/** Input do `crmHeartbeat` — VAZIO de propósito: dia e slot saem do relógio do
 *  SERVIDOR. O cliente sinaliza "estou ativo agora", nunca "estive ativo às X". */
export const CrmHeartbeatInputSchema = z.object({});
export type CrmHeartbeatInput = z.infer<typeof CrmHeartbeatInputSchema>;

/** Resultado do `crmHeartbeat`. `recorded: false` = papel não medido
 *  (ADMIN/AUDITOR) — no-op silencioso, sem linha no painel. */
export const CrmHeartbeatResultSchema = z.object({
  ok: z.literal(true),
  dayKey: CrmDayKeySchema,
  slot: CrmActivitySlotSchema.nullable(),
  recorded: z.boolean(),
});
export type CrmHeartbeatResult = z.infer<typeof CrmHeartbeatResultSchema>;

/** Input do `crmLogActivity` — incrementa/decrementa 1 contador do dia de HOJE
 *  (o dayKey é calculado no servidor; o cliente não escolhe o dia). */
export const CrmLogActivityInputSchema = z.object({
  counter: CrmDailyCounterSchema,
  delta: z.union([z.literal(1), z.literal(-1)]).default(1),
});
export type CrmLogActivityInput = z.infer<typeof CrmLogActivityInputSchema>;

/** Resultado do `crmLogActivity` — o novo valor do contador (piso 0). */
export const CrmLogActivityResultSchema = z.object({
  ok: z.literal(true),
  dayKey: CrmDayKeySchema,
  counter: CrmDailyCounterSchema,
  value: z.number().int().nonnegative(),
});
export type CrmLogActivityResult = z.infer<typeof CrmLogActivityResultSchema>;

/** Os 7 indicadores derivados de leads/appointments num dia. */
export const CrmDailyDerivedSchema = z.object({
  newLeads: z.number().int().nonnegative(),
  meetingsScheduled: z.number().int().nonnegative(),
  demosDone: z.number().int().nonnegative(),
  proposalsSent: z.number().int().nonnegative(),
  negotiations: z.number().int().nonnegative(),
  won: z.number().int().nonnegative(),
  onboardingDone: z.number().int().nonnegative(),
});
export type CrmDailyDerived = z.infer<typeof CrmDailyDerivedSchema>;

/** Presença medida no dia. `firstSeenAt`/`lastSeenAt` são epoch ms (o cliente
 *  formata no fuso SP — o painel é SP-fixo); `hourly[h]` = quantos dos 12 slots
 *  daquela hora tiveram atividade, e alimenta a mini-barra de 24h. */
export const CrmDailyTimeSchema = z.object({
  activeMinutes: z.number().int().nonnegative(),
  firstSeenAt: z.number().int().nullable(),
  lastSeenAt: z.number().int().nullable(),
  longestIdleMs: z.number().int().nonnegative(),
  hourly: z.array(z.number().int().min(0).max(12)).length(24),
});
export type CrmDailyTime = z.infer<typeof CrmDailyTimeSchema>;

/** Disciplina de carteira do vendedor NO INSTANTE da consulta (não é do dia:
 *  "parado há 7 dias" é estado atual, não evento daquele dia). */
export const CrmDailyPipelineSchema = z.object({
  staleLeads: z.number().int().nonnegative(),
  overdueFollowUps: z.number().int().nonnegative(),
});
export type CrmDailyPipeline = z.infer<typeof CrmDailyPipelineSchema>;

/** Assiduidade na semana. `businessDays` é o denominador (seg–sex; feriados NÃO
 *  são modelados — num feriado nacional todos pontuam um dia sem acesso). */
export const CrmDailyWeeklySchema = z.object({
  daysWithoutAccess: z.number().int().min(0).max(7),
  businessDays: z.number().int().min(0).max(7),
});
export type CrmDailyWeekly = z.infer<typeof CrmDailyWeeklySchema>;

/**
 * Uma linha do relatório de atividade diária: um vendedor no dia escolhido.
 *
 * `time`/`pipeline`/`weekly` são NULL — e não ausentes — quando o requisitante
 * não pode ver métricas de tempo (SELLER, inclusive sobre si mesmo). Nullable em
 * vez de optional porque `exactOptionalPropertyTypes` está ligado, `null` obriga
 * a UI a tratar o ramo explicitamente, e o formato do wire não muda com o papel.
 */
export const CrmDailyActivityRowSchema = z.object({
  uid: z.string(),
  name: z.string().nullable(),
  photoPath: z.string().nullable(),
  counters: CrmDailyActivityCountersSchema,
  derived: CrmDailyDerivedSchema,
  time: CrmDailyTimeSchema.nullable(),
  pipeline: CrmDailyPipelineSchema.nullable(),
  weekly: CrmDailyWeeklySchema.nullable(),
});
export type CrmDailyActivityRow = z.infer<typeof CrmDailyActivityRowSchema>;

/** Filtro do relatório — mesmo contrato do quadro de metas (multi-seleção) +
 *  `dayKey` opcional (default hoje SP). */
export const CrmDailyActivityReportInputSchema = z.object({
  dayKey: CrmDayKeySchema.optional(),
  managerUserIds: z.array(z.string().min(1)).max(50).optional(),
  sellerUserIds: z.array(z.string().min(1)).max(50).optional(),
});
export type CrmDailyActivityReportInput = z.infer<typeof CrmDailyActivityReportInputSchema>;

/** Relatório de atividade diária, escopado por papel (uma linha por vendedor). */
export const CrmDailyActivityReportResultSchema = z.object({
  scope: CrmRoleSchema,
  dayKey: CrmDayKeySchema,
  dayStart: z.number().int(),
  dayEnd: z.number().int(),
  rows: z.array(CrmDailyActivityRowSchema),
});
export type CrmDailyActivityReportResult = z.infer<typeof CrmDailyActivityReportResultSchema>;

// ---------------------------------------------------------------------------
// Minha performance (/my-performance, SELLER) — visão motivacional pessoal
// ---------------------------------------------------------------------------
// Duas callables independentes: `crmMyPerformance` (números, rápido) e
// `crmPerformanceAdvice` (análise IA, cacheada). A tela dispara ambas em
// paralelo; a IA nunca bloqueia os números. Metas do vendedor vêm do
// `crmGoalsBoard` (reuso do quadro), não daqui.

export const CrmMyPerformanceInputSchema = z.object({});
export type CrmMyPerformanceInput = z.infer<typeof CrmMyPerformanceInputSchema>;

/**
 * Uma venda ganha do vendedor (para celebrar) — link para o card + valor. O
 * `value` está nas MESMAS unidades do relatório de vendas (soldValue/estimatedValue
 * do lead, número simples da Fase 1; formatar com formatCurrency, sem /100).
 */
export const CrmMyWinSchema = z.object({
  boardId: z.string(),
  leadId: z.string(),
  name: z.string(),
  companyName: z.string().nullable(),
  value: z.number().nonnegative(),
  wonAt: z.number().int().nullable(),
});
export type CrmMyWin = z.infer<typeof CrmMyWinSchema>;

export const CrmMyPerformanceResultSchema = z.object({
  totals: z.object({
    activeLeads: z.number().int().nonnegative(),
    won: z.number().int().nonnegative(),
    lost: z.number().int().nonnegative(),
  }),
  wonValue: z.number().nonnegative(),
  ticketAverage: z.number().nonnegative(),
  revenueMonthly: z.number().nonnegative(),
  revenueAnnual: z.number().nonnegative(),
  // Ganhos dentro das janelas vigentes (fuso São Paulo). A semana usa a MESMA
  // janela WEEKLY das metas (goalWindow), para o hero bater com o quadro de metas.
  wonThisWeek: z.number().int().nonnegative(),
  wonThisMonth: z.number().int().nonnegative(),
  recentWins: z.array(CrmMyWinSchema).max(5),
  bestWin: CrmMyWinSchema.nullable(),
  lostReasons: z.array(z.object({ code: z.string(), count: z.number().int().nonnegative() })),
  onboarding: z.object({
    inProgress: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
    wonNotImplemented: z.number().int().nonnegative(),
  }),
});
export type CrmMyPerformanceResult = z.infer<typeof CrmMyPerformanceResultSchema>;

export const CrmPerformanceAdviceInputSchema = z.object({
  // `true` ignora o cache e regenera (botão "atualizar análise").
  refresh: z.boolean().default(false),
});
export type CrmPerformanceAdviceInput = z.infer<typeof CrmPerformanceAdviceInputSchema>;

/**
 * Análise motivacional estruturada (coach de vendas). Campos em português como
 * o assistente do card (CrmLeadAdviceSchema). `dicas` vem priorizada (a 1ª é a
 * de maior impacto na meta com maior lacuna).
 */
export const CrmPerformanceAdviceSchema = z.object({
  resumo: z.string().trim().max(600),
  pontosFortes: z.array(z.string().trim().min(1).max(300)).max(3),
  dicas: z
    .array(
      z.object({
        titulo: z.string().trim().min(1).max(120),
        comoFazer: z.string().trim().min(1).max(400),
      }),
    )
    .max(4),
  focoSemana: z.string().trim().max(300),
});
export type CrmPerformanceAdvice = z.infer<typeof CrmPerformanceAdviceSchema>;

/** `advice: null` = LLM indisponível ou vendedor sem dados — a tela degrada em
 *  silêncio (mostra os números normalmente, sem alerta de erro). */
export const CrmPerformanceAdviceResultSchema = z.object({
  advice: CrmPerformanceAdviceSchema.nullable(),
  cached: z.boolean(),
  signature: z.string(),
});
export type CrmPerformanceAdviceResult = z.infer<typeof CrmPerformanceAdviceResultSchema>;

/** Uma proposta parada (sem resposta) na Home "Meu dia". */
export const CrmStalledProposalSchema = z.object({
  boardId: z.string(),
  leadId: z.string(),
  name: z.string(),
  companyName: z.string().nullable(),
  ownerUserId: z.string(),
  ownerName: z.string().nullable(),
  estimatedValue: z.number().nullable(),
  columnEnteredAt: z.number().int(),
  stalledDays: z.number().int().nonnegative(),
});
export type CrmStalledProposal = z.infer<typeof CrmStalledProposalSchema>;

/**
 * Payload da Home operacional (`crmHomeDigest`). As metas saíram da Home (vivem
 * no quadro de `/performance`, `crmGoalsBoard`); o digest ainda usa o progresso
 * semanal do ator internamente só para ancorar as dicas. `tips` é null quando o
 * LLM está indisponível — a Home renderiza sem dicas, nunca quebra. `tips.byLead`
 * mapeia leadId → dica curta da proposta parada.
 */
export const CrmHomeDigestResultSchema = z.object({
  stalledProposals: z.array(CrmStalledProposalSchema),
  tips: z
    .object({
      general: z.string().nullable(),
      byLead: z.record(z.string(), z.string()),
    })
    .nullable(),
});
export type CrmHomeDigestResult = z.infer<typeof CrmHomeDigestResultSchema>;

// Output do gate de acesso (verifyAccess).
export const CrmAccessResultSchema = z.object({
  ok: z.boolean(),
  uid: z.string(),
  role: CrmRoleSchema,
  status: CrmUserStatusSchema,
});
export type CrmAccessResult = z.infer<typeof CrmAccessResultSchema>;

// --- Demonstração: acesso demo para leads (telas /demos e /admin/demo-tenants) ---

/**
 * Línguas dos tenants de demonstração. Cobre os 4 locales do app principal
 * (EtiquetaChef) — pt/en/es/it —, diferente de `CrmLanguageSchema` (só pt/en,
 * os locales do próprio CRM): o tenant demo vive no app principal e o usuário
 * demo entra na língua escolhida.
 */
export const CrmDemoLanguageSchema = z.enum(['pt', 'en', 'es', 'it']);
export type CrmDemoLanguage = z.infer<typeof CrmDemoLanguageSchema>;

/**
 * `crm_demo_tenants/{language}` — 1 tenant de demonstração por língua (doc id =
 * língua). Aponta para um tenant do app principal. Escrita só via callable
 * ADMIN; usuário CRM ativo lê (o form de nova demo e a tela admin precisam das
 * línguas configuradas).
 */
export const CrmDemoTenantSchema = z.object({
  language: CrmDemoLanguageSchema,
  tenantId: z.string().min(1),
  /** Snapshot do nome do tenant no cadastro (o cliente CRM não lê `/tenants`
   * do app principal — as rules de lá não o liberam a usuários CRM). */
  tenantName: z.string().nullable().optional(),
  createdByUserId: z.string(),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
});
export type CrmDemoTenant = z.infer<typeof CrmDemoTenantSchema>;

/**
 * `crm_demo_accesses/{autoId}` — um acesso de demonstração concedido a um lead
 * OU ao próprio usuário do CRM (auto-acesso, `isSelf: true`).
 * `ownerUserId` é o uid de quem criou (base do escopo por papel + filtro por
 * equipe). Sem histórico de senha: a senha nunca é persistida.
 */
export const CrmDemoAccessSchema = z.object({
  /** Nulos no auto-acesso (não há lead). Ausentes só em teoria — docs de lead
   *  sempre os têm; docs antigos (pré auto-acesso) também. */
  boardId: z.string().nullable().optional(),
  leadId: z.string().nullable().optional(),
  /** No auto-acesso, nome/e-mail do PRÓPRIO usuário do CRM (a tabela reusa as colunas). */
  leadName: z.string(),
  leadEmail: z.string(),
  /** Auto-acesso do usuário do CRM ao tenant demo. Ausente = demo de lead
   *  (docs criados antes desta feature não têm o campo). */
  isSelf: z.boolean().optional(),
  language: CrmDemoLanguageSchema,
  tenantId: z.string(),
  authUid: z.string(),
  /** A conta Firebase Auth foi criada agora (true) ou já existia e foi reusada. */
  accountCreated: z.boolean(),
  /** A conta já era membro do tenant demo antes desta concessão. */
  memberAlreadyExisted: z.boolean(),
  ownerUserId: z.string(),
  createdByUserId: z.string(),
  createdAt: z.number().int().nonnegative(),
  /** O e-mail de convite ao lead foi enviado na concessão (best-effort).
   *  Ausente em docs criados antes desta feature. */
  emailSent: z.boolean().optional(),
});
export type CrmDemoAccess = z.infer<typeof CrmDemoAccessSchema>;

/**
 * Linha já enriquecida no servidor para a tabela de /demos. `ownerManagerUserId`
 * é o gerente DIRETO do dono (`crm_users.managerUserId`) — contrato do filtro
 * por equipe, igual a `CrmLeadRow`.
 */
export const CrmDemoAccessRowSchema = z.object({
  id: z.string(),
  /** Nulos quando a row é um auto-acesso (`isSelf`) — sem link para lead. */
  boardId: z.string().nullable(),
  leadId: z.string().nullable(),
  leadName: z.string(),
  leadEmail: z.string(),
  /** Auto-acesso do usuário do CRM (computado no servidor; docs antigos = false). */
  isSelf: z.boolean(),
  language: CrmDemoLanguageSchema,
  tenantId: z.string(),
  accountCreated: z.boolean(),
  ownerUserId: z.string(),
  ownerManagerUserId: z.string().nullable(),
  ownerName: z.string().nullable(),
  createdAt: z.number().int().nonnegative(),
  /**
   * Último acesso do prospect ao app EtiquetaChef com a conta da demo. NÃO é um
   * campo persistido: o servidor deriva de `users/{authUid}/devices`
   * (`reportSignInDevice`, issue #214), que só o app cliente (web + Android)
   * escreve — o CRM nunca. Conta apenas o que aconteceu DEPOIS de `createdAt`,
   * porque uma conta reusada (`accountCreated: false`) pode ter dispositivos
   * anteriores à concessão. `null` = nunca acessou.
   */
  lastAccessAt: z.number().int().nonnegative().nullable(),
  /** Plataforma do último acesso. `null` quando não houve acesso. */
  lastAccessPlatform: z.enum(['android', 'web', 'ios']).nullable(),
  /** O e-mail de convite saiu na concessão (best-effort). `null` em docs
   *  criados antes de o campo existir — "não sei", não "não enviou". */
  emailSent: z.boolean().nullable(),
});
export type CrmDemoAccessRow = z.infer<typeof CrmDemoAccessRowSchema>;

export const CrmCreateDemoAccessInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  language: CrmDemoLanguageSchema,
  // 6 a 12 caracteres, só letras minúsculas e números (owner, #778 —
  // 2026-09-29 12:25 +0200): a senha vai por e-mail e é redigitada pelo
  // prospect no celular — espaço, maiúscula e símbolo viravam "senha errada"
  // no primeiro login. O owner pediu mínimo 4; ficou 6 porque o Firebase Auth
  // recusa senha menor no `createUser` do Admin SDK — com 4 a demonstração
  // falharia na criação da conta. Não baixar sem trocar o provedor de senha.
  // O form (`NewDemoModal`) filtra ao digitar e corta em 12; aqui é a régua
  // que vale. Conta demo já existente não é afetada: a senha só é aplicada a
  // conta nova.
  password: z.string().min(6).max(12).regex(/^[a-z0-9]+$/),
  // E-mail digitado no form quando o lead não tem um. Se informado, é gravado no
  // lead e usado para criar/reusar a conta demo. Ausente = usa o e-mail do lead.
  email: z.email().optional(),
  // Telefone digitado no form quando o lead não tem `phone` nem `whatsapp`.
  // Mesma mecânica do `email`: se informado, é gravado no lead (em `phone`).
  // Vem em E.164 do cliente; o servidor revalida com libphonenumber, porque o
  // número é carimbado como verificado no perfil SEM passar por OTP.
  phone: z.string().min(1).max(20).optional(),
});
export type CrmCreateDemoAccessInput = z.infer<typeof CrmCreateDemoAccessInputSchema>;

export const CrmCreateDemoAccessResultSchema = z.object({
  id: z.string(),
  accountCreated: z.boolean(),
  memberAlreadyExisted: z.boolean(),
  /** O e-mail de convite foi enviado (best-effort; `false` = demo criada, e-mail
   *  não saiu). Ausente = função antiga (cliente trata como sucesso). */
  emailSent: z.boolean().optional(),
  /** Convite pronto para o vendedor copiar e mandar pelo WhatsApp, na língua
   *  da demo: links (Android, iPhone, web) + e-mail + senha (só conta nova).
   *  Contém a senha em claro — não logar. Ausente = função antiga (#778). */
  inviteText: z.string().optional(),
  /** Número para o link do WhatsApp do convite: dígitos do E.164, preferindo o
   *  `whatsapp` do lead ao `phone` (que pode ser fixo). Vazio = sem número
   *  discável. Ausente = função antiga (#778). */
  invitePhone: z.string().optional(),
});
export type CrmCreateDemoAccessResult = z.infer<typeof CrmCreateDemoAccessResultSchema>;

/**
 * Auto-acesso: o próprio usuário do CRM entra no tenant de demonstração da
 * língua escolhida com a conta Auth com que já loga no CRM (mesmo projeto
 * Firebase). Sem lead, sem senha, sem e-mail de convite. Idempotente por
 * (uid, língua): repetir devolve o acesso existente com `alreadyExisted: true`.
 */
export const CrmCreateSelfDemoAccessInputSchema = z.object({
  language: CrmDemoLanguageSchema,
});
export type CrmCreateSelfDemoAccessInput = z.infer<typeof CrmCreateSelfDemoAccessInputSchema>;

export const CrmCreateSelfDemoAccessResultSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  /** Já havia auto-acesso deste uid nesta língua — nada foi criado/reescrito. */
  alreadyExisted: z.boolean(),
  /** A conta já era membro do tenant demo antes desta concessão. */
  memberAlreadyExisted: z.boolean(),
});
export type CrmCreateSelfDemoAccessResult = z.infer<typeof CrmCreateSelfDemoAccessResultSchema>;

export const CrmListDemoAccessesResultSchema = z.object({
  rows: z.array(CrmDemoAccessRowSchema),
});
export type CrmListDemoAccessesResult = z.infer<typeof CrmListDemoAccessesResultSchema>;

/**
 * Revoga um ou mais acessos de demonstração: remove o membro do tenant demo
 * (corta o acesso ao app) e apaga o registro `crm_demo_accesses/{id}`. A conta
 * Firebase Auth do lead **nunca** é excluída (decisão do owner) — só a
 * membership no tenant demo. Aceita lote (checkbox da tabela) ou 1 id (menu de
 * três pontinhos). O servidor reautoriza cada id pelo mesmo escopo da listagem.
 */
export const CrmRevokeDemoAccessInputSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(200),
});
export type CrmRevokeDemoAccessInput = z.infer<typeof CrmRevokeDemoAccessInputSchema>;

export const CrmRevokeDemoAccessResultSchema = z.object({
  /** Registros revogados (membership removida + doc apagado). */
  revoked: z.number().int().nonnegative(),
  /** Ids ausentes (já removidos) ou cuja membership não era demo (`isDemo != true`, preservada). */
  skipped: z.number().int().nonnegative(),
  /** Ids fora do escopo do ator (não deveria ocorrer pela UI; guarda de servidor). */
  denied: z.number().int().nonnegative(),
  /** Ids que falharam por erro de I/O (o lote continua; re-tentar pode resolver).
   *  Ausente = função antiga (cliente trata como 0). */
  failed: z.number().int().nonnegative().optional(),
});
export type CrmRevokeDemoAccessResult = z.infer<typeof CrmRevokeDemoAccessResultSchema>;

export const CrmUpsertDemoTenantInputSchema = z.object({
  language: CrmDemoLanguageSchema,
  // Id do tenant do app principal. Os demos têm id escolhido
  // (`demo-account-<lang>`, registro em `seedTenants.ts`), não o formato dos
  // clientes (`{CC}-{autoId}` desde a #1005; `${countryCode}-${taxId}` antes) — a regex antiga
  // `^[A-Z]{2}-[A-Za-z0-9]+$` recusava os demos novos (#1003, 2026-10-19
  // 12:15 +0200). Agora é o mesmo alfabeto das outras validações de id do
  // servidor (`hardDeleteEmptyTenant`, `nfc.ts`); quem decide se o tenant
  // pode ser demo é o callable (ACTIVE + `isDemo` + tipo DEMO + língua).
  tenantId: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{1,128}$/),
});
export type CrmUpsertDemoTenantInput = z.infer<typeof CrmUpsertDemoTenantInputSchema>;

export const CrmDeleteDemoTenantInputSchema = z.object({
  language: CrmDemoLanguageSchema,
});
export type CrmDeleteDemoTenantInput = z.infer<typeof CrmDeleteDemoTenantInputSchema>;

/**
 * Tenant do app principal que pode ser o demo de uma língua — saída de
 * `crmListDemoTenantCandidates` (#994). É a lista do dropdown de
 * `/admin/demo-tenants`: só tenant ACTIVE, `isDemo` e de tipo comercial DEMO,
 * com a língua do idioma efetivo do tenant (`appLocale` fixado, senão `locale`).
 */
export const CrmDemoTenantCandidateSchema = z.object({
  tenantId: z.string().min(1),
  tenantName: z.string().nullable(),
  language: CrmDemoLanguageSchema,
});
export type CrmDemoTenantCandidate = z.infer<typeof CrmDemoTenantCandidateSchema>;

export const CrmListDemoTenantCandidatesResultSchema = z.object({
  candidates: z.array(CrmDemoTenantCandidateSchema),
});
export type CrmListDemoTenantCandidatesResult = z.infer<typeof CrmListDemoTenantCandidatesResultSchema>;

/**
 * Lead elegível para virar demonstração, resolvido no servidor. O picker do
 * form (`/demos/new`) usa isto em vez de ler `crm_leads` no cliente: as rules
 * do Firestore só liberam a leitura de leads a MEMBROS do quadro (ADMIN não tem
 * leitura global via cliente), então o escopo por papel (ADMIN=todos,
 * MANAGER=equipe/quadros, SELLER=próprios) só é possível via Admin SDK.
 */
export const CrmEligibleDemoLeadSchema = z.object({
  boardId: z.string(),
  boardName: z.string(),
  leadId: z.string(),
  name: z.string(),
  email: z.string(),
  /**
   * Telefone do lead em E.164, resolvido no servidor como `phone ?? whatsapp`
   * (mesma regra `phoneOrWhatsapp` do cadastro de lead). String VAZIA quando o
   * lead não tem nenhum dos dois — o form então pede o número na hora e o grava
   * no lead, exatamente como já faz com o `email`.
   */
  phone: z.string(),
});
export type CrmEligibleDemoLead = z.infer<typeof CrmEligibleDemoLeadSchema>;

export const CrmListEligibleDemoLeadsResultSchema = z.object({
  leads: z.array(CrmEligibleDemoLeadSchema),
});
export type CrmListEligibleDemoLeadsResult = z.infer<typeof CrmListEligibleDemoLeadsResultSchema>;

// ---------------------------------------------------------------------------
// Vendas & Comissões (issue #168)
//
// Fluxo: lead WON → onboarding → `crmVerifyPayment` casa o lead com o tenant
// pago do app principal (país+taxId) → onboarding DONE registra a venda em
// `crm_sales/{leadId}` (percentuais CONGELADOS) → cada pagamento Stripe
// (`tenants/{tid}/payments`) gera parcelas de comissão em `crm_commissions`
// via trigger. Dinheiro SEMPRE em cents (int) — desde o M12 (#173) o lead
// também: `soldValueCents`/`estimatedValueCents` são o canônico e o snapshot
// da venda copia o int direto (docs legados em unidades convertem na leitura
// via `crmLeadSoldCents`).
// ---------------------------------------------------------------------------

/**
 * Campos do lead exigidos para verificar pagamento e registrar a venda
 * (gate "dados completos" do onboarding). Regra extra fora desta lista:
 * `phone` OU `whatsapp` (pelo menos um). `soldValue` exige número > 0.
 * Validação de FORMATO do taxId fica fora do v1 (campo livre por decisão do
 * owner) — o matching com o tenant é a validação real.
 */
export const CRM_SALE_REQUIRED_LEAD_FIELDS = [
  'name',
  'companyName',
  'taxId',
  'countryCode',
  'email',
  'city',
  'state',
  'address',
  'businessType',
  'soldPlan',
  'soldValue',
] as const;
export type CrmSaleRequiredLeadField =
  | (typeof CRM_SALE_REQUIRED_LEAD_FIELDS)[number]
  | 'phoneOrWhatsapp';

/** True se o valor "conta como preenchido" (string não-vazia ou número/enum não-nulo). */
export function crmLeadFieldHasValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

type CrmSaleRequiredLeadData = Partial<
  Pick<
    CrmLead,
    | 'name'
    | 'companyName'
    | 'taxId'
    | 'countryCode'
    | 'email'
    | 'phone'
    | 'whatsapp'
    | 'city'
    | 'state'
    | 'address'
    | 'businessType'
    | 'soldPlan'
    | 'soldValue'
    | 'soldValueCents'
  >
>;

/**
 * Campos obrigatórios ainda não preenchidos (gate compartilhado cliente E
 * servidor — o botão "Verificar pagamento" desabilita com esta lista e o
 * callable revalida). `phoneOrWhatsapp` é o pseudo-campo da regra "pelo menos
 * um contato".
 */
export function crmLeadDataMissingFields(
  lead: CrmSaleRequiredLeadData | null | undefined,
): CrmSaleRequiredLeadField[] {
  const data = lead ?? {};
  const missing: CrmSaleRequiredLeadField[] = CRM_SALE_REQUIRED_LEAD_FIELDS.filter((field) => {
    if (field === 'soldValue') {
      // Cents-aware (M12): canônico `soldValueCents`, legado `soldValue`.
      const cents = crmLeadSoldCents(data);
      return !(cents != null && cents > 0);
    }
    return !crmLeadFieldHasValue(data[field]);
  });
  if (!crmLeadFieldHasValue(data.phone) && !crmLeadFieldHasValue(data.whatsapp)) {
    missing.push('phoneOrWhatsapp');
  }
  return missing;
}

/** As partes do endereço estruturado que a Contratação no Brasil exige (#771). */
export const CRM_BR_ADDRESS_FIELDS = ['postalCode', 'street', 'streetNumber', 'district', 'city', 'state'] as const;
export type CrmBrAddressField = (typeof CRM_BR_ADDRESS_FIELDS)[number];

/**
 * As partes do endereço que faltam para a Contratação de um lead do Brasil.
 *
 * Origem: #771 — 2026-09-28 17:59 +0200 (owner: "o campo lead não tem CEP!").
 *
 * Por quê: o cliente do Asaas precisa de endereço estruturado — sem ele a
 * NFS-e de boleto e Pix é recusada ("Endereço do cliente incompleto; CEP do
 * cliente é inválido", medido no sandbox em 2026-09-28). Separado de
 * `crmLeadDataMissingFields` DE PROPÓSITO: aquele gate vale para ganhar,
 * verificar pagamento e onboarding em qualquer país; este vale só para GERAR O
 * LINK no Brasil, e juntar os dois travaria o ganho de leads antigos que nunca
 * vão passar pela Contratação.
 *
 * Contrato: `countryCode` diferente de `BR` → `[]`. No BR, devolve os campos
 * de {@link CRM_BR_ADDRESS_FIELDS} vazios; o CEP conta como vazio se não tiver
 * exatamente 8 dígitos.
 *
 * Usado por: CRM `LeadCheckoutPage.tsx` (bloqueia "Gerar link") e Functions
 * `runSaleProvision` (`billing/provisionTenantForSale.ts`, recusa
 * `lead_address_incomplete`).
 */
export function crmLeadBrAddressMissing(
  lead: Partial<Pick<CrmLead, 'countryCode' | CrmBrAddressField>> | null | undefined,
): CrmBrAddressField[] {
  const data = lead ?? {};
  if ((data.countryCode ?? '').trim().toUpperCase() !== 'BR') return [];
  return CRM_BR_ADDRESS_FIELDS.filter((field) =>
    field === 'postalCode'
      ? (data.postalCode ?? '').replace(/\D/g, '').length !== 8
      : !crmLeadFieldHasValue(data[field]),
  );
}

/**
 * A razão social falta para a Contratação deste lead?
 *
 * Origem: #998 — 2026-10-18 14:22 +0200.
 *
 * Por quê: na venda RESELLER (#965) o tomador da NFS-e é o PARCEIRO, e a nota
 * identifica o cliente final pela razão social + CNPJ (decisão do owner,
 * 2026-10-18). `companyName` é o nome fantasia; sem a razão social a nota sairia
 * com o nome de rua. Separado de `crmLeadDataMissingFields` pelo mesmo motivo de
 * `crmLeadBrAddressMissing`: só vale para gerar o link, e só na revenda BR.
 *
 * Contrato: `true` só com `reseller` + país `BR` + `legalName` vazio.
 *
 * Usado por: CRM `LeadCheckoutPage.tsx` (pendência que bloqueia "Gerar link") e
 * Functions `runSaleProvision` (`billing/provisionTenantForSale.ts`, recusa
 * `lead_legal_name_missing`) e `createSaleCheckoutLink`.
 */
export function crmLeadLegalNameMissing(
  lead: Partial<Pick<CrmLead, 'countryCode' | 'legalName'>> | null | undefined,
  opts: { readonly reseller: boolean },
): boolean {
  if (!opts.reseller) return false;
  if ((lead?.countryCode ?? '').trim().toUpperCase() !== 'BR') return false;
  return !crmLeadFieldHasValue(lead?.legalName);
}

/**
 * Monta a linha livre `address` a partir das partes (#771): "Rua, número -
 * complemento - bairro - CEP". Partes vazias são omitidas. Usado pelo editor do
 * lead no BR e pela criação a partir da base de CNPJ, para os dois gerarem a
 * MESMA linha que a busca de cidades e o link do Maps já leem.
 *
 * Origem: #771 — 2026-09-28 17:59 +0200.
 *
 * Usado por: CRM `LeadEditorPage` (save de lead do Brasil) e CRM Functions
 * `cnpjLeadFields` (`lib/cnpjLeadDraft.ts`, criação pela base de CNPJ). Os dois
 * TÊM de usar esta função: formatos diferentes do CEP fariam todo save de um
 * lead da base mudar a linha e sujar a auditoria (auditoria da #771, achado 9).
 */
export function crmComposeLeadAddress(parts: {
  readonly street?: string | null | undefined;
  readonly streetNumber?: string | null | undefined;
  readonly complement?: string | null | undefined;
  readonly district?: string | null | undefined;
  readonly postalCode?: string | null | undefined;
}): string {
  const clean = (v: string | null | undefined): string => (v ?? '').trim();
  const cep = clean(parts.postalCode).replace(/\D/g, '');
  const cepText = cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : clean(parts.postalCode);
  const head = [clean(parts.street), clean(parts.streetNumber)].filter(Boolean).join(', ');
  return [head, clean(parts.complement), clean(parts.district), cepText].filter(Boolean).join(' - ');
}

/** True quando o cadastro do lead está completo para venda (gate do onboarding). */
export function crmLeadDataComplete(lead: CrmSaleRequiredLeadData | null | undefined): boolean {
  return crmLeadDataMissingFields(lead).length === 0;
}

/**
 * Campos do lead que TRAVAM após `billingLockedAt` (pagamento Stripe
 * vinculado): preenchidos ficam imutáveis; vazios ainda podem ser completados
 * (uma vez). Compartilhado entre `crmUpdateLead` (guard server-side) e o
 * `LeadEditorPage` (disabled).
 */
export const CRM_BILLING_LOCKED_LEAD_FIELDS = [
  'name',
  'companyName',
  // #998: a razão social é cadastro fiscal (vai na NFS-e do parceiro) — trava
  // como o nome; vazia ainda pode ser completada uma vez.
  'legalName',
  'taxId',
  'taxIdType',
  'countryCode',
  'email',
  'phone',
  'whatsapp',
  'city',
  'state',
  'address',
  // #771: as partes do endereço travam como a linha — vazias ainda podem ser
  // completadas uma vez (é assim que um lead já vendido ganha o CEP).
  'postalCode',
  'street',
  'streetNumber',
  'complement',
  'district',
  'businessType',
  'soldPlan',
  // Valor vendido: `soldValueCents` (canônico, M12) e `soldValue` (patch de
  // bundle antigo) são o MESMO campo lógico — `lockedFieldViolations` compara
  // ambos em cents contra `crmLeadSoldCents(lead)`.
  'soldValue',
  'soldValueCents',
] as const;
export type CrmBillingLockedLeadField = (typeof CRM_BILLING_LOCKED_LEAD_FIELDS)[number];

/**
 * Campos de atribuição que travam APÓS o registro da venda (`lead.saleId`):
 * os percentuais já estão congelados na venda; trocar dono/gerente depois só
 * dessincroniza a comissão de quem realmente vendeu.
 */
export const CRM_SALE_LOCKED_ASSIGNMENT_FIELDS = ['ownerUserId', 'managerUserId'] as const;

/** Status da venda. CANCELLED = todos os pagamentos 100% estornados (issue #278). */
export const CrmSaleStatusSchema = z.enum(['ACTIVE', 'CANCELLED']);
export type CrmSaleStatus = z.infer<typeof CrmSaleStatusSchema>;

/**
 * Motivo de cancelamento (venda ou parcela). Só existe `REFUND` hoje, mas o
 * campo é um enum de propósito: a reconciliação de estorno REVERTE apenas
 * cancelamentos com reason `REFUND` (um refund Pix `pending` que falha
 * "des-estorna" o pagamento) — cancelamentos futuros de outra natureza nunca
 * podem ser ressuscitados por ela.
 */
// `COLLABORATOR_REMOVED` (owner, 2026-09-25): remover o colaborador anula as
// parcelas PENDING dele. Não é revertido pelo estorno nem pela restauração.
// `MANAGER_OFFBOARDED` (#788, owner 2026-09-30): gerente rebaixado ou
// desativado perde as parcelas PENDING de GERENTE (as de vendedor ficam).
// `TENANT_DELETED` (#789, owner 2026-10-01): só em VENDA, nunca em parcela. É a
// venda de um tenant apagado, arquivada em `crm_sales/{leadId}__{createdAt}`
// quando o mesmo CNPJ (id `BR-<CNPJ>`) é vendido de novo pelo mesmo lead. Não é
// revertida pelo estorno.
export const CrmCancelReasonSchema = z.enum(['REFUND', 'COLLABORATOR_REMOVED', 'MANAGER_OFFBOARDED', 'TENANT_DELETED']);
export type CrmCancelReason = z.infer<typeof CrmCancelReasonSchema>;

/**
 * Snapshot CONGELADO do cliente no momento do registro da venda. A venda é um
 * registro financeiro: edições posteriores do lead (campos ainda vazios) não
 * retroagem aqui.
 */
export const CrmSaleCustomerSnapshotSchema = z.object({
  name: z.string(),
  companyName: z.string().nullable(),
  taxId: z.string(),
  taxIdType: z.string().nullable(),
  countryCode: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  whatsapp: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  address: z.string().nullable(),
  businessType: z.string().nullable(),
});
export type CrmSaleCustomerSnapshot = z.infer<typeof CrmSaleCustomerSnapshotSchema>;

/**
 * Por que um pagamento virou evento de ciclo na venda (#740). Registrado
 * quando o ciclo muda OU quando a cobrança é semestral/anual (comissão única).
 */
export const CrmCycleEventReasonSchema = z.enum([
  /** 1ª cobrança semestral/anual sem histórico: comissão integral. */
  'FIRST_ONE_SHOT',
  /** Migração para semestral/anual: `% × cobrança − já gerado` > 0. */
  'MIGRATION_TOP_UP',
  /** Migração para semestral/anual em que o já gerado cobre a comissão nova: 0. */
  'MIGRATION_NO_TOP_UP',
  /** Mensal ↔ trimestral: segue o % normal por cobrança dentro da janela. */
  'RECURRING_CYCLE_CHANGE',
  /** Depois de uma comissão semestral/anual nada mais comissiona. */
  'AFTER_CLOSE',
  /** A cobrança começa depois dos 12 meses do primeiro pagamento. */
  'OUTSIDE_WINDOW',
  /** Nenhum beneficiário tinha direito (vendedor inativo/removido). */
  'NOT_ENTITLED',
]);
export type CrmCycleEventReason = z.infer<typeof CrmCycleEventReasonSchema>;

/** Cálculo de UM beneficiário num evento de ciclo — a trilha de auditoria. */
export const CrmCycleEventBeneficiarySchema = z.object({
  userId: z.string(),
  // Mesma lista de `CrmCommissionBeneficiaryRoleSchema` (declarado mais abaixo;
  // referenciá-lo aqui seria uso antes da declaração). `PARTNER_MANAGER` (#743)
  // nunca aparece num evento de ciclo de venda, mas o tipo tem de aceitá-lo.
  // `PARTNER` (#753) idem: venda com split não passa pela regra de ciclos.
  // Sem `PARTICIPANT` desde a #758 (2026-09-28): a comissão de participação
  // saiu e os eventos antigos foram limpos de produção antes de o valor sair.
  role: z.enum(['SELLER', 'MANAGER', 'PARTNER_MANAGER', 'PARTNER']),
  name: z.string().nullable(),
  percent: z.number().min(0).max(100),
  /** `% × cobrança`: a comissão total do novo contrato. */
  newContractCents: z.number().int().nonnegative(),
  /** Comissões não canceladas já geradas para ele nesta venda (PENDING + PAID). */
  priorGeneratedCents: z.number().int().nonnegative(),
  /** O que de fato virou parcela: `max(0, novo − já gerado)` no one-shot. */
  additionalCents: z.number().int().nonnegative(),
});
export type CrmCycleEventBeneficiary = z.infer<typeof CrmCycleEventBeneficiarySchema>;

/**
 * Evento de ciclo gravado em `crm_sales.cycleEvents` (#740), um por pagamento
 * (dedupe por `paymentId`). É o histórico de migrações da tela e a prova do
 * cálculo — nunca é reescrito depois.
 */
export const CrmSaleCycleEventSchema = z.object({
  paymentId: z.string(),
  paidAt: z.number().int(),
  /** Início do período cobrado (linha da fatura); `null` quando a fatura não existia. */
  periodStart: z.number().int().nullable(),
  /** Ciclo anterior; `null` = primeira cobrança da venda. */
  fromCycle: CrmPlanInterestSchema.nullable(),
  toCycle: CrmPlanInterestSchema,
  /** Valor efetivamente cobrado (`payment.amount`, cents). */
  chargeCents: z.number().int().nonnegative(),
  /** Cobrança semestral/anual — encerra o comissionamento enquanto não estornada. */
  oneShot: z.boolean(),
  reason: CrmCycleEventReasonSchema,
  beneficiaries: z.array(CrmCycleEventBeneficiarySchema),
  /** Soma das parcelas geradas por este pagamento. */
  totalCommissionCents: z.number().int().nonnegative(),
  /**
   * `chargeCents − totalCommissionCents` (antes de impostos e taxas). Sempre
   * gravado; `null` só na leitura de quem não vê a comissão dos outros.
   */
  netToCompanyCents: z.number().int().nullable(),
  recordedAt: z.number().int(),
});
export type CrmSaleCycleEvent = z.infer<typeof CrmSaleCycleEventSchema>;

/**
 * `crm_sales/{saleId}` — venda registrada. **docId = leadId** (1 venda por
 * lead, idempotente). Nasce quando o PAGAMENTO é vinculado ao lead (#278):
 * vínculo automático do trigger de payment ou `crmVerifyPayment` manual; a
 * transição DONE do onboarding segue chamando `registerSaleForLead` como
 * no-op idempotente. Nunca pelo cliente (rules: default-deny total; leitura
 * só via callables com escopo por papel).
 *
 * Percentuais de comissão são CONGELADOS aqui no registro — mudar o slider em
 * /admin/collaborators depois só afeta vendas futuras (mesmo princípio do
 * pricing congelado do billing).
 */
export const CrmSaleSchema = z.object({
  id: z.string(),
  boardId: z.string(),
  leadId: z.string(),
  /** Tenant pago do app principal (docId de `tenants/`: `{CC}-{autoId}`, ou `{CC}-{doc}` nos antigos — #1005). */
  tenantId: z.string(),
  customer: CrmSaleCustomerSnapshotSchema,
  sellerUserId: z.string(),
  sellerName: z.string().nullable(),
  /** % congelado do vendedor no registro (leitura tolerante 0..100). */
  sellerCommissionPercent: z.number().min(0).max(100),
  managerUserId: z.string().nullable(),
  managerName: z.string().nullable(),
  /** % congelado do gerente; `null` quando a venda não tem gerente. */
  managerCommissionPercent: z.number().min(0).max(100).nullable(),
  // `participantUserId`/`participantName`/`participationCommissionPercent`
  // (comissão de participação) saíram em 2026-09-28 (#758): a venda tem
  // vendedor + gerente, e venda fechada por MANAGER tem só o vendedor.
  soldPlan: CrmPlanInterestSchema,
  /** Valor negociado no lead convertido p/ cents (referência; a comissão usa o pago real). */
  soldAmountCents: z.number().int().nonnegative().nullable(),
  currency: z.string(),
  /**
   * Teto do ciclo VENDIDO (D14, `crmInstallmentsCapFor`), congelado no
   * registro. Campo LEGADO: desde a #780 (2026-09-29) nada o lê para decidir —
   * o limite é o prazo de cada beneficiário, lido do cadastro a cada pagamento.
   */
  installmentsCap: z.union([z.literal(1), z.literal(2), z.literal(4), z.literal(12)]),
  /**
   * Início dos 12 meses de comissionamento (#740): o `period_start` da fatura
   * do primeiro pagamento comissionável (fallback `paid_at`). Gravado uma vez;
   * troca de ciclo e reativação NÃO reiniciam. `null` em venda anterior à
   * #740 até o próximo pagamento — aí vem da parcela nº 1.
   */
  commissionWindowStartAt: z.number().int().nullable().default(null),
  /** Ciclo da última cobrança processada (#740); `null` = nenhuma ainda. */
  currentCycle: CrmPlanInterestSchema.nullable().default(null),
  /** Trocas de ciclo e cobranças semestrais/anuais, com o cálculo (#740). */
  cycleEvents: z.array(CrmSaleCycleEventSchema).default([]),
  /**
   * Composição vendida, item a item (#679 F2) — só em venda da Contratação;
   * venda legada (plano único do catálogo) não tem. Vem do `billing/subscription`
   * congelado, não do lead.
   */
  items: z.array(CrmSaleItemSchema).nullable().optional(),
  /** Origem do preço: `CRM_SALE` = negociado na Contratação; ausente = catálogo. */
  // #761 — os valores de `PricingSource` (`enums.ts`), não uma cópia redigitada.
  pricingSource: z.enum(ALL_PRICING_SOURCES).nullable().optional(),
  /** Contador transacional de parcelas já geradas (gate do cap no trigger). */
  installmentsGenerated: z.number().int().nonnegative().default(0),
  /**
   * Pagamentos do cliente que NÃO geraram comissão porque o vendedor não estava
   * ATIVO no momento (removido/desativado). Eles consomem uma das
   * `installmentsCap` janelas — comissão suspensa é perdida, não adiada: se o
   * colaborador voltar, recebe as janelas restantes, nunca as do período fora.
   *
   * Guardado como lista (e não só um contador) porque é o que dá idempotência:
   * o trigger roda com `retry: true`, e sem o id do pagamento uma re-entrega
   * consumiria a janela duas vezes, tirando parcela de quem tem direito. Cresce
   * no máximo até `installmentsCap` (12).
   */
  skippedPaymentIds: z.array(z.string()).default([]),
  /**
   * Beneficiários que PERDERAM o direito a esta venda para sempre — hoje, quem
   * foi REMOVIDO do CRM (owner, 2026-09-25). Diferente do status do usuário, que
   * é lido a cada pagamento: restaurar e reativar o colaborador não devolve a
   * comissão das vendas antigas dele. Vendedor aqui = ninguém recebe (mesma
   * regra do vendedor inativo em `filterEntitledBeneficiaries`).
   */
  revokedBeneficiaryUserIds: z.array(z.string()).default([]),
  /**
   * Gerentes que perderam só a parte de GERENTE desta venda — rebaixados ou
   * desativados (#788, owner 2026-09-30). Diferente de
   * `revokedBeneficiaryUserIds`, não tira a parte de vendedor de quem vendeu
   * sendo gerente. Opcional (ausente = `[]`): vendas anteriores não têm o
   * campo, e os fixtures de teste montam `CrmSale` sem ele.
   */
  revokedManagerUserIds: z.array(z.string()).optional(),
  wonAt: z.number().int().nullable(),
  registeredAt: z.number().int(),
  /** Quem registrou: uid do verificador manual ou `__system__` (vínculo automático, #278). */
  registeredBy: z.string(),
  status: CrmSaleStatusSchema.default('ACTIVE'),
  /** Quando a venda foi cancelada (todos os pagamentos estornados); `null` em vendas ativas. */
  cancelledAt: z.number().int().nullable().default(null),
  /** Por que foi cancelada — a reversão declarativa só toca reason `REFUND`. */
  cancelReason: CrmCancelReasonSchema.nullable().default(null),
  /**
   * Venda ARQUIVADA de uma encarnação anterior do tenant (#789): o doc
   * `crm_sales/{leadId}__{createdAt}` guarda a venda do tenant apagado quando o
   * mesmo lead vende o mesmo CNPJ de novo, e `crm_sales/{leadId}` passa a ser a
   * venda nova. `status`/`cancelReason` de antes do arquivamento ficam aqui;
   * no doc, `CANCELLED`/`TENANT_DELETED`. Ausente em toda venda não arquivada
   * (`.optional()`, como `revokedManagerUserIds`: venda anterior ao campo).
   *
   * Origem: #789 — 2026-10-01 10:18 +0200. Gravado só por
   * `archivePreviousIncarnationSale` (`apps/crm-functions/src/lib/saleArchive.ts`).
   */
  archivedFrom: z
    .object({
      saleId: z.string(),
      status: CrmSaleStatusSchema,
      cancelReason: CrmCancelReasonSchema.nullable(),
      archivedAt: z.number().int(),
    })
    .optional(),
  /**
   * Venda de TESTE: o tenant é um dos estabelecimentos sintéticos de billing
   * (`TEST_MODE_BILLING_TENANT_IDS`) e o pagamento veio do sandbox.
   *
   * Origem: #768 — 2026-09-28 17:10 +0200 (owner: pagamento sandbox do estabelecimento de
   * teste ganha o lead e registra a venda, marcada como teste, para o pós-venda
   * ser testável). Gravado por `buildSaleDoc` no registro; nunca muda.
   * `createCommissionsForPayment` recusa venda de teste (`test_sale`): ela
   * existe para o fluxo, nunca para dinheiro. `.default(false)`: venda anterior
   * ao campo é real.
   */
  test: z.boolean().default(false),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type CrmSale = z.infer<typeof CrmSaleSchema>;

/**
 * PENDING = a receber (pagamento Stripe entrou); PAID = admin pagou o
 * colaborador; CANCELLED = o pagamento de origem foi 100% estornado antes de a
 * parcela ser paga (issue #278) — nunca se cancela dinheiro já pago (isso é
 * `clawbackDue`, decisão manual do admin).
 */
export const CrmCommissionStatusSchema = z.enum(['PENDING', 'PAID', 'CANCELLED']);
export type CrmCommissionStatus = z.infer<typeof CrmCommissionStatusSchema>;

/**
 * Papel do beneficiário NA VENDA (não no CRM). Não há `PARTICIPANT`: a comissão
 * de participação (#280, 2026-08-27) saiu na #758 (2026-09-28), e as parcelas
 * dela foram apagadas de produção antes de o valor sair daqui — um doc com ele
 * falharia no parse.
 */
/** `PARTNER_MANAGER` (#743, 9.9): o gerente interno do parceiro, sobre o repasse recebido. */
/**
 * `PARTNER` (#753, A4): o parceiro do modelo DIRECT, sobre o líquido de cada
 * pagamento. A linha não é de um colaborador: `beneficiaryUserId` é
 * `partnerCommissionBeneficiaryId(partnerId)` e `partnerId` vem preenchido.
 * Fica fora dos totais de colaborador (`SalesPage`/`SaleDetailPage`) e é a
 * matéria do extrato de comissões do parceiro (`crmListPartnerCommissions`).
 */
export const CrmCommissionBeneficiaryRoleSchema = z.enum([
  'SELLER',
  'MANAGER',
  'PARTNER_MANAGER',
  'PARTNER',
]);
export type CrmCommissionBeneficiaryRole = z.infer<typeof CrmCommissionBeneficiaryRoleSchema>;
/**
 * Os papéis de beneficiário da comissão como constantes
 * (`CrmCommissionBeneficiaryRole.PARTNER`).
 *
 * Origem: #753 — 2026-09-27 19:03 +0200 (padrão da #761: `PARTNER` aqui não é
 * `CrmRole.PARTNER` nem `CommercialMode.PARTNER`).
 */
export const CrmCommissionBeneficiaryRole = CrmCommissionBeneficiaryRoleSchema.enum;

/**
 * `beneficiaryUserId` sintético da linha do parceiro DIRECT.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 *
 * Por quê: o parceiro não é um `crm_users` (os usuários dele são vários, e o
 * dinheiro é da empresa), mas o docId da parcela é
 * `{paymentId}_{beneficiaryUserId}` e o campo é string obrigatória para os
 * consumidores antigos. O prefixo `partner:` não colide com uid do Firebase
 * Auth (que não tem `:`).
 */
export function partnerCommissionBeneficiaryId(partnerId: string): string {
  return `partner:${partnerId}`;
}

/**
 * Por onde a comissão chegou (ou vai chegar) ao beneficiário — o
 * `transfer_mode` do split congelado na venda (`SplitTransferModeSchema`).
 *
 * Origem: #753 — 2026-09-27 19:03 +0200 (A4; a dedupe foi registrada na #752
 * como pré-requisito da A7, #756).
 *
 * - `ASAAS_SPLIT` / `STRIPE_CONNECT`: o gateway já pagou. A linha é
 *   INFORMATIVA — nasce `PAID`, `paidBy: COMMISSION_PAID_BY_SPLIT`, e
 *   `crmSetCommissionPaid` a recusa. Existe para o extrato e para os totais;
 *   sem ela o colaborador com carteira era pago duas vezes (split + CRM).
 * - `MANUAL`: sem destino no gateway; a linha nasce `PENDING` e o ADMIN paga
 *   por fora, como sempre.
 * - `null`: venda sem snapshot de split (anterior à #748) — regra da #740.
 */
export const CrmCommissionPaidViaSchema = SplitTransferModeSchema;
export type CrmCommissionPaidVia = z.infer<typeof CrmCommissionPaidViaSchema>;
/** As formas de pagamento da comissão como constantes (`CrmCommissionPaidVia.MANUAL`). */
export const CrmCommissionPaidVia = CrmCommissionPaidViaSchema.enum;

/**
 * `paidBy` da linha que o gateway pagou — não é um uid, e nenhum ADMIN a marcou.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 */
export const COMMISSION_PAID_BY_SPLIT = '__split__';

/** A linha foi paga pelo gateway (split Asaas ou Connect) e não pelo ADMIN? */
export function isCommissionPaidByGateway(c: { paidVia?: CrmCommissionPaidVia | null | undefined }): boolean {
  return c.paidVia === CrmCommissionPaidVia.ASAAS_SPLIT || c.paidVia === CrmCommissionPaidVia.STRIPE_CONNECT;
}

/** Natureza da parcela (#740): recorrente (mensal/trimestral) ou única (semestral/anual). */
export const CrmCommissionKindSchema = z.enum(['RECURRING', 'ONE_SHOT']);
export type CrmCommissionKind = z.infer<typeof CrmCommissionKindSchema>;

/**
 * `crm_commissions/{paymentId}_{beneficiaryUserId}` — parcela de comissão de UM
 * beneficiário sobre UM pagamento Stripe. docId determinístico = idempotência
 * do trigger (re-entrega/retry não duplica). Top-level (não subcoleção) para a
 * query por beneficiário sem collection-group. Escrita só server-side.
 *
 * Um pagamento gera até 2 docs (SELLER + MANAGER), mesmo `installmentNumber`.
 * `amountCents = Math.round(baseAmountCents * percent / 100)`.
 */
export const CrmCommissionSchema = z.object({
  id: z.string(),
  /**
   * #743 (9.9) — de onde vem a parcela. Ausente = pagamento de uma venda
   * (tudo o que existia antes). `PARTNER_REMITTANCE` = comissão do gerente do
   * parceiro sobre o extrato de repasse recebido: nessa parcela não há venda,
   * lead nem pagamento Stripe. Os campos de venda seguem string (consumidores
   * antigos os leem sem checar nulo) e carregam o extrato: `saleId` e
   * `paymentId` = `remittanceId`; `boardId`/`leadId`/`tenantId` = ''.
   */
  sourceType: z.enum(['SALE_PAYMENT', 'PARTNER_REMITTANCE']).optional(),
  remittanceId: z.string().min(1).nullable().optional(),
  partnerId: z.string().min(1).nullable().optional(),
  saleId: z.string(),
  boardId: z.string(),
  leadId: z.string(),
  tenantId: z.string(),
  /** DocId do payment em `tenants/{tid}/payments` (id Stripe: pi_, ch_ ou in_). */
  paymentId: z.string(),
  invoiceId: z.string().nullable(),
  /** 1..installmentsCap da venda. */
  installmentNumber: z.number().int().min(1),
  beneficiaryUserId: z.string(),
  beneficiaryRole: CrmCommissionBeneficiaryRoleSchema,
  beneficiaryName: z.string().nullable(),
  /** % congelado (copiado da venda no momento da geração). */
  percent: z.number().min(0).max(100),
  /** `payment.amount` (= amount_paid da invoice Stripe), em cents. */
  baseAmountCents: z.number().int().nonnegative(),
  amountCents: z.number().int().nonnegative(),
  currency: z.string(),
  /** Quando o CLIENTE pagou na Stripe (`payment.paid_at`). */
  paidStripeAt: z.number().int(),
  /** Vencimento: quarta-feira estritamente seguinte ao paid_at, 12:00 America/Sao_Paulo. */
  dueAt: z.number().int(),
  status: CrmCommissionStatusSchema.default('PENDING'),
  /** Quando o ADMIN pagou o colaborador (marcação manual). */
  paidAt: z.number().int().nullable(),
  paidBy: z.string().nullable(),
  /** Comprovante no Storage: `crm_commissions/{id}/{fileName}`. */
  receiptPath: z.string().nullable(),
  // --- Estorno (issue #278). Todos com default: docs anteriores ao refund
  // handling continuam parseando sem backfill. A reconciliação é DECLARATIVA:
  // deriva estes campos do estado atual do payment, então reaplicar o mesmo
  // estado é no-op e um refund que regride (Pix pending→failed) converge.
  /** Quando a parcela foi cancelada por estorno; `null` fora disso. */
  cancelledAt: z.number().int().nullable().default(null),
  /** Motivo do cancelamento — só cancelamento `REFUND` pode ser revertido. */
  cancelReason: CrmCancelReasonSchema.nullable().default(null),
  /** `refunded_amount` do payment de origem na última reconciliação (cents). */
  sourceRefundedCents: z.number().int().nonnegative().default(0),
  /**
   * `amountCents` original antes do 1º ajuste pro-rata de refund parcial.
   * Gravado UMA vez; `null` enquanto a parcela nunca foi ajustada.
   */
  originalAmountCents: z.number().int().nullable().default(null),
  /**
   * Parcela JÁ PAGA cujo pagamento de origem foi estornado: o dinheiro saiu e
   * só o ADMIN decide reaver (clawback). A reconciliação liga/desliga a flag,
   * nunca toca `status`/`paidAt`.
   */
  clawbackDue: z.boolean().default(false),
  /** Quando `clawbackDue` ligou pela 1ª vez (dedupe do alerta ao admin). */
  refundFlaggedAt: z.number().int().nullable().default(null),
  // --- Ciclo (#740). `null` em parcelas anteriores: nada é recalculado.
  /** Ciclo da cobrança que gerou a parcela. */
  cycle: CrmPlanInterestSchema.nullable().default(null),
  /** RECURRING = mensal/trimestral; ONE_SHOT = semestral/anual (encerra a venda). */
  kind: CrmCommissionKindSchema.nullable().default(null),
  /** `% × base` antes do desconto do já gerado. */
  grossAmountCents: z.number().int().nonnegative().nullable().default(null),
  /** Comissão já gerada descontada (migração para semestral/anual). */
  priorGeneratedCents: z.number().int().nonnegative().nullable().default(null),
  /** Ciclo anterior quando a parcela nasceu de uma troca de ciclo. */
  previousCycle: CrmPlanInterestSchema.nullable().default(null),
  /** Início da janela de 12 meses no momento do cálculo. */
  windowStartAt: z.number().int().nullable().default(null),
  // --- Split (#753, A4). Ausentes/`null` nas parcelas de venda sem snapshot
  // de split — `.optional()` (e não `.default`) pelo mesmo motivo do
  // `sourceType`: quem monta a parcela das regras antigas não os conhece.
  // Nestas parcelas `baseAmountCents` é o LÍQUIDO do pagamento (settlement) e
  // `percent` é o percentual configurado; o valor sai de `netPercent`.
  /** Ver `CrmCommissionPaidViaSchema`. */
  paidVia: CrmCommissionPaidViaSchema.nullable().optional(),
  /** Percentual efetivo sobre o líquido (`net_percent` do split congelado). */
  netPercent: z.number().min(0).max(100).nullable().optional(),
  /** Status do split no gateway (só nas linhas pagas pelo gateway). */
  splitStatus: SettlementSplitStatusSchema.nullable().optional().catch('UNKNOWN'),
  /**
   * #764 (owner, 2026-10-27) — id do parcelamento Asaas (`installment_id` do
   * payment) quando a parcela nasceu da 1ª parcela do anual parcelado e a base
   * é o TOTAL do ano (`baseAmountCents` = `installment_total_cents`). O estorno
   * dessa parcela é julgado pelo parcelamento inteiro, não pela 1ª cobrança
   * (`installmentPlanRefundState`, `commissionRefunds.ts`). Ausente/`null` em
   * todo o resto.
   */
  installmentPlanId: z.string().min(1).nullable().optional(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type CrmCommission = z.infer<typeof CrmCommissionSchema>;

// --- crmVerifyPayment ------------------------------------------------------

export const CrmVerifyPaymentInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
});
export type CrmVerifyPaymentInput = z.infer<typeof CrmVerifyPaymentInputSchema>;

/**
 * Resultado do matching lead↔tenant + 1º pagamento confirmado. Erros esperados
 * viajam como `HttpsError` com códigos de app no message: `LEAD_DATA_INCOMPLETE`,
 * `TENANT_NOT_FOUND`, `NO_PAYMENT_FOUND`, `TENANT_ALREADY_SOLD`.
 */
export const CrmVerifyPaymentResultSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string().nullable(),
  /** E-mail do OWNER do tenant bate com o do lead (sinal secundário; divergir não bloqueia). */
  ownerEmailMatch: z.boolean(),
  firstPaymentAt: z.number().int(),
  firstPaymentAmountCents: z.number().int().nonnegative(),
  currency: z.string(),
});
export type CrmVerifyPaymentResult = z.infer<typeof CrmVerifyPaymentResultSchema>;

// --- crmSyncTenantDeployment -----------------------------------------------
// (`CrmTenantSnapshotSchema` fica lá em cima, junto do checklist: o
// `CrmLeadSchema` o referencia e é declarado antes deste ponto.)

export const CrmSyncTenantInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
});
export type CrmSyncTenantInput = z.infer<typeof CrmSyncTenantInputSchema>;

/**
 * Resultado do sync. Erros esperados viajam como `HttpsError` com código de app
 * no message: `TENANT_NOT_LINKED` (pagamento não verificado ainda),
 * `TENANT_NOT_FOUND`.
 */
export const CrmSyncTenantResultSchema = z.object({
  snapshot: CrmTenantSnapshotSchema,
  /** Itens marcados por este sync. */
  checked: z.array(z.string()),
  /** Itens desmarcados por este sync (sinal deixou de valer). */
  unchecked: z.array(z.string()),
  /**
   * Onboarding já concluído (status DONE): o checklist fica INTOCADO e só os
   * KPIs são atualizados — concluir registra a venda, e um cliente que arquiva
   * produtos não pode reverter isso por efeito colateral de um botão.
   */
  frozen: z.boolean(),
});
export type CrmSyncTenantResult = z.infer<typeof CrmSyncTenantResultSchema>;

// --- crmUpdateLead (edição de lead com lock de billing) ---------------------

/**
 * Patch parcial do lead para edição via servidor — único caminho quando
 * `billingLockedAt != null` (as rules negam a escrita direta). O guard rejeita
 * mudança em campo travado já preenchido (`failed-precondition
 * FIELD_LOCKED:{campo}`); campos vazios podem ser completados.
 */
export const CrmUpdateLeadPatchSchema = z
  .object({
    name: z.string().min(1),
    companyName: z.string().nullable(),
    // #998 — razão social (ver `CrmLeadSchema`); mesmo teto das rules.
    legalName: z.string().max(160).nullable(),
    taxId: z.string().nullable(),
    taxIdType: z.string().nullable(),
    countryCode: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    whatsapp: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    address: z.string().nullable(),
    // #771 — endereço estruturado (ver `CrmLeadSchema`).
    postalCode: z.string().max(12).nullable(),
    street: z.string().max(160).nullable(),
    streetNumber: z.string().max(20).nullable(),
    complement: z.string().max(80).nullable(),
    district: z.string().max(80).nullable(),
    businessType: z.string().nullable(),
    source: z.string().nullable(),
    planInterest: CrmPlanInterestSchema.nullable(),
    // Dinheiro em CENTS int + moeda (issue #173, M12). Os floats legados
    // `estimatedValue`/`soldValue` seguem aceitos SÓ para o bundle antigo na
    // janela de deploy; nada novo os manda.
    estimatedValue: z.number().nonnegative().nullable(),
    estimatedValueCents: z.number().int().nonnegative().nullable(),
    currency: CrmCurrencySchema.nullable(),
    // Venda ganha: editáveis só enquanto vazios (gate de dados completos exige
    // ambos; travam como os demais campos após o lock/registro).
    soldPlan: CrmPlanInterestSchema.nullable(),
    soldValue: z.number().nonnegative().nullable(),
    soldValueCents: z.number().int().nonnegative().nullable(),
    temperature: CrmLeadTemperatureSchema,
    // Sem `columnId` (issue #173, A5): mudar a etapa é MOVIMENTAÇÃO e sai só
    // pelo `crmMoveLead` — que carimba `columnEnteredAt`/`stageReachedAt`,
    // notifica demo agendada e aplica o guard de billing. Aceitá-lo aqui
    // gravava a coluna crua no lead travado, sem nada disso. Bundle antigo que
    // ainda mande `columnId` no patch tem o campo silenciosamente descartado
    // pelo Zod (não-strict) — o save passa, a coluna não muda.
    // Sem `ownerUserId` (issue #173, M11): trocar o dono é transferência de
    // carteira e sai só pelo `crmTransferLead` — que exige MANAGER, valida
    // membro ATIVO do quadro e audita. Aceitá-lo aqui era a porta dos fundos:
    // o editor reatribuía sem nada disso, e o lead podia acabar com um dono que
    // nem lê o quadro.
    managerUserId: z.string().nullable(),
    // Metadados de apresentação do cabeçalho (favorito/logo). Fora do lock de
    // billing de propósito: não são cadastro fiscal — lead travado ainda pode
    // favoritar e trocar o logo.
    favorite: z.boolean(),
    logoPath: z.string().trim().max(500).nullable(),
    // Sem `nextAction`/`nextFollowUpAt` (issue #173, M6): são o ESPELHO do
    // próximo compromisso pendente de `crm_appointments` — o único escritor é
    // o recálculo transacional dos callables da agenda. Aceitá-los aqui
    // gravava por cima do espelho (o valor evaporava no próximo recálculo) e
    // `crmScanFollowUps` notificava a data errada no meio-tempo.
    notes: z.string().nullable(),
  })
  .partial();
export type CrmUpdateLeadPatch = z.infer<typeof CrmUpdateLeadPatchSchema>;

export const CrmUpdateLeadInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  patch: CrmUpdateLeadPatchSchema,
});
export type CrmUpdateLeadInput = z.infer<typeof CrmUpdateLeadInputSchema>;

/**
 * Troca do documento fiscal de um lead JÁ existente (issue #202) — `crmSetLeadTaxId`.
 *
 * Sai do `crmUpdateLead`/escrita direta porque mudar o documento exige mover a
 * reserva de unicidade `crm_lead_taxids` (apagar a antiga + criar a nova numa
 * transaction). As rules negam `delete` na reserva de propósito: se qualquer
 * membro pudesse apagá-la, bastava isso para burlar o "1 CNPJ = 1 lead em
 * negociação" (só o servidor a solta, quando o lead vira cliente — #1005).
 *
 * `taxId` nulo/vazio REMOVE o documento do lead (e libera a reserva) — é o
 * caminho de corrigir um CNPJ digitado errado.
 */
export const CrmSetLeadTaxIdInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
  taxId: z.string().max(32).nullable().default(null),
  taxIdType: z.string().max(40).nullable().default(null),
  countryCode: z.string().max(2).nullable().default(null),
});
export type CrmSetLeadTaxIdInput = z.infer<typeof CrmSetLeadTaxIdInputSchema>;

// --- crmLeadTaxIdInfo / crmClaimColdLead (conflito de documento, issue #202) --

/**
 * Consulta "quem já tem este documento fiscal" — `crmLeadTaxIdInfo`.
 *
 * Existe porque o cliente NÃO consegue responder isso sozinho: as rules deixam
 * ele ler a reserva `crm_lead_taxids` (o ponteiro board+lead), mas não o lead
 * apontado (pode estar em outro quadro, ou num quadro OWN_ONLY) nem o
 * `crm_users` do dono. Chamado só quando o save já falhou por duplicidade —
 * o caminho feliz não paga essas leituras.
 */
export const CrmLeadTaxIdInfoInputSchema = z.object({
  countryCode: z.string().min(1).max(2),
  taxId: z.string().min(1).max(32),
});
export type CrmLeadTaxIdInfoInput = z.infer<typeof CrmLeadTaxIdInfoInputSchema>;

/**
 * Veredito do conflito, decidido NO SERVIDOR (o cliente só renderiza):
 * - `AVAILABLE` — não há reserva (o save falhou por outro motivo), ou ela era
 *   de um lead já cliente e o servidor acabou de soltá-la (#1005, 2ª venda) —
 *   o editor repete o save uma vez.
 * - `ORPHAN` — reserva aponta para lead inexistente/divergente (dado antigo:
 *   até a #1005 o `crmMoveLeadToBoard` não reapontava a reserva). ADMIN
 *   reconcilia com `crmBackfillLeadTaxIds`.
 * - `SELF` — o lead já é do próprio usuário; basta abri-lo.
 * - `CLAIMABLE` — lead parado: pode ser transferido para quem consultou.
 * - `BLOCKED` — duplicação impedida; `blockedReason` diz o porquê.
 */
export type CrmLeadTaxIdConflictVerdict =
  | 'AVAILABLE'
  | 'ORPHAN'
  | 'SELF'
  | 'CLAIMABLE'
  | 'BLOCKED';

/**
 * `CLIENT` — ganho ou com pagamento travado, mas SEM tenant vinculado (o lead
 * vinculado já não segura a reserva desde a #1005): nunca reivindicável. `STAGE` — negócio em andamento (≥ "Demonstração agendada").
 * `FRESH` — outro vendedor mexeu nele há menos de `CRM_LEAD_CLAIM_FRESH_DAYS`.
 * `TENANT` — LEGADO: era "o documento já é de um tenant, só ADMIN cria lead"
 * (#795, CRM-1b). Desde a #1005 (2026-10-19) um CNPJ pode ter vários tenants e
 * o servidor não produz mais este valor; fica no tipo pelo cliente antigo.
 */
export type CrmLeadTaxIdBlockedReason = 'CLIENT' | 'FRESH' | 'STAGE' | 'TENANT';

export type CrmLeadTaxIdInfoResult = {
  verdict: CrmLeadTaxIdConflictVerdict;
  blockedReason: CrmLeadTaxIdBlockedReason | null;
  boardId: string | null;
  leadId: string | null;
  ownerUserId: string | null;
  /** Nome do vendedor dono (de `crm_users`); null se o doc sumiu. */
  ownerName: string | null;
  /** Dias inteiros desde `updatedAt` do lead existente. */
  daysSinceUpdate: number | null;
  /** Nome da empresa/contato do lead existente — contexto mínimo na mensagem. */
  leadLabel: string | null;
  /** `false` = o lead vive num quadro do qual quem consultou não participa. */
  callerIsBoardMember: boolean;
};

/**
 * Reivindicação de lead parado — `crmClaimColdLead`. Diferente do
 * `crmTransferLead` (restrito a MANAGER, transfere para terceiros): aqui o
 * vendedor toma para SI um lead que o servidor confirmou estar frio, dentro de
 * uma transaction que revalida a condição (dois cliques concorrentes não passam
 * ambos — o primeiro renova `updatedAt` e o segundo vê o lead fresco).
 */
export const CrmClaimColdLeadInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
});
export type CrmClaimColdLeadInput = z.infer<typeof CrmClaimColdLeadInputSchema>;

// --- crmListSales / crmGetSale ----------------------------------------------

/**
 * Linha da tabela de /sales, já ESCOPADA por papel no servidor:
 * ADMIN/AUDITOR — `totalCents`/`paidCents`/`pendingCents` somam TODAS as
 * comissões da venda; SELLER/MANAGER — somam apenas as PRÓPRIAS parcelas
 * (regra do owner: cada um vê só a própria comissão; manager vê o vendedor).
 */
export const CrmSaleRowSchema = z.object({
  saleId: z.string(),
  boardId: z.string(),
  leadId: z.string(),
  customerName: z.string(),
  companyName: z.string().nullable(),
  sellerUserId: z.string(),
  sellerName: z.string().nullable(),
  /** Foto de perfil do vendedor (Storage) — avatar da coluna de vendedor. */
  sellerPhotoPath: z.string().nullable().optional(),
  managerUserId: z.string().nullable(),
  managerName: z.string().nullable(),
  /** Foto de perfil do gerente (Storage) — avatar da coluna de gerente. */
  managerPhotoPath: z.string().nullable().optional(),
  soldPlan: CrmPlanInterestSchema,
  /**
   * Ciclo em vigor quando difere do vendido (#740); `null` = nenhuma cobrança
   * conhecida ainda. `default(null)` protege o rollout.
   */
  currentCycle: CrmPlanInterestSchema.nullable().default(null),
  currency: z.string(),
  registeredAt: z.number().int(),
  installmentsGenerated: z.number().int().nonnegative(),
  installmentsCap: z.number().int().min(1),
  totalCents: z.number().int().nonnegative(),
  paidCents: z.number().int().nonnegative(),
  pendingCents: z.number().int().nonnegative(),
  /** #753 — pago pelo gateway (split), fora de `paidCents`. Ausente = 0. */
  paidBySplitCents: z.number().int().nonnegative().optional(),
  /** Próximo vencimento entre as parcelas PENDING do escopo; null = nada pendente. */
  nextDueAt: z.number().int().nullable(),
  status: CrmSaleStatusSchema,
  /**
   * Venda de TESTE (`CrmSale.test`, #768): a lista mostra um selo para o ADMIN
   * não confundir com venda real (auditoria 2026-09-28 18:32 +0200, achado 9).
   * `.default(false)`: resposta de deploy anterior.
   */
  test: z.boolean().default(false),
});
export type CrmSaleRow = z.infer<typeof CrmSaleRowSchema>;

/** KPIs do topo de /sales — mesmas somas escopadas por papel das linhas. */
export const CrmSalesKpisSchema = z.object({
  paidCents: z.number().int().nonnegative(),
  pendingCents: z.number().int().nonnegative(),
  /**
   * #753 — o que o gateway já pagou aos colaboradores (split Asaas / Connect).
   * Fora de `paidCents` e `totalCents`: não é dinheiro que o ADMIN transferiu
   * nem que ainda deve. Ausente = 0.
   */
  paidBySplitCents: z.number().int().nonnegative().optional(),
  totalCents: z.number().int().nonnegative(),
  /** Moeda predominante (v1 assume moeda única por venda). */
  currency: z.string(),
});
export type CrmSalesKpis = z.infer<typeof CrmSalesKpisSchema>;

export const CrmListSalesInputSchema = z.object({
  /** Filtro opcional por `registeredAt` (millis, inclusive). */
  from: z.number().int().optional(),
  to: z.number().int().optional(),
});
export type CrmListSalesInput = z.infer<typeof CrmListSalesInputSchema>;

export const CrmListSalesResultSchema = z.object({
  items: z.array(CrmSaleRowSchema),
  kpis: CrmSalesKpisSchema,
});
export type CrmListSalesResult = z.infer<typeof CrmListSalesResultSchema>;

export const CrmGetSaleInputSchema = z.object({
  saleId: z.string().min(1),
});
export type CrmGetSaleInput = z.infer<typeof CrmGetSaleInputSchema>;

/**
 * Venda no detalhe, com percentuais REDIGIDOS por papel: cada beneficiário só
 * vê o próprio % (ADMIN/AUDITOR veem ambos); o servidor anula o que o chamador
 * não pode ver.
 */
export const CrmSaleDetailSchema = CrmSaleSchema.extend({
  sellerCommissionPercent: z.number().min(0).max(100).nullable(),
  managerCommissionPercent: z.number().min(0).max(100).nullable(),
});
export type CrmSaleDetail = z.infer<typeof CrmSaleDetailSchema>;

export const CrmGetSaleResultSchema = z.object({
  sale: CrmSaleDetailSchema,
  /** Parcelas escopadas: ADMIN/AUDITOR = todas; SELLER/MANAGER = só as próprias. */
  commissions: z.array(CrmCommissionSchema),
  /**
   * Beneficiários ainda ATIVOS no CRM. Comissão de quem saiu não é mais devida
   * (regra do owner 2026-08-04): a tela precisa dizer por que as parcelas
   * pararam de aparecer, senão vira ticket de suporte. `default(true)` protege
   * a janela de rollout (web à frente das Functions).
   */
  sellerActive: z.boolean().default(true),
  /** `null` = venda sem gerente. */
  managerActive: z.boolean().nullable().default(null),
  /** Mensalidades que não geraram comissão por inatividade do vendedor. */
  skippedPayments: z.number().int().nonnegative().default(0),
  /**
   * Resumo de ciclo e comissionamento (#740), calculado no SERVIDOR sobre o
   * escopo do chamador — a tela só apresenta. `null` protege o rollout (web à
   * frente das Functions).
   */
  commissionSummary: z.lazy(() => CrmSaleCommissionSummarySchema).nullable().default(null),
});
export type CrmGetSaleResult = z.infer<typeof CrmGetSaleResultSchema>;

/**
 * Resumo do comissionamento de uma venda (#740). Somas sobre as parcelas DO
 * ESCOPO do chamador (SELLER/MANAGER veem só as próprias); os campos da
 * empresa (`receivedCents`, `netToCompanyCents`) só vão para ADMIN/AUDITOR.
 */
export const CrmSaleCommissionSummarySchema = z.object({
  /** Data de aquisição = início do prazo; `null` enquanto nada foi pago. */
  acquisitionAt: z.number().int().nullable(),
  /**
   * Fim (exclusivo) do prazo mais longo entre os beneficiários visíveis;
   * `null` se algum é indeterminado ou nada foi pago (#780).
   */
  windowEndsAt: z.number().int().nullable(),
  /**
   * #780 — prazo VIGENTE de cada beneficiário (cadastro atual); fora do escopo
   * global, só o do chamador. Ausente = Functions anteriores à #780.
   */
  beneficiaryWindows: z
    .array(
      z.object({
        userId: z.string(),
        role: CrmCommissionBeneficiaryRoleSchema,
        name: z.string().nullable(),
        windowMonths: z.number().int().nullable(),
        windowEndsAt: z.number().int().nullable(),
      }),
    )
    .optional(),
  previousCycle: CrmPlanInterestSchema.nullable(),
  currentCycle: CrmPlanInterestSchema.nullable(),
  /** Encerrado por uma comissão semestral/anual não estornada — sempre `false` desde a #780. */
  closed: z.boolean(),
  /** Pagamentos confirmados menos estornos (cents). Só ADMIN/AUDITOR. */
  receivedCents: z.number().int().nullable(),
  /** Parcelas não canceladas: adquiridas (PENDING) + pagas (PAID). */
  generatedCents: z.number().int().nonnegative(),
  /** Efetivamente pagas ao colaborador (PAID) pelo ADMIN. */
  paidOutCents: z.number().int().nonnegative(),
  /** #753 — pagas pelo gateway (split Asaas / Connect), fora de `paidOutCents`. Ausente = 0. */
  paidBySplitCents: z.number().int().nonnegative().optional(),
  /** Adquiridas, ainda a pagar ao colaborador (PENDING). */
  owedCents: z.number().int().nonnegative(),
  /** Canceladas/estornadas (valor original). */
  cancelledCents: z.number().int().nonnegative(),
  /** Parte gerada por migração de ciclo (one-shot com desconto do já gerado). */
  migrationCents: z.number().int().nonnegative(),
  /** Recebido menos TODAS as comissões não canceladas. Só ADMIN/AUDITOR. */
  netToCompanyCents: z.number().int().nullable(),
  /** Histórico de ciclo, com o cálculo redigido ao escopo do chamador. */
  cycleEvents: z.array(CrmSaleCycleEventSchema),
  currency: z.string(),
});
export type CrmSaleCommissionSummary = z.infer<typeof CrmSaleCommissionSummarySchema>;

// --- crmSimulateCommission (#740) -------------------------------------------

/**
 * Teto de uma cobrança simulada: R$ 10 milhões. Não é regra comercial — é o que
 * mantém a conta em cents longe da imprecisão do double.
 */
const SIMULATED_PRICE_MAX_CENTS = 1_000_000_000;
const SimulatedPriceSchema = z.number().int().positive().max(SIMULATED_PRICE_MAX_CENTS).nullable();

/** Preço de uma cobrança de cada ciclo, em cents; `null` = não simular o ciclo. */
export const CrmCyclePricesSchema = z.object({
  MONTHLY: SimulatedPriceSchema,
  QUARTERLY: SimulatedPriceSchema,
  SEMIANNUAL: SimulatedPriceSchema,
  ANNUAL: SimulatedPriceSchema,
});
export type CrmCyclePrices = z.infer<typeof CrmCyclePricesSchema>;

/**
 * Simulação SEM efeito: nada é gravado. O % vem da configuração comercial do
 * colaborador (`crm_users`) — o próprio chamador, ou `collaboratorUid` quando
 * ADMIN. Cenário opcional de migração: N cobranças no ciclo de origem e depois
 * o ciclo de destino até o fim dos 12 meses.
 */
export const CrmSimulateCommissionInputSchema = z
  .object({
    prices: CrmCyclePricesSchema,
    /** Só formata o resultado; a conta é em cents, sem conversão. */
    currency: z.string().regex(/^[A-Z]{3}$/).default('BRL'),
    /** uid do Firebase Auth: sem `/`, que viraria caminho no `doc()`. */
    collaboratorUid: z.string().regex(/^[A-Za-z0-9_-]{1,128}$/).optional(),
    role: z.enum(['SELLER', 'MANAGER']).default('SELLER'),
    /**
     * #780 — prazo a simular; ausente = o do cadastro do colaborador, `null` =
     * indeterminado (a simulação corta em 24 meses).
     */
    windowMonths: CommissionWindowSchema.optional(),
    scenario: z
      .object({
        fromCycle: CrmPlanInterestSchema,
        chargesBeforeChange: z.number().int().min(1).max(12),
        toCycle: CrmPlanInterestSchema,
      })
      .nullable()
      .default(null),
  })
  .refine((v) => Object.values(v.prices).some((p) => p !== null), {
    message: 'Informe o preço de ao menos um ciclo.',
    path: ['prices'],
  })
  .refine(
    (v) =>
      v.scenario === null ||
      (v.prices[v.scenario.fromCycle] !== null && v.prices[v.scenario.toCycle] !== null),
    { message: 'O cenário precisa do preço dos dois ciclos.', path: ['scenario'] },
  );
export type CrmSimulateCommissionInput = z.input<typeof CrmSimulateCommissionInputSchema>;

/** Por que uma cobrança simulada gerou (ou não) comissão. */
export const CrmSimulationStepReasonSchema = z.enum([
  'RECURRING',
  'RECURRING_CYCLE_CHANGE',
  'FIRST_ONE_SHOT',
  'MIGRATION_TOP_UP',
  'MIGRATION_NO_TOP_UP',
  'AFTER_CLOSE',
  'OUTSIDE_WINDOW',
]);
export type CrmSimulationStepReason = z.infer<typeof CrmSimulationStepReasonSchema>;

export const CrmSimulationStepSchema = z.object({
  /** Mês (0 = primeiro pagamento) em que a cobrança começa. */
  monthOffset: z.number().int().nonnegative(),
  cycle: CrmPlanInterestSchema,
  chargeCents: z.number().int().nonnegative(),
  newContractCents: z.number().int().nonnegative(),
  priorGeneratedCents: z.number().int().nonnegative(),
  commissionCents: z.number().int().nonnegative(),
  netToCompanyCents: z.number().int(),
  reason: CrmSimulationStepReasonSchema,
});
export type CrmSimulationStep = z.infer<typeof CrmSimulationStepSchema>;

export const CrmSimulationTotalsSchema = z.object({
  chargedCents: z.number().int().nonnegative(),
  commissionCents: z.number().int().nonnegative(),
  netToCompanyCents: z.number().int(),
  installments: z.number().int().nonnegative(),
});

export const CrmSimulateCommissionResultSchema = z.object({
  percent: z.number().min(0).max(100),
  /** #780 — prazo usado na simulação; `null` = indeterminado. Ausente = Functions antigas. */
  windowMonths: z.number().int().nullable().optional(),
  currency: z.string(),
  collaboratorName: z.string().nullable(),
  /** Um por ciclo com preço, dentro do prazo, sem migração. */
  cycles: z.array(
    z.object({
      cycle: CrmPlanInterestSchema,
      chargeCents: z.number().int().positive(),
      /** Preço equivalente por mês (cobrança ÷ meses do ciclo, arredondado). */
      monthlyEquivalentCents: z.number().int().nonnegative(),
      steps: z.array(CrmSimulationStepSchema),
      totals: CrmSimulationTotalsSchema,
    }),
  ),
  scenario: z
    .object({
      fromCycle: CrmPlanInterestSchema,
      toCycle: CrmPlanInterestSchema,
      steps: z.array(CrmSimulationStepSchema),
      totals: CrmSimulationTotalsSchema,
    })
    .nullable(),
});
export type CrmSimulateCommissionResult = z.infer<typeof CrmSimulateCommissionResultSchema>;

// --- crmSetCommissionPaid (ADMIN) -------------------------------------------

/**
 * Marca/desmarca o pagamento de UMA parcela ao colaborador (ação de dinheiro —
 * sempre auditada). `paid=false` limpa `paidAt`/`paidBy` e preserva o
 * comprovante, salvo `receiptPath: null` explícito.
 */
export const CrmSetCommissionPaidInputSchema = z.object({
  commissionId: z.string().min(1),
  paid: z.boolean(),
  /** Path no Storage (`crm_commissions/{commissionId}/{fileName}`). Ausente = não mexe. */
  receiptPath: z.string().min(1).nullable().optional(),
});
export type CrmSetCommissionPaidInput = z.infer<typeof CrmSetCommissionPaidInputSchema>;
